import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  User, UserCheck, Mail, Phone, MapPin, Building, 
  Calendar, Edit3, Save, X, Eye, Heart,
  CreditCard, Bank, Shield, FileText, GraduationCap, 
  Briefcase, Award, Users, Baby, Plus, Trash2, Camera,
  Upload, File, Download, Clock, CheckCircle, AlertCircle,
  XCircle, Filter, Globe, Contact, Lock, DollarSign, 
  Settings, Car, Gift, AlertTriangle, ArrowRight,
  ExternalLink, Edit, Grid3X3, List,
  IdCard, Fingerprint, HeartPulse, Landmark, Receipt,
} from 'lucide-react';
import { isValidPhoneNumber as validatePhone } from 'libphonenumber-js';
import { useTabPersistence, useNestedTabPersistence, usePersistence } from '../hooks/usePersistence';
import { useDocuments } from '../hooks';
import { getDocumentDisplayName, getDocumentSubtitle, hasLaterReplacementUpload, getEmployeeDocumentListStatus } from '../config/documentTypes';
import { getLetterTypeIcon } from '../config/letterTypeIcons';
import Modal from '../components/UI/Modal';
import ProfileEditFAB from '../components/UI/ProfileEditFAB';
import ConfirmModal from '../components/UI/ConfirmModal';
import SimpleDocumentViewerModal from '../components/UI/SimpleDocumentViewerModal';
import LetterPreviewModal from '../components/LetterPreviewModal';
import { StatusBadge } from '../components/UI/StatusBadge';
import { getLabelForStatus } from '../utils/statusConfig';
import { ClassificationBadge } from '../components/UI/ClassificationBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import RejectionFeedback from '../components/UI/RejectionFeedback';
import { CountBadge } from '../components/UI/CountBadge';
import FieldLabelRow from '../components/UI/FieldLabelRow';
import PassDateFieldDisplay from '../components/UI/PassDateFieldDisplay';
import PillToggle from '../components/UI/PillToggle';
import AccordionSection from '../components/UI/AccordionSection';

// Import sample images
import epFrontImage from '../assets/images/ep_front.png';
import spassFrontImage from '../assets/images/spass_front.png';
import wpFrontImage from '../assets/images/wp_front.png';
import epBackImage from '../assets/images/ep_back.png';
import passportSampleImage from '../assets/images/passport_sample.png';
import spassBackImage from '../assets/images/spass_back.png';
import wpBackImage from '../assets/images/wp_back.png';
import digitalWorkPassImage from '../assets/images/digital_work_pass.jpg';
import api, { staffAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SkillsUpgradingTab from '../components/SkillsUpgrading/SkillsUpgradingTab';
import DrivingLicenseManager from '../components/DrivingLicense/DrivingLicenseManager';
import DocumentVerificationModal from '../components/DocumentVerificationModal';
import NameCardConsentSection from '../components/profile/NameCardConsentSection';
import NameCardTab from '../components/profile/NameCardTab';
import ResidentialAddressEditor from '../components/profile/ResidentialAddressEditor';
import CountryOfBirthField from '../components/profile/CountryOfBirthField';
import EmploymentHistorySection from '../components/employees/EmploymentHistorySection';
import WorkExperienceFormModal from '../components/employees/WorkExperienceFormModal';
import CareerTimelineEventBody from '../components/employees/CareerTimelineEventBody';
import { useNameCard } from '../hooks/useNameCard';
import EmergencyContactModal from '../components/UI/EmergencyContactModal';
import PhoneNumberInput from '../components/UI/PhoneNumberInput';
import DatePickerField from '../components/UI/DatePickerField';
import FormSelect from '../components/UI/FormSelect';
import { RELIGION_OPTIONS, RELIGION_DISPLAY, RELIGION_DEFAULT } from '../constants/religionOptions';
import { RACE_DISPLAY } from '../constants/raceOptions';
import {
  getMaritalStatusLabel,
  isIncompleteMaritalStatus,
  MARITAL_STATUS_OPTIONS,
} from '../constants/maritalStatusOptions';
import {
  EMERGENCY_CONTACT_RELATIONSHIP_VALUES,
  formatEmergencyContactRelationship,
} from '../constants/emergencyContactRelationships';
import { formatDate, formatDateTime, formatDateTimeCompact, formatDaySpan, formatEducationYearRange, formatServicePeriod, formatExperienceFromDays, formatWorkExperiencePeriod, getCalendarDaysUntil, toLocalISODate, parseLocalDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/numberUtils';
import { formatDirectLine, formatPhoneForDisplay, splitWorkContact } from '../utils/phoneUtils';
import { getLetterExpiryStatus } from '../utils/letterExpiryStatus';
import { appendProbationTimelineEvents } from '../utils/probationTimelineUtils';
import toast from '../utils/toast';
import { firstMeaningfulQualificationN8nPage } from '../utils/documentOcrUtils';
import {
  getWorkExpEmploymentTypeLabel,
  getWorkExpIndustryLabel,
  splitWorkExperienceDescription,
} from '../utils/workExperienceUtils';
import { getClassificationSolidColor } from '../utils/classificationConfig';
import { normalizeNationality } from '../utils/ocrConflictUtils';
import {
  OVERSEAS_RESIDENTIAL_COUNTRY,
  ensureCountryInAddress,
  inferResidentialAddressLocation,
  residentialAddressDigestToastNote,
} from '../utils/residentialAddressUtils';
import { 
  useEmergencyContacts, 
  useCreateEmergencyContact, 
  useUpdateEmergencyContact, 
  useDeleteEmergencyContact 
} from '../hooks/useEmergencyContactsData';
import { EmployeeAllowancesSection, EmployeeDeductionsSection } from './EmployeeManagement';
import { useMyHRLetters, useEmployeeTrainingEnrollments, isMilestoneLetterCode } from '../hooks/useTimelineData';
import { maskValue, formatVisible, SensitiveRevealButton } from '../components/UI/SensitiveFieldDisplay';

// Create a safe wrapper for phone validation that handles edge cases
const isValidPhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  try {
    return validatePhone(phone);
  } catch (error) {
    // Fallback to basic validation if library fails
    return phone.startsWith('+') && phone.length >= 8;
  }
};

const normalizeDocumentStatus = (rawStatus) => {
  if (!rawStatus) return 'pending_review';
  const s = rawStatus.toString().toLowerCase();
  if (s === 'on_file') return 'on_file';
  if (s === 'processing_ocr') return 'processing';
  // Extraction finished — employee still needs to review and submit.
  // Do not label this "Processing" (that reads as "OCR is still running").
  if (s === 'ocr_completed') return 'pending_review';
  if (s === 'submitted') return 'pending_hr';
  if (s === 'pending') return 'pending_review';
  if (s.includes('process')) return 'processing';
  if (s.includes('pending')) return 'pending_review';
  if (s.includes('submitted')) return 'pending_hr';
  if (s.includes('review') || s.includes('under_review')) return 'under_review';
  if (s.includes('awaiting') || s.includes('waiting')) return 'awaiting_approval';
  if (s.includes('approved') || s.includes('verified') || s.includes('validated') || s.includes('accepted')) return 'verified';
  if (s.includes('completed') || s.includes('success')) return s.includes('ocr') ? 'pending_review' : 'completed';
  if (s.includes('fail') || s.includes('reject')) return 'rejected';
  if (s.includes('invalid') || s.includes('error')) return 'invalid';
  if (s.includes('expired') || s.includes('expiry')) return 'expired';
  // A previously-approved bank statement (or similar) that has since been
  // replaced by a newer verified submission — no longer "Pending Review".
  if (s.includes('superseded')) return 'superseded';
  if (s.includes('uploaded') || s.includes('new')) return 'new_upload';
  if (s.includes('draft') || s.includes('saved')) return 'draft';
  if (s.includes('archived') || s.includes('deleted')) return 'archived';
  return 'pending_review';
};

const getDropzoneStatusEntries = (docs) => {
  if (!docs || docs.length === 0) return [];
  const order = [
    'on_file',
    'verified',
    'pending_hr',
    'confirm_and_send',
    'processing',
    'pending_review',
    'superseded',
    'rejected',
  ];
  const counts = {};
  docs.forEach((d) => {
    const key = getEmployeeDocumentListStatus(d.status, d.approvalType).status;
    counts[key] = (counts[key] || 0) + 1;
  });
  return order.filter((k) => counts[k]).map((k) => ({ status: k, count: counts[k] }));
};

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [hrProfile, setHrProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const { hasCard: hasNameCard } = useNameCard();

  // Delegates to classificationConfig — the single source of truth for classification colours.
  const getClassificationAvatarColor = (classification) => getClassificationSolidColor(classification);
  // Tab persistence
  const validTabs = ['personal', 'employment', 'compensation', 'professional', 'legal', 'name-card', 'skills-upgrading', 'documents'];
  const [activeTab, setActiveTab] = useTabPersistence('profile', 'personal', validTabs);
  const [showSensitiveData, setShowSensitiveData] = useState({
    personal: false,
    identity: false,
    contact: false,
    banking: false,
    compliance: false
  });
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});

  // Tab data states - using React Query for emergency contacts
  const { data: emergencyContacts = [], isLoading: emergencyContactsLoading } = useEmergencyContacts();
  const [education, setEducation] = useState([]);
  const [workExperience, setWorkExperience] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [dependents, setDependents] = useState([]);
  const [childcareEligibility, setChildcareEligibility] = useState(null);
  const [drivingLicenses, setDrivingLicenses] = useState([]);
  const [tabDataLoading, setTabDataLoading] = useState({
    education: false,
    experience: false,
    certificates: false,
    family: false
  });

  // Ref to track in-flight requests to prevent duplicate concurrent requests
  const inFlightRequestsRef = useRef(new Set());

  const tabs = [
    { id: 'personal', name: 'Personal', icon: User },
    { id: 'employment', name: 'Employment', icon: Briefcase },
    { id: 'compensation', name: 'Compensation', icon: DollarSign },
    { id: 'professional', name: 'Professional', icon: Award },
    { id: 'legal', name: 'Legal & Compliance', icon: Shield },
    ...(hasNameCard ? [
      { id: 'name-card', name: 'Name Card', icon: CreditCard }
    ] : []),
    ...(profile?.employment_classification === 'worker' ? [
      { id: 'skills-upgrading', name: 'Skills Upgrading', icon: Award }
    ] : []),
    { id: 'documents', name: 'Documents', icon: File }
  ];

  useEffect(() => {
    fetchProfileData();
    // Load family data immediately on page load
    fetchTabData('family');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch professional tab data when professional tab becomes active
  useEffect(() => {
    if (activeTab === 'professional') {
      const hasToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (hasToken) {
        fetchTabData('education');
        fetchTabData('experience');
        fetchTabData('certificates');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Fetch driving licenses when profile is loaded
  useEffect(() => {
    if (profile?.staff_id) {
      fetchDrivingLicenses();
    }
  }, [profile?.staff_id]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      console.log('🔍 PROFILE DEBUG: Fetching profile data...');
      
      const [profileResponse, hrResponse] = await Promise.all([
        api.getMyProfile(),
        api.getMyHRProfile()
      ]);
      
      console.log('🔍 PROFILE DEBUG: Profile response:', profileResponse.data);
      console.log('🔍 PROFILE DEBUG: HR response:', hrResponse.data);
      
      setProfile(profileResponse.data);
      
      // Handle direct HR profile response (not paginated anymore)
      if (hrResponse.data) {
        setHrProfile(hrResponse.data);
        console.log('🔍 PROFILE DEBUG: HR Profile data:', hrResponse.data);
        console.log('🔍 PROFILE DEBUG: WP Application Date:', hrResponse.data.hrprofile_wp_application_date);
        console.log('🔍 PROFILE DEBUG: Employment Pass Type:', hrResponse.data.hrprofile_employment_pass_type);
        initializeFormData(profileResponse.data, hrResponse.data);
      } else {
        console.log('No HR profile found');
        initializeFormData(profileResponse.data, null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      if (error.response?.status === 404) {
        console.log('HR profile not found - user may not have one yet');
        // Initialize with profile data only if we have it, otherwise use empty data
        if (profile) {
          initializeFormData(profile, null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = useCallback(async (tabId) => {
    // Prevent duplicate concurrent requests for the same tab using ref
    if (inFlightRequestsRef.current.has(tabId)) {
      return;
    }

    try {
      inFlightRequestsRef.current.add(tabId);
      setTabDataLoading(prev => ({ ...prev, [tabId]: true }));
      
      switch (tabId) {
        case 'education':
          const educationResponse = await api.getEducation();
          const educationData = educationResponse.data?.results || educationResponse.data || [];
          setEducation(educationData);
          break;

        case 'experience':
          const experienceResponse = await api.getWorkExperience();
          const experienceData = experienceResponse.data?.results || experienceResponse.data || [];
          setWorkExperience(experienceData);
          break;

        case 'certificates':
          const certificatesResponse = await api.getCertificates();
          const certificatesData = certificatesResponse.data?.results || certificatesResponse.data || [];
          setCertificates(certificatesData);
          break;

        case 'family':
          const [dependentsResponse, eligibilityResponse] = await Promise.all([
            api.getMyDependents(),
            api.getMyChildcareEligibility()
          ]);
          const dependentsData = dependentsResponse.data?.results || dependentsResponse.data || [];
          const eligibilityData = eligibilityResponse.data;
          setDependents(dependentsData);
          setChildcareEligibility(eligibilityData);
          break;

        default:
          console.warn(`Unknown tab ID: ${tabId}`);
          break;
      }
    } catch (error) {
      // Silently handle 401 errors (token expired) - token refresh interceptor will handle it
      // Only log other errors to avoid noise from expected auth failures
      if (error.response?.status !== 401) {
        console.error(`Error fetching ${tabId} data:`, error);
      }
      // Don't show error toast for 401 - user will be redirected to login if refresh fails
      if (error.response?.status === 401) {
        // Token refresh interceptor will handle this, just silently fail
        return;
      }
    } finally {
      inFlightRequestsRef.current.delete(tabId);
      setTabDataLoading(prev => ({ ...prev, [tabId]: false }));
    }
  }, [setEducation, setWorkExperience, setCertificates, setDependents, setChildcareEligibility, setTabDataLoading]);

  const fetchDrivingLicenses = async () => {
    try {
      const response = await api.getDrivingLicenses(profile.staff_id);
      const licensesData = response.data?.results || response.data || [];
      setDrivingLicenses(licensesData);
    } catch (error) {
      console.error('Error fetching driving licenses:', error);
      if (error.response?.status !== 404) {
        // Only log non-404 errors (404 means no licenses, which is fine)
        console.warn('Failed to load driving licenses');
      }
      setDrivingLicenses([]);
    }
  };

  const initializeFormData = (profileData, hrProfileData) => {
    console.log('🔍 PROFILE DEBUG: Initializing form data...');
    console.log('🔍 PROFILE DEBUG: HR Profile Data:', hrProfileData);
    console.log('🔍 PROFILE DEBUG: WP Application Date from HR:', hrProfileData?.hrprofile_wp_application_date);
    
    setFormData({
      // Basic profile fields
      first_name: profileData?.first_name || '',
      last_name: profileData?.last_name || '',
      alias: profileData?.alias || '',
      work_email: profileData?.work_email || '',
      work_phone_number: profileData?.work_phone_number || '',
      did_number: profileData?.did_number || '',
      extension_number: profileData?.extension_number || '',
      designation: profileData?.designation || '',
      
      // HR Profile fields
      hrprofile_residential_address: hrProfileData?.hrprofile_residential_address || '',
      hrprofile_residential_postal_code: hrProfileData?.hrprofile_residential_postal_code || '',
      hrprofile_residential_unit_number: hrProfileData?.hrprofile_residential_unit_number || '',
      hrprofile_residential_address_location:
        hrProfileData?.hrprofile_residential_address_location ||
        inferResidentialAddressLocation({
          address: hrProfileData?.hrprofile_residential_address,
          postalCode: hrProfileData?.hrprofile_residential_postal_code,
          location: hrProfileData?.hrprofile_residential_address_location,
          country: hrProfileData?.hrprofile_residential_address_country,
        }),
      hrprofile_residential_address_country: hrProfileData?.hrprofile_residential_address_country || '',
      hrprofile_dormitory_address: hrProfileData?.hrprofile_dormitory_address || '',
      hrprofile_dormitory_postal_code: hrProfileData?.hrprofile_dormitory_postal_code || '',
      hrprofile_dorm_unit: hrProfileData?.hrprofile_dorm_unit || '',
      hrprofile_personal_email: hrProfileData?.hrprofile_personal_email || '',
      hrprofile_personal_phone_number: hrProfileData?.hrprofile_personal_phone_number || '',
      hrprofile_date_of_birth: hrProfileData?.hrprofile_date_of_birth || '',
      hrprofile_gender: hrProfileData?.hrprofile_gender || '',
      hrprofile_nationality: hrProfileData?.hrprofile_nationality || '',
      hrprofile_country_of_birth: hrProfileData?.hrprofile_country_of_birth || '',
      hrprofile_race: hrProfileData?.hrprofile_race || 'not_disclosed',
      hrprofile_religion: hrProfileData?.hrprofile_religion || RELIGION_DEFAULT,
      hrprofile_marital_status: hrProfileData?.hrprofile_marital_status || '',
      hrprofile_marriage_date: hrProfileData?.hrprofile_marriage_date || '',
      hrprofile_blood_type: hrProfileData?.hrprofile_blood_type || '',
      hrprofile_nric_fin_number: hrProfileData?.hrprofile_nric_fin_number || '',
      hrprofile_name_as_per_nric_fin: hrProfileData?.hrprofile_name_as_per_nric_fin || '',
      hrprofile_wp_number: hrProfileData?.hrprofile_wp_number || '',
      hrprofile_wp_application_date: hrProfileData?.hrprofile_wp_application_date || '',
      hrprofile_passport_number: hrProfileData?.hrprofile_passport_number || '',
      hrprofile_employment_pass_type: hrProfileData?.hrprofile_employment_pass_type || '',
      hrprofile_pr_obtained_date: hrProfileData?.hrprofile_pr_obtained_date || '',
              hrprofile_pass_expiry_date: hrProfileData?.hrprofile_pass_expiry_date || '',
      hrprofile_passport_expiry_date: hrProfileData?.hrprofile_passport_expiry_date || '',
      hrprofile_pass_issue_date: hrProfileData?.hrprofile_pass_issue_date || '',
      hrprofile_pass_renewal_reminder_days: hrProfileData?.hrprofile_pass_renewal_reminder_days || 90,
      hrprofile_bank_account_number: hrProfileData?.hrprofile_bank_account_number || '',
      hrprofile_bank_name: hrProfileData?.hrprofile_bank_name || '',
      hrprofile_bank_account_holder_name: hrProfileData?.hrprofile_bank_account_holder_name || '',
      hrprofile_branch_code: hrProfileData?.hrprofile_branch_code || '',
      hrprofile_pay_mode: hrProfileData?.hrprofile_pay_mode || 'bank_transfer'
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Only validate editable Contact & Family fields
    if (formData.hrprofile_personal_email && !/\S+@\S+\.\S+/.test(formData.hrprofile_personal_email)) {
      errors.hrprofile_personal_email = 'Please enter a valid email address';
    }

    if (
      !hrProfile?.has_approved_marriage_certificate
      && isIncompleteMaritalStatus(formData.hrprofile_marital_status)
    ) {
      errors.hrprofile_marital_status = 'Marital status is required.';
    }

    const addressLocation = inferResidentialAddressLocation({
      address: formData.hrprofile_residential_address,
      postalCode: formData.hrprofile_residential_postal_code,
      location: formData.hrprofile_residential_address_location,
      country: formData.hrprofile_residential_address_country,
    });

    if (addressLocation === 'overseas') {
      if (!String(formData.hrprofile_residential_address || '').trim()) {
        errors.hrprofile_residential_address = 'Please enter your overseas residential address';
      }
      const postal = String(formData.hrprofile_residential_postal_code || '').trim().replace(/\s+/g, '');
      if (postal && !/^\d{5}$/.test(postal)) {
        errors.hrprofile_residential_postal_code = 'Malaysia postcode must be 5 digits';
      }
    } else {
      const postal = String(formData.hrprofile_residential_postal_code || '').trim().replace(/\s+/g, '');
      if (postal && !/^\d{6}$/.test(postal)) {
        errors.hrprofile_residential_postal_code = 'Singapore postal code must be 6 digits';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Get field label for display
  const getFieldLabel = (fieldName) => {
    const labels = {
      'alias': 'Alias',
      'hrprofile_personal_phone_number': 'Personal Phone',
      'hrprofile_personal_email': 'Personal Email',
      'hrprofile_residential_address': 'Residential Address',
      'hrprofile_residential_postal_code': 'Postal Code',
      'hrprofile_residential_unit_number': 'Unit Number',
      'hrprofile_residential_address_location': 'Address Location',
      'hrprofile_residential_address_country': 'Address Country',
      'hrprofile_religion': 'Religion',
      'hrprofile_country_of_birth': 'Country/Region of Birth',
      'hrprofile_marital_status': 'Marital Status',
    };
    return labels[fieldName] || fieldName;
  };

  // Mask sensitive data and humanize stored codes for the save receipt
  const formatSummaryValue = (value, fieldName) => {
    if (!value || value === 'Not provided') return 'Not provided';
    if (fieldName === 'hrprofile_personal_phone_number') return maskValue(value, 'phone');
    if (fieldName === 'hrprofile_personal_email') return maskValue(value, 'email');

    if (typeof value === 'string') {
      if (fieldName === 'hrprofile_religion') {
        return RELIGION_DISPLAY[value.toLowerCase()] || value;
      }
      if (fieldName === 'hrprofile_marital_status') {
        return getMaritalStatusLabel(value) || value;
      }
      if (value.includes('_')) {
        return value
          .split('_')
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      }
    }
    return value;
  };

  // Editable self-service fields: alias (StaffUser) + contact/address + religion (HRProfile)
  const getChangedFields = () => {
    const editableFields = [
      'alias',
      'hrprofile_personal_phone_number',
      'hrprofile_personal_email',
      'hrprofile_residential_address',
      'hrprofile_residential_postal_code',
      'hrprofile_residential_unit_number',
      'hrprofile_residential_address_location',
      'hrprofile_residential_address_country',
      'hrprofile_religion',
      'hrprofile_country_of_birth',
      ...(!hrProfile?.has_approved_marriage_certificate
        ? ['hrprofile_marital_status']
        : []),
    ];

    const changes = [];
    editableFields.forEach(field => {
      const original = originalFormData[field] || '';
      const modified = formData[field] || '';
      if (original !== modified) {
        changes.push({
          field,
          label: getFieldLabel(field),
          original: original || 'Not provided',
          modified: modified || 'Not provided'
        });
      }
    });

    return changes;
  };

  // Check if a specific field has changed
  const hasFieldChanged = (fieldName) => {
    const original = originalFormData[fieldName] || '';
    const modified = formData[fieldName] || '';
    return original !== modified;
  };

  // Memoize all changed field values to prevent focus loss on keystroke
  const changedFields = useMemo(() => {
    const editableFields = [
      'alias',
      'hrprofile_personal_phone_number',
      'hrprofile_personal_email',
      'hrprofile_residential_address',
      'hrprofile_residential_postal_code',
      'hrprofile_residential_unit_number',
      'hrprofile_residential_address_location',
      'hrprofile_residential_address_country',
      'hrprofile_religion',
      'hrprofile_country_of_birth',
    ];
    
    const changed = {};
    editableFields.forEach(field => {
      const original = originalFormData[field] || '';
      const modified = formData[field] || '';
      changed[field] = original !== modified;
    });
    return changed;
  }, [formData, originalFormData]);

  // Derived count from memo — avoids repeated getChangedFields() calls during render
  const changedCount = useMemo(
    () => Object.values(changedFields).filter(Boolean).length,
    [changedFields]
  );

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('❌ Please fix the errors before saving');
      return;
    }

    // Calculate changes
    const changes = getChangedFields();
    
    // Check if anything changed - silently exit edit mode if no changes
    if (changes.length === 0) {
      setIsEditing(false);
      setOriginalFormData({ ...formData });
      return;
    }

    const aliasChanged = !!changedFields.alias;
    const hrFieldKeys = [
      'hrprofile_personal_phone_number',
      'hrprofile_personal_email',
      'hrprofile_residential_address',
      'hrprofile_residential_postal_code',
      'hrprofile_residential_unit_number',
      'hrprofile_residential_address_location',
      'hrprofile_residential_address_country',
      'hrprofile_religion',
      'hrprofile_country_of_birth',
      ...(!hrProfile?.has_approved_marriage_certificate
        ? ['hrprofile_marital_status']
        : []),
    ];
    const hrChanged = hrFieldKeys.some((field) => changedFields[field]);

    // Save directly (no modal) — split by data owner
    let loadingToast;
    try {
      setSaving(true);
      loadingToast = toast.loading('Saving profile changes...');

      const saveErrors = [];
      let residentialAddressHrDigestQueued = false;

      if (aliasChanged) {
        try {
          await api.updateMyProfile({
            alias: (formData.alias || '').trim(),
          });
        } catch (err) {
          console.error('Alias update failed:', err);
          saveErrors.push('alias');
        }
      }

      if (hrChanged && hrProfile) {
        const addressLocation = inferResidentialAddressLocation({
          address: formData.hrprofile_residential_address,
          postalCode: formData.hrprofile_residential_postal_code,
          location: formData.hrprofile_residential_address_location,
          country: formData.hrprofile_residential_address_country,
        });
        const addressText =
          addressLocation === 'overseas'
            ? ensureCountryInAddress(
                formData.hrprofile_residential_address,
                OVERSEAS_RESIDENTIAL_COUNTRY
              )
            : formData.hrprofile_residential_address;

        const hrProfileData = {
          hrprofile_personal_email: formData.hrprofile_personal_email,
          hrprofile_personal_phone_number: formData.hrprofile_personal_phone_number,
          hrprofile_residential_address: addressText,
          hrprofile_residential_postal_code: formData.hrprofile_residential_postal_code,
          hrprofile_residential_unit_number: formData.hrprofile_residential_unit_number,
          hrprofile_residential_address_location: addressLocation,
          hrprofile_residential_address_country:
            addressLocation === 'overseas' ? OVERSEAS_RESIDENTIAL_COUNTRY : '',
          hrprofile_religion: formData.hrprofile_religion || RELIGION_DEFAULT,
          hrprofile_country_of_birth: formData.hrprofile_country_of_birth || '',
          ...(!hrProfile?.has_approved_marriage_certificate
            ? { hrprofile_marital_status: formData.hrprofile_marital_status || '' }
            : {}),
        };

        try {
          const hrResponse = await api.updateMyHRProfile(hrProfileData);
          residentialAddressHrDigestQueued = Boolean(
            hrResponse?.data?.residential_address_hr_digest_queued
          );
        } catch (err) {
          console.error('HR profile update failed:', err);
          saveErrors.push('personal details');
        }
      }

      // Refresh data after attempted saves
      await fetchProfileData();
      toast.dismiss(loadingToast);

      if (saveErrors.length > 0) {
        toast.error(
          `Failed to update ${saveErrors.join(' and ')}. Other changes may have been saved.`
        );
        return;
      }

      const formatChangeLine = (change) =>
        `${formatSummaryValue(change.original, change.field)} → ${formatSummaryValue(change.modified, change.field)}`;
      const digestNote = residentialAddressDigestToastNote(residentialAddressHrDigestQueued);
      const successMessage = changes.length === 1
        ? `${changes[0].label} updated: ${formatChangeLine(changes[0])}`
        : `Profile updated\n${changes.map((change) => `${change.label}: ${formatChangeLine(change)}`).join('\n')}`;
      toast.success(digestNote ? `${successMessage}\n${digestNote}` : successMessage);
      
      setIsEditing(false);
      setOriginalFormData({ ...formData });
    } catch (error) {
      console.error('Error saving profile:', error);
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.error(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormErrors({});
    initializeFormData(profile, hrProfile);
    setOriginalFormData({});
  };

  const formatFieldValue = (value) => {
    if (!value) return 'Not provided';
    // Handle cases where encrypted fields might return memory addresses or objects
    if (typeof value === 'object' && value !== null) {
      // If it's an object, try to get a meaningful string representation
      if (value.toString && value.toString() !== '[object Object]') {
        return value.toString();
      }
      return 'Data processing error';
    }
    // Handle memory address strings
    if (typeof value === 'string' && value.includes('<memory at 0x')) {
      return 'Data processing error';
    }
    
    // Format specific field values for better display
    if (typeof value === 'string') {
      const maritalLabel = getMaritalStatusLabel(value);
      if (maritalLabel) {
        return maritalLabel;
      }
      
      // Format nationality - country name / ISO code / demonym -> canonical
      // demonym via the shared normalizer (single source of truth).
      const canonicalNationality = normalizeNationality(value);
      const properNationalities = ['Singaporean', 'Malaysian', 'Chinese', 'Indian', 'Filipino', 'Indonesian', 'Thai', 'Vietnamese', 'Burmese', 'Bangladeshi', 'Sri Lankan', 'Japanese', 'Other', 'Others'];
      if (properNationalities.includes(canonicalNationality)) {
        return canonicalNationality;
      }
      
      // Format employment pass type
      const passTypeMap = {
        'citizen': 'Singapore Citizen',
        'pr': 'Permanent Resident',
        'ep': 'Employment Pass',
        's_pass': 'S Pass',
        'wp': 'Work Permit',
        'ltvp': 'Long Term Visit Pass',
        'dp': 'Dependant Pass',
        'student_pass': 'Student Pass',
        'visit_pass': 'Visit Pass'
      };
      
      if (passTypeMap[value.toLowerCase()]) {
        return passTypeMap[value.toLowerCase()];
      }

      if (RACE_DISPLAY[value.toLowerCase()]) {
        return RACE_DISPLAY[value.toLowerCase()];
      }

      // Format religion (workplace accommodations)
      if (RELIGION_DISPLAY[value.toLowerCase()]) {
        return RELIGION_DISPLAY[value.toLowerCase()];
      }
    }
    
    return value;
  };

  const getGenderDisplay = (genderCode) => {
    if (!genderCode) return 'Not provided';
    switch (genderCode.toUpperCase()) {
      case 'M':
        return 'Male';
      case 'F':
        return 'Female';
      case 'O':
        return 'Other';
      default:
        return genderCode;
    }
  };

  const maskSensitiveData = (value, type = 'default', shouldShow = false) => {
    if (!value) return 'Not provided';
    if (typeof value === 'object' && value !== null) return 'Data processing error';
    if (typeof value === 'string' && value.includes('<memory at 0x')) return 'Data processing error';
    if (shouldShow) return formatVisible(String(value), type);
    return maskValue(value, type) || 'Not provided';
  };

  const handleTabChange = (tabId) => {
    // Auto-cancel edit mode if switching away from contact tab
    if (isEditing && tabId !== 'contact') {
      setIsEditing(false);
      setFormErrors({});
      initializeFormData(profile, hrProfile);
    }
    
    setActiveTab(tabId);
    
    // Fetch data based on the new combined tabs
    switch (tabId) {
      case 'contact':
        fetchTabData('family');
        break;
      case 'professional':
        fetchTabData('education');
        fetchTabData('experience');
        fetchTabData('certificates');
        break;
      default:
        break;
    }
  };

  const toggleSensitiveData = (section) => {
    setShowSensitiveData(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6 min-h-screen bg-gray-50">
        {/* Header Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-28 animate-pulse"></div>
                </div>
              </div>
            </div>
            <div className="h-10 bg-gray-200 rounded-full w-24 animate-pulse"></div>
          </div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="flex items-center gap-2 overflow-x-auto mb-4 md:mb-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-full w-28 animate-pulse flex-shrink-0"></div>
          ))}
        </div>

        {/* Content Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
          {/* Section Header Skeleton */}
          <div className="mb-6">
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
          </div>

          {/* Form Fields Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="space-y-2 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-10 bg-gray-100 rounded-lg w-full"></div>
              </div>
            ))}
          </div>

          {/* Additional Section Skeleton */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="h-6 bg-gray-200 rounded w-40 animate-pulse mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 ${getClassificationAvatarColor(profile?.employment_classification)} rounded-full flex items-center justify-center text-white text-base font-bold`}>
                {(profile?.alias && profile.alias.trim())
                  ? profile.alias.trim().charAt(0).toUpperCase()
                  : profile?.first_name?.charAt(0)}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-subsection-header">
                    {(profile?.alias && profile.alias.trim())
                      ? profile.alias.trim()
                      : `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()}
                  </h1>
                  <span className="text-lg text-gray-400">•</span>
                  <span className="text-base text-gray-600 font-mono">
                    {profile?.staff_id}
                  </span>
                </div>
                {(profile?.alias && profile.alias.trim()) && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {[profile?.first_name, profile?.last_name].filter(Boolean).join(' ')}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <p className="text-body">{profile?.designation}</p>
                  {profile?.employment_type === 'part_time' && (
                    <InfoBadge color="orange" label="Part-Time" />
                  )}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <StatusBadge
                    status={
                      profile?.employment_status === 'active'
                        ? 'active'
                        : profile?.employment_status === 'probation'
                        ? 'pending'
                        : 'deactivated'
                    }
                    label={profile?.employment_status ? profile.employment_status.charAt(0).toUpperCase() + profile.employment_status.slice(1) : 'Unknown'}
                    showIcon
                  />
                  <span className="text-sm text-gray-500">
                    <Calendar className="w-4 h-4 inline mr-1 text-blue-500" />
                    Joined {profile?.date_joined ? formatDate(profile.date_joined) : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Edit Toggle - Only show on Personal tab, hidden on small screens (FAB shown instead) */}
              {activeTab === 'personal' && (
                <div className="hidden md:flex items-center gap-3">
                  {isEditing ? (
                    <div className="flex gap-2">
                      {changedCount > 0 && (
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="btn btn-success self-center sm:self-auto"
                        >
                          {saving ? (
                            <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      )}
                      <button
                        onClick={handleCancel}
                        className="btn btn-secondary self-center sm:self-auto"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setIsEditing(true);
                        setOriginalFormData({ ...formData });
                      }}
                      className="btn btn-primary self-start sm:self-auto"
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Edit Profile
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Containerless Pill Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`tab-btn ${isActive ? 'active' : ''}`}
                title={tab.name}
              >
                <span className="tab-icon"><IconComponent className="w-4 h-4" /></span>
                {tab.name}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
          {activeTab === 'personal' && (
            <PersonalIdentityTab
              profile={profile}
              hrProfile={hrProfile}
              formData={formData}
              formErrors={formErrors}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              isEditing={isEditing}
              handleInputChange={handleInputChange}
              formatFieldValue={formatFieldValue}
              maskSensitiveData={maskSensitiveData}
              showSensitiveData={showSensitiveData}
              onToggleSensitiveData={toggleSensitiveData}
              getGenderDisplay={getGenderDisplay}
              dependents={dependents}
              setDependents={setDependents}
              emergencyContacts={emergencyContacts}
              emergencyContactsLoading={emergencyContactsLoading}
              childcareEligibility={childcareEligibility}
              setChildcareEligibility={setChildcareEligibility}
              tabDataLoading={tabDataLoading}
              fetchTabData={fetchTabData}
              changedFields={changedFields}
              drivingLicenses={drivingLicenses}
            />
          )}

          {activeTab === 'employment' && (
            <EmploymentTab
              profile={profile}
              hrProfile={hrProfile}
              formData={formData}
              formErrors={formErrors}
              isEditing={isEditing}
              handleInputChange={handleInputChange}
              formatFieldValue={formatFieldValue}
              maskSensitiveData={maskSensitiveData}
              showSensitiveData={showSensitiveData}
              onToggleSensitiveData={toggleSensitiveData}
            />
          )}

          {activeTab === 'professional' && (
            <ProfessionalProfileTab
              profile={profile}
              education={education}
              setEducation={setEducation}
              workExperience={workExperience}
              setWorkExperience={setWorkExperience}
              certificates={certificates}
              setCertificates={setCertificates}
              tabDataLoading={tabDataLoading}
              fetchTabData={fetchTabData}
              activeTab={activeTab}
            />
          )}

          {activeTab === 'compensation' && (
            <CompensationBenefitsTab
              profile={profile}
              hrProfile={hrProfile}
              formData={formData}
              formErrors={formErrors}
              isEditing={false}
              handleInputChange={handleInputChange}
              formatFieldValue={formatFieldValue}
              maskSensitiveData={maskSensitiveData}
              showSensitiveData={showSensitiveData}
              onToggleSensitiveData={toggleSensitiveData}
            />
          )}

          {activeTab === 'legal' && (
            <ComplianceTab
              hrProfile={hrProfile}
              profile={profile}
            />
          )}

          {activeTab === 'skills-upgrading' && profile?.employment_classification === 'worker' && (
            <SkillsUpgradingTab 
              profile={profile}
              hrProfile={hrProfile}
            />
          )}

          {activeTab === 'name-card' && (
            <NameCardTab
              profile={profile}
              hrProfile={hrProfile}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab profile={profile} hrProfile={hrProfile} fetchProfileData={fetchProfileData} activeTab={activeTab} />
          )}
        </div>

        {/* Profile Edit FAB - Only visible on small screens and Personal tab */}
        {activeTab === 'personal' && (
          <ProfileEditFAB
            isEditing={isEditing}
            hasChanges={changedCount > 0}
            saving={saving}
            onEdit={() => {
              setIsEditing(true);
              setOriginalFormData({ ...formData });
            }}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
    </div>
  );
};

// SensitiveFieldDisplay Component - Moved outside to prevent recreation on each render
const SensitiveFieldDisplay = ({ 
  label, 
  name,
  value, 
  fieldName, 
  type = 'default', 
  isEditing,
  onChange,
  error,
  required = false,
  showRequiredAsterisk = true,
  className = "",
  placeholder,
  disabled = false,
  inputType = 'text',
  isTextArea = false,
  readOnly = false,
  hasChanged = false,
  sensitiveFieldsVisible = {},
  toggleFieldVisibility = () => {},
  helpText
}) => {
  const maskFieldDataForDisplay = (value, fieldName, type = 'default') => {
    if (!value) return 'Not provided';
    if (sensitiveFieldsVisible[fieldName]) return formatVisible(value, type);
    return maskValue(value, type) || 'Not provided';
  };

  return (
    <div className={className}>
      <label className={isEditing && !readOnly ? 'form-label' : 'field-label'}>
        {label}
        {required && showRequiredAsterisk && <span className="text-red-500">*</span>}
      </label>
      <div>
        <div className="relative">
          {isEditing && !readOnly ? (
            <div className="relative">
              {isTextArea ? (
                <textarea
                  name={name}
                  value={value || ''}
                  onChange={onChange}
                  rows={2}
                  className={`textarea min-h-[60px] pr-4 ${error ? 'input-error' : ''} ${hasChanged ? 'border-blue-300 bg-blue-50/30' : ''}`}
                  placeholder={placeholder}
                />
              ) : type === 'date' ? (
                <DatePickerField
                  theme="employee"
                  selected={parseLocalDate(value)}
                  onChange={(date) => onChange({ target: { name, value: date ? toLocalISODate(date) : '' } })}
                  placeholderText={placeholder || "Select date"}
                  disabled={disabled}
                />
              ) : (
                <input
                  type={inputType}
                  name={name}
                  value={value || ''}
                  onChange={onChange}
                  className={`input px-4 rounded-full ${type === 'account' || fieldName === 'bank_account' || fieldName === 'bank_account_number' ? 'font-mono' : ''} ${error ? 'input-error' : ''} ${hasChanged ? 'border-blue-300 bg-blue-50/30' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder={placeholder}
                  disabled={disabled}
                />
              )}
            </div>
          ) : (
            <p className={`field-display ${
              type === 'nric' || type === 'account' || type === 'currency' || fieldName === 'bank_account' || fieldName === 'bank_account_number' ? 'font-mono' : ''
            } ${type === 'currency' ? 'font-semibold' : ''} ${isTextArea ? 'field-display-multiline' : ''} ${value ? 'field-display-sensitive' : ''}`}>
              <span className="block break-words whitespace-normal">{maskFieldDataForDisplay(value, fieldName, type)}</span>
            </p>
          )}
          {!isEditing && value && (
            <SensitiveRevealButton
              isVisible={!!sensitiveFieldsVisible[fieldName]}
              onClick={() => toggleFieldVisibility(fieldName)}
              theme="employee"
              multiline={!!isTextArea}
            />
          )}
        </div>
        {error && (
          <p className="form-error">{error}</p>
        )}
        {helpText && (
          <p className="form-help">{helpText}</p>
        )}
      </div>
    </div>
  );
};

// Personal & Identity Tab Component (Combined)
const PersonalIdentityTab = ({ 
  profile, 
  hrProfile, 
  formData, 
  formErrors, 
  setFormData,
  setFormErrors,
  isEditing,
  handleInputChange, 
  formatFieldValue, 
  maskSensitiveData,
  showSensitiveData,
  onToggleSensitiveData,
  getGenderDisplay,
  dependents = [],
  setDependents,
  emergencyContacts,
  emergencyContactsLoading,
  childcareEligibility,
  setChildcareEligibility,
  tabDataLoading,
  fetchTabData,
  changedFields = {},
  drivingLicenses = []
}) => {
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = React.useState({});
  const maritalLocked = !!hrProfile?.has_approved_marriage_certificate;
  const workContact = splitWorkContact(formData);
  const maritalDisplay =
    getMaritalStatusLabel(hrProfile?.hrprofile_marital_status)
    || formatFieldValue(hrProfile?.hrprofile_marital_status)
    || 'Not provided';
  // Progressive disclosure: keep primary contact open; tuck secondary blocks
  const [openSections, setOpenSections] = React.useState({
    emergency: true,
    family: false,
    identity: false,
    driving: false,
  });
  const emergencyAddHandlerRef = React.useRef(null);
  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const maskFieldData = (value, fieldName, type = 'default') => {
    if (!value) return 'Not provided';
    if (sensitiveFieldsVisible[fieldName]) return formatVisible(value, type);
    return maskValue(value, type) || 'Not provided';
  };

  // Helper function to get marriage date from spouse dependent record
  const getMarriageDateFromDependents = () => {
    const spouseDependent = dependents.find(dep => dep.relationship_type === 'spouse');
    return spouseDependent?.marriage_date || null;
  };

  return (
    <div className="space-y-4">
      {/* Basic Information */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-card-header mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-800" />
          Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField
            label="First Name"
            name="first_name"
            value={formData.first_name}
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.first_name}
            displayValue={formatFieldValue(profile?.first_name)}
            required={true}
            showRequiredAsterisk={false}
            placeholder="Enter your first name"
            readOnly={true}
          />
          <InputField
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.last_name}
            displayValue={formatFieldValue(profile?.last_name)}
            required={true}
            showRequiredAsterisk={false}
            placeholder="Enter your last name"
            readOnly={true}
          />
          <InputField
            label="Alias"
            name="alias"
            value={formData.alias}
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.alias}
            displayValue={formatFieldValue(profile?.alias)}
            placeholder="How you prefer to be called"
            helpText="Does not change your legal name."
            maxLength={100}
            hasChanged={!!changedFields.alias}
          />
          <SelectField
            label="Gender"
            name="hrprofile_gender"
            value={formData.hrprofile_gender}
            isEditing={isEditing}
            onChange={handleInputChange}
            displayValue={getGenderDisplay(hrProfile?.hrprofile_gender)}
            options={[
              { value: '', label: 'Select Gender' },
              { value: 'M', label: 'Male' },
              { value: 'F', label: 'Female' },
              { value: 'O', label: 'Other' }
            ]}
            readOnly={true}
          />
          <SensitiveFieldDisplay
            label="Date of Birth"
            name="hrprofile_date_of_birth"
            value={formData.hrprofile_date_of_birth}
            fieldName="date_of_birth"
            type="date"
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.hrprofile_date_of_birth}
            readOnly={true}
            sensitiveFieldsVisible={sensitiveFieldsVisible}
            toggleFieldVisibility={toggleFieldVisibility}
          />
          <SelectField
            label="Nationality"
            name="hrprofile_nationality"
            value={formData.hrprofile_nationality}
            isEditing={isEditing}
            onChange={handleInputChange}
            displayValue={formatFieldValue(hrProfile?.hrprofile_nationality)}
            options={[
              { value: '', label: 'Select Nationality' },
              { value: 'Singaporean', label: 'Singaporean' },
              { value: 'Malaysian', label: 'Malaysian' },
              { value: 'Chinese', label: 'Chinese' },
              { value: 'Indian', label: 'Indian' },
              { value: 'Filipino', label: 'Filipino' },
              { value: 'Indonesian', label: 'Indonesian' },
              { value: 'Thai', label: 'Thai' },
              { value: 'Vietnamese', label: 'Vietnamese' },
              { value: 'Burmese', label: 'Burmese' },
              { value: 'Bangladeshi', label: 'Bangladeshi' },
              { value: 'Other', label: 'Other' }
            ]}
            readOnly={true}
          />
          <CountryOfBirthField
            theme="employee"
            isEditing={isEditing}
            value={formData.hrprofile_country_of_birth}
            onChange={(val) => handleInputChange({
              target: { name: 'hrprofile_country_of_birth', value: val || '' },
            })}
            displayValue={hrProfile?.hrprofile_country_of_birth || 'Not provided'}
            hasChanged={!!changedFields?.hrprofile_country_of_birth}
            error={formErrors.hrprofile_country_of_birth}
          />
          <div>
            <label className={isEditing ? 'form-label' : 'field-label'}>
              Race
            </label>
            <p className={isEditing ? 'field-static' : 'field-display'}>
              {formatFieldValue(hrProfile?.hrprofile_race)}
            </p>
            <p className="form-help">
              As printed on your NRIC/FIN. Used for MOM statistical reporting and community fund contributions. Contact HR if this is wrong.
            </p>
          </div>
          <div>
            <label className={isEditing ? 'form-label' : 'field-label'}>
              Marital Status
              {isEditing && !maritalLocked && (
                <span className="text-red-500"> *</span>
              )}
            </label>
            {isEditing && !maritalLocked ? (
              <FormSelect
                theme="employee"
                options={MARITAL_STATUS_OPTIONS}
                value={formData.hrprofile_marital_status || ''}
                onChange={(val) => handleInputChange({
                  target: { name: 'hrprofile_marital_status', value: val },
                })}
                placeholder="Select marital status"
                className={changedFields?.hrprofile_marital_status ? 'border-blue-300 bg-blue-50/30' : ''}
              />
            ) : (
              <p className={isEditing ? 'field-static' : 'field-display'}>
                {maritalDisplay}
              </p>
            )}
            {formErrors.hrprofile_marital_status && (
              <p className="form-error">{formErrors.hrprofile_marital_status}</p>
            )}
            <p className="form-help">
              {maritalLocked
                ? 'Updated from your verified marriage certificate. Contact HR if your status has changed.'
                : 'Used for benefits and family administration.'}
            </p>
          </div>
          <div>
            <label className={isEditing ? 'form-label' : 'field-label'}>
              Religion
            </label>
            {isEditing ? (
              <FormSelect
                theme="employee"
                options={RELIGION_OPTIONS}
                value={formData.hrprofile_religion || RELIGION_DEFAULT}
                onChange={(val) => handleInputChange({
                  target: { name: 'hrprofile_religion', value: val },
                })}
                className={changedFields?.hrprofile_religion ? 'border-blue-300 bg-blue-50/30' : ''}
              />
            ) : (
              <p className="field-display">
                {formatFieldValue(hrProfile?.hrprofile_religion)}
              </p>
            )}
            <p className="form-help">
              Used only for workplace accommodations such as dietary needs and religious observances.
            </p>
          </div>
          
          {/* Marriage Details - Show only when married */}
          {(formData.hrprofile_marital_status === 'married' || hrProfile?.hrprofile_marriage_date || getMarriageDateFromDependents()) && (
            <>
              <SensitiveFieldDisplay
                label="Marriage Date"
                name="hrprofile_marriage_date"
                type="date"
                value={hrProfile?.hrprofile_marriage_date || getMarriageDateFromDependents() || ''}
                fieldName="marriage_date"
                isEditing={isEditing}
                onChange={handleInputChange}
                sensitiveFieldsVisible={sensitiveFieldsVisible}
                toggleFieldVisibility={toggleFieldVisibility}
                helpText="For paternity leave eligibility verification"
                readOnly={true}
              />
            </>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-card-header mb-3 flex items-center gap-2">
          <Phone className="w-4 h-4 text-blue-800" />
          Contact Information
        </h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="field-label">Work Phone</label>
              <p className="field-display">
                {workContact.mobile
                  ? formatPhoneForDisplay(workContact.mobile)
                  : 'Not provided'}
              </p>
              <p className="form-help">
                Company mobile. Assigned lines are managed in the Asset Portal.
              </p>
            </div>

            <div>
              <label className="field-label">Direct line</label>
              <p className="field-display">
                {workContact.did
                  ? formatDirectLine(workContact.did, workContact.extension)
                  : 'Not provided'}
              </p>
              <p className="form-help">
                Office DID. The extension belongs to this number, not the mobile.
              </p>
            </div>

            <SensitiveFieldDisplay
              label="Personal Phone"
              name="hrprofile_personal_phone_number"
              value={formData.hrprofile_personal_phone_number}
              fieldName="personal_phone"
              type="phone"
              isEditing={isEditing}
              onChange={handleInputChange}
              error={formErrors.hrprofile_personal_phone_number}
              placeholder="Enter personal phone number"
              inputType="tel"
              hasChanged={changedFields?.hrprofile_personal_phone_number || false}
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
            />

            <SensitiveFieldDisplay
              label="Personal Email"
              name="hrprofile_personal_email"
              value={formData.hrprofile_personal_email}
              fieldName="personal_email"
              type="email"
              isEditing={isEditing}
              onChange={handleInputChange}
              error={formErrors.hrprofile_personal_email}
              placeholder="Enter personal email address"
              inputType="email"
              hasChanged={changedFields?.hrprofile_personal_email || false}
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
            />
          </div>

          <div className="pt-5 border-t border-gray-200">
            <ResidentialAddressEditor
              isEditing={isEditing}
              formData={formData}
              formErrors={formErrors}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              onFieldChange={handleInputChange}
              SensitiveFieldDisplay={SensitiveFieldDisplay}
              changedFields={changedFields}
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
              primaryAddress={hrProfile?.primary_address}
              theme="employee"
              audience="self"
              employmentPassType={hrProfile?.hrprofile_employment_pass_type}
            />
          </div>
        </div>
      </div>

      {/* Emergency Contacts */}
      <AccordionSection
        expanded={openSections.emergency}
        onToggle={() => toggleSection('emergency')}
        theme="employee"
        contentVariant="padded"
        icon={<Contact className="w-4 h-4 text-blue-800" />}
        iconBg="bg-blue-100"
        title={
          <span className="inline-flex items-center gap-2 min-w-0">
            <span className="truncate">Emergency Contacts</span>
            {emergencyContacts?.length > 0 ? (
              <CountBadge
                count={emergencyContacts.length}
                theme="employee"
                size="md"
                className="flex-shrink-0"
              />
            ) : null}
          </span>
        }
        trailing={
          <button
            type="button"
            onClick={() => emergencyAddHandlerRef.current?.()}
            className="btn btn-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Add emergency contact"
            title="Add emergency contact"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Add Contact</span>
          </button>
        }
      >
        <EmergencyContactsTab
          emergencyContacts={emergencyContacts}
          loading={emergencyContactsLoading}
          profile={profile}
          hrProfile={hrProfile}
          addHandlerRef={emergencyAddHandlerRef}
        />
      </AccordionSection>

      {/* Family & Dependents */}
      <AccordionSection
        expanded={openSections.family}
        onToggle={() => toggleSection('family')}
        theme="employee"
        contentVariant="padded"
        icon={<Heart className="w-4 h-4 text-blue-800" />}
        iconBg="bg-blue-100"
        title="Family & Dependents"
      >
        <FamilyDependentsTab
          dependents={dependents}
          setDependents={setDependents}
          childcareEligibility={childcareEligibility}
          setChildcareEligibility={setChildcareEligibility}
          loading={tabDataLoading?.family}
          fetchTabData={fetchTabData}
          readOnly={true}
          hrProfile={hrProfile}
          renderHeader={() => null}
        />
      </AccordionSection>

      {/* Identity Numbers */}
      <AccordionSection
        expanded={openSections.identity}
        onToggle={() => toggleSection('identity')}
        theme="employee"
        contentVariant="padded"
        icon={<Shield className="w-4 h-4 text-blue-800" />}
        iconBg="bg-blue-100"
        title="Identity Numbers"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SensitiveFieldDisplay
            label="Name as per NRIC/FIN"
            name="hrprofile_name_as_per_nric_fin"
            value={formData.hrprofile_name_as_per_nric_fin}
            fieldName="name_as_per_nric_fin"
            type="name"
            isEditing={isEditing}
            onChange={handleInputChange}
            readOnly={true}
            helpText={
              hrProfile?.hrprofile_name_as_per_nric_fin_verified_at
                ? `Verified by HR on ${formatDate(hrProfile.hrprofile_name_as_per_nric_fin_verified_at)}`
                : 'Provisional or missing — upload your NRIC/FIN for HR verification'
            }
            sensitiveFieldsVisible={sensitiveFieldsVisible}
            toggleFieldVisibility={toggleFieldVisibility}
          />
          <SensitiveFieldDisplay
            label="NRIC/FIN Number"
            name="hrprofile_nric_fin_number"
            value={formData.hrprofile_nric_fin_number}
            fieldName="nric_fin"
            type="nric"
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.hrprofile_nric_fin_number}
            placeholder="Enter NRIC/FIN"
            className="font-mono"
            readOnly={true}
            sensitiveFieldsVisible={sensitiveFieldsVisible}
            toggleFieldVisibility={toggleFieldVisibility}
          />
        </div>
      </AccordionSection>

      {/* Driving Licenses */}
      {drivingLicenses.length > 0 && (
        <AccordionSection
          expanded={openSections.driving}
          onToggle={() => toggleSection('driving')}
          theme="employee"
          contentVariant="padded"
          icon={<Car className="w-4 h-4 text-blue-800" />}
          iconBg="bg-blue-100"
          title={
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="truncate">Driving Licences</span>
              <CountBadge
                count={drivingLicenses.length}
                theme="employee"
                size="md"
                className="flex-shrink-0"
              />
            </span>
          }
        >
          <DrivingLicenseManager 
            staffId={profile?.staff_id}
            isEditing={isEditing}
            readOnly={true}
            employmentPassType={profile?.hrprofile_employment_pass_type}
          />
        </AccordionSection>
      )}
    </div>
  );
};

// Employment Tab Component
const EmploymentTab = ({
  profile,
  hrProfile,
  formData,
  formErrors,
  isEditing,
  handleInputChange,
  formatFieldValue,
  maskSensitiveData,
  showSensitiveData,
  onToggleSensitiveData
}) => {
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = React.useState({});
  
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Probation date calculation helpers
  const calculateDaysRemaining = (endDate) => getCalendarDaysUntil(endDate);

  const isProbationEndingSoon = (endDate, thresholdDays = 30) => {
    const daysRemaining = calculateDaysRemaining(endDate);
    return daysRemaining !== null && daysRemaining > 0 && daysRemaining <= thresholdDays;
  };

  const isProbationCompleted = (endDate) => {
    const daysRemaining = calculateDaysRemaining(endDate);
    return daysRemaining !== null && daysRemaining < 0;
  };

  return (
    <div className="space-y-4">
      {/* Pass & Immigration — number + expiry together (moved from Personal Identity Numbers) */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-card-header mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-800" />
          Pass & Immigration
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <SelectField
            label="Employment Pass Type"
            name="hrprofile_employment_pass_type"
            value={formData.hrprofile_employment_pass_type}
            isEditing={isEditing}
            onChange={handleInputChange}
            displayValue={formatFieldValue(hrProfile?.hrprofile_employment_pass_type)}
            options={[
              { value: '', label: 'Select Pass Type' },
              { value: 'citizen', label: 'Singapore Citizen' },
              { value: 'pr', label: 'Permanent Resident' },
              { value: 'ep', label: 'Employment Pass' },
              { value: 's_pass', label: 'S Pass' },
              { value: 'wp', label: 'Work Permit' },
              { value: 'ltvp', label: 'Long Term Visit Pass' },
              { value: 'dp', label: 'Dependant Pass' },
              { value: 'student_pass', label: 'Student Pass' },
              { value: 'visit_pass', label: 'Visit Pass' },
              { value: 'other', label: 'Other' }
            ]}
          />

          {formData.hrprofile_employment_pass_type === 'pr' && (
            <InputField
              label="PR Obtained Date"
              name="hrprofile_pr_obtained_date"
              type="date"
              value={formData.hrprofile_pr_obtained_date}
              isEditing={isEditing}
              onChange={handleInputChange}
              error={formErrors.hrprofile_pr_obtained_date}
              displayValue={hrProfile?.hrprofile_pr_obtained_date ? formatDate(hrProfile.hrprofile_pr_obtained_date) : 'Not provided'}
            />
          )}

          {['s_pass', 'wp'].includes(formData.hrprofile_employment_pass_type) && (
            <SensitiveFieldDisplay
              label={formData.hrprofile_employment_pass_type === 's_pass' ? 'S Pass Number' : 'Work Permit Number'}
              name="hrprofile_wp_number"
              value={formData.hrprofile_wp_number}
              fieldName="wp_number"
              type="wp_number"
              isEditing={isEditing}
              onChange={handleInputChange}
              error={formErrors.hrprofile_wp_number}
              placeholder={formData.hrprofile_employment_pass_type === 's_pass' ? 'Enter S Pass number' : 'Enter Work Permit number'}
              readOnly={true}
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
            />
          )}

          {formData.hrprofile_employment_pass_type &&
           ['wp', 's_pass', 'ep'].includes(formData.hrprofile_employment_pass_type) && (
            <>
              <SensitiveFieldDisplay
                label="Pass Issue Date"
                name="hrprofile_pass_issue_date"
                value={formData.hrprofile_pass_issue_date}
                fieldName="pass_issue_date"
                type="date"
                isEditing={isEditing}
                onChange={handleInputChange}
                error={formErrors.hrprofile_pass_issue_date}
                sensitiveFieldsVisible={sensitiveFieldsVisible}
                toggleFieldVisibility={toggleFieldVisibility}
              />
              <PassDateFieldDisplay
                theme="employee"
                label="Pass Expiry Date"
                name="hrprofile_pass_expiry_date"
                value={formData.hrprofile_pass_expiry_date}
                fieldName="pass_expiry_date"
                isEditing={isEditing}
                onChange={handleInputChange}
                error={formErrors.hrprofile_pass_expiry_date}
                reminderDays={formData.hrprofile_pass_renewal_reminder_days}
                isVisible={!!sensitiveFieldsVisible.pass_expiry_date}
                onToggleVisibility={toggleFieldVisibility}
              />
              {formData.hrprofile_employment_pass_type === 'wp' && (
                <PassDateFieldDisplay
                  theme="employee"
                  label="Security Bond Expiry"
                  name="security_bond_expiry_date"
                  value={profile?.security_bond_expiry_date || ''}
                  fieldName="security_bond_expiry"
                  isEditing={false}
                  reminderDays={90}
                  isVisible={!!sensitiveFieldsVisible.security_bond_expiry}
                  onToggleVisibility={toggleFieldVisibility}
                />
              )}
              {formData.hrprofile_employment_pass_type === 'wp' && (
                <SensitiveFieldDisplay
                  label="Work Permit Application Date"
                  name="hrprofile_wp_application_date"
                  value={formData.hrprofile_wp_application_date}
                  fieldName="wp_application_date"
                  type="date"
                  isEditing={isEditing}
                  onChange={handleInputChange}
                  error={formErrors.hrprofile_wp_application_date}
                  sensitiveFieldsVisible={sensitiveFieldsVisible}
                  toggleFieldVisibility={toggleFieldVisibility}
                />
              )}
            </>
          )}

          {formData.hrprofile_employment_pass_type &&
           !['citizen', 'pr'].includes(formData.hrprofile_employment_pass_type) && (
            <>
              <SensitiveFieldDisplay
                label="Passport Number"
                name="hrprofile_passport_number"
                value={formData.hrprofile_passport_number}
                fieldName="passport_number"
                type="passport"
                isEditing={isEditing}
                onChange={handleInputChange}
                error={formErrors.hrprofile_passport_number}
                placeholder="Enter passport number"
                readOnly={true}
                sensitiveFieldsVisible={sensitiveFieldsVisible}
                toggleFieldVisibility={toggleFieldVisibility}
              />
              <PassDateFieldDisplay
                theme="employee"
                label="Passport Expiry Date"
                name="hrprofile_passport_expiry_date"
                value={formData.hrprofile_passport_expiry_date}
                fieldName="passport_expiry_date"
                isEditing={isEditing}
                onChange={handleInputChange}
                error={formErrors.hrprofile_passport_expiry_date}
                reminderDays={formData.hrprofile_pass_renewal_reminder_days || 90}
                isVisible={!!sensitiveFieldsVisible.passport_expiry_date}
                onToggleVisibility={toggleFieldVisibility}
              />
            </>
          )}

          {formData.hrprofile_employment_pass_type &&
           ['wp', 's_pass', 'ep'].includes(formData.hrprofile_employment_pass_type) && (
            <InputField
              label="Renewal Reminder Days"
              name="hrprofile_pass_renewal_reminder_days"
              type="number"
              value={formData.hrprofile_pass_renewal_reminder_days}
              isEditing={isEditing}
              onChange={handleInputChange}
              error={formErrors.hrprofile_pass_renewal_reminder_days}
              displayValue={hrProfile?.hrprofile_pass_renewal_reminder_days || '90'}
              placeholder="Days before expiry to remind"
            />
          )}
        </div>
      </div>

      {/* Probation Information */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-card-header mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-800" />
          Probation Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <FieldLabelRow
              theme="form"
              label="Probation End Date"
            >
              {hrProfile?.hrprofile_probation_end_date && (
                <>
                  {profile?.probation_extended && (
                    <StatusBadge status="probation_extended" />
                  )}
                  {isProbationCompleted(hrProfile.hrprofile_probation_end_date) && (
                    <StatusBadge status="completed" label="Completed" showIcon />
                  )}
                  {isProbationEndingSoon(hrProfile.hrprofile_probation_end_date) && (
                    <StatusBadge status="pending" label="Ending Soon" showIcon />
                  )}
                  {!isProbationCompleted(hrProfile.hrprofile_probation_end_date) &&
                   !isProbationEndingSoon(hrProfile.hrprofile_probation_end_date) &&
                   !profile?.probation_extended && (
                    <StatusBadge status="active" />
                  )}
                </>
              )}
            </FieldLabelRow>
            <div className="relative">
              <p className={`field-display ${
                hrProfile?.hrprofile_probation_end_date
                  ? isProbationCompleted(hrProfile.hrprofile_probation_end_date)
                    ? 'text-green-900 !bg-green-50 !border-green-200'
                    : isProbationEndingSoon(hrProfile.hrprofile_probation_end_date)
                      ? 'text-orange-900 !bg-orange-50 !border-orange-200'
                      : ''
                  : ''
              }`}>
                {hrProfile?.hrprofile_probation_end_date 
                  ? formatDate(hrProfile.hrprofile_probation_end_date)
                  : 'Not provided'}
              </p>
            </div>
            {hrProfile?.hrprofile_probation_end_date && (
              <p className="text-xs text-gray-500 mt-2">
                {(() => {
                  const daysRemaining = calculateDaysRemaining(hrProfile.hrprofile_probation_end_date);
                  if (daysRemaining === null) return '';
                  const extendedPrefix = profile?.probation_extended ? 'Extended · ' : '';
                  if (daysRemaining < 0) {
                    return `${extendedPrefix}Completed ${formatDaySpan(Math.abs(daysRemaining), 'long')} ago`;
                  } else if (daysRemaining === 0) {
                    return `${extendedPrefix}Ends today`;
                  } else {
                    return `${extendedPrefix}${formatDaySpan(daysRemaining, 'long')} remaining`;
                  }
                })()}
              </p>
            )}
          </div>
        </div>
      </div>

      <EmploymentHistorySection staffId={profile?.staff_id} theme="employee" detailLevel="summary" />
    </div>
  );
};

// Reusable Input Field Component
const InputField = ({ 
  label, 
  name, 
  type = 'text', 
  value, 
  isEditing, 
  onChange, 
  error, 
  displayValue, 
  required = false,
  sensitive = false,
  maskValue,
  showSensitiveData,
  placeholder,
  disabled = false,
  readOnly = false,
  hasChanged = false,
  helpText,
  maxLength,
}) => (
  <div>
    <label className={isEditing && !readOnly ? 'form-label' : 'field-label'}>
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    {isEditing && !readOnly ? (
      <div className="relative">
        {type === 'date' ? (
          <DatePickerField
            theme="employee"
            selected={parseLocalDate(value)}
            onChange={(date) => onChange({ target: { name, value: date ? toLocalISODate(date) : '' } })}
            placeholderText={placeholder || "Select date"}
            disabled={disabled}
          />
        ) : (
          <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            maxLength={maxLength}
            className={`input px-4 rounded-full ${error ? 'input-error' : ''} ${hasChanged ? 'border-blue-300 bg-blue-50/30' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder={placeholder}
          />
        )}
        {helpText && (
          <p className="form-help">{helpText}</p>
        )}
        {error && (
          <p className="form-error flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
      </div>
    ) : (
      <p className="field-display">
        {sensitive && !showSensitiveData ? maskValue : displayValue}
      </p>
    )}
  </div>
);

// Reusable Select Field Component
const SelectField = ({ 
  label, 
  name, 
  value, 
  isEditing, 
  onChange, 
  displayValue, 
  options, 
  readOnly = false,
  isSensitive = false,
  fieldName = '',
  sensitiveFieldsVisible = {},
  toggleFieldVisibility = () => {},
  maskedValue = maskValue('_', 'default')
}) => {
  const isVisible = sensitiveFieldsVisible[fieldName];
  const hasValue = displayValue && displayValue !== 'Not provided';
  const showValue = isSensitive && hasValue && !isVisible ? maskedValue : displayValue;

  return (
    <div>
      <label className={isEditing && !readOnly ? 'form-label' : 'field-label'}>
        {label}
      </label>
      {isEditing && !readOnly ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          className="select px-4 rounded-full"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <div className="relative">
          <p className={`field-display ${isSensitive && hasValue ? 'field-display-sensitive' : ''}`}>
            {showValue || 'Not provided'}
          </p>
          {isSensitive && hasValue && (
            <SensitiveRevealButton
              isVisible={!!isVisible}
              onClick={() => toggleFieldVisibility(fieldName)}
              theme="employee"
            />
          )}
        </div>
      )}
    </div>
  );
};

// Reusable TextArea Field Component
const TextAreaField = ({ 
  label, 
  name, 
  value, 
  isEditing, 
  onChange, 
  displayValue, 
  rows = 3,
  sensitive = false,
  maskValue,
  showSensitiveData,
  icon 
}) => (
  <div>
    <label className={`${isEditing ? 'form-label' : 'field-label'} flex items-center gap-2`}>
      {icon}
      {label}
    </label>
    {isEditing ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={rows}
        className="textarea px-4 py-3 rounded-xl min-h-[80px]"
      />
    ) : (
      <p className="field-display field-display-multiline">
        {sensitive && !showSensitiveData ? maskValue : displayValue}
      </p>
    )}
  </div>
);

// Emergency Contacts Tab Component
// Professional Profile Tab Component (Combined)
const ProfessionalProfileTab = ({
  profile,
  education,
  setEducation,
  workExperience,
  setWorkExperience,
  certificates,
  setCertificates,
  tabDataLoading,
  fetchTabData,
  activeTab
}) => {
  // Memoize config object to prevent unnecessary re-renders in useNestedTabPersistence
  const subTabConfig = useMemo(() => ({
    subTab: {
      default: 'timeline',
      valid: ['timeline', 'education', 'experience', 'certs'],
      param: 'subTab'
    }
  }), []);
  
  // Use nested tab persistence for sub-tabs (timeline, education, experience, certs)
  const { subTab: subTabState } = useNestedTabPersistence(
    'profile-professional', 
    activeTab === 'professional' ? activeTab : null, 
    subTabConfig
  );
  const [subTab, setSubTab] = subTabState;
  
  // NOTE: Auto-refresh removed to prevent infinite loops with 401 errors.
  // Data is fetched by the parent Profile component when activeTab changes.
  // Users can manually refresh by switching tabs or reloading the page.

  // ── P1 & P3 timeline data hooks (P2 & P4 reuse props) ────────────────
  const staffId = profile?.staff_id;
  const isTimelineActive = subTab === 'timeline';

  const { data: myHRLetters = [] } = useMyHRLetters(isTimelineActive);
  const { data: trainingEnrollments = [] } = useEmployeeTrainingEnrollments(
    isTimelineActive ? staffId : null
  );

  const buildTimeline = () => {
    const events = [];
    const empStart = profile?.employment_start_date;
    const empStartYear = empStart ? new Date(empStart).getFullYear() : null;
    
    // ── Core career events ──────────────────────────────────────────────
    if (empStart) {
      events.push({
        date: empStart,
        type: 'system',
        title: 'Joined Company',
        subtitle: 'Career milestone',
      });
    }
    
    // Probation milestones (employee audience — no overdue, no internal reason)
    appendProbationTimelineEvents(events, {
      probEndDate: profile?.hrprofile_probation_end_date,
      isProbation: profile?.employment_status === 'probation',
      probationExtended: !!profile?.probation_extended,
      extensionReason: profile?.probation_extension_reason,
      probationStartDate: profile?.probation_start_date || empStart,
      audience: 'employee',
    });
    
    if (profile?.employment_end_date) {
      events.push({
        date: profile.employment_end_date,
        type: 'system',
        title: 'Left Company',
        subtitle: 'Career milestone',
      });
    }

    // ── P1: HR Letter milestones (promotion, confirmation, increment, etc.) ─
    // NOTE: Warning/termination letters excluded from self-service via isMilestoneLetterCode
    myHRLetters.forEach((letter) => {
      if (!isMilestoneLetterCode(letter.template_code)) return;
      events.push({
        date: letter.generated_at?.split('T')[0] || letter.generated_at,
        title: letter.letter_type_display || letter.template_code,
        type: 'hr_letter',
        subtitle: letter.letter_reference || 'HR letter issued',
      });
    });

    // ── P2: Professional certificates obtained during employment ─────────
    if (empStart && certificates?.length) {
      certificates.forEach((cert) => {
        if (!cert.cert_issue_date) return;
        if (cert.cert_issue_date < empStart) return; // Only during employment
        events.push({
          date: cert.cert_issue_date,
          title: cert.cert_name,
          type: 'certificate',
          subtitle: cert.cert_issuing_organization || 'Professional certificate',
        });
      });
    }

    // ── P3: Training completions ────────────────────────────────────────
    trainingEnrollments.forEach((enrollment) => {
      if (!enrollment.completion_date) return;
      const courseTitle = enrollment.course_detail?.course_title || 'Training course';
      events.push({
        date: enrollment.completion_date,
        title: courseTitle,
        type: 'training',
        subtitle: enrollment.certification_issued ? 'Completed — certification issued' : 'Course completed',
      });
    });

    // ── P4: Education completed during employment ───────────────────────
    if (empStartYear && education?.length) {
      education.forEach((edu) => {
        if (!edu.education_end_year) return;
        if (edu.education_is_ongoing) return;
        if (edu.education_end_year < empStartYear) return; // Only during employment
        events.push({
          date: `${edu.education_end_year}-12-31`, // Synthetic date for sorting
          title: edu.education_qualification_name || 'Education',
          type: 'education',
          subtitle: edu.institution_display_name || edu.education_field_of_study || 'Education completed',
          yearOnly: true,
        });
      });
    }
    
    return events.filter(e => e.date).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Color map for timeline event dot and label (blue-900 base for self-service theme)
  // `extended` uses Info-tier blue to align with StatusBadge probation_extended
  const eventStyles = {
    system:      { dot: 'bg-blue-900',   label: 'text-gray-600' },
    milestone:   { dot: 'bg-green-500',  label: 'text-green-700' },
    upcoming:    { dot: 'bg-amber-400 border-dashed', label: 'text-amber-700' },
    extended:    { dot: 'bg-blue-500',   label: 'text-blue-700' },
    hr_letter:   { dot: 'bg-indigo-500', label: 'text-indigo-700' },
    certificate: { dot: 'bg-amber-500',  label: 'text-amber-700' },
    training:    { dot: 'bg-teal-500',   label: 'text-teal-700' },
    education:   { dot: 'bg-purple-500', label: 'text-purple-700' },
  };

  const events = buildTimeline();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6" style={{ touchAction: 'pan-x pan-y', WebkitOverflowScrolling: 'touch' }}>
        <button
          onClick={() => setSubTab('timeline')}
          className={`tab-btn tab-btn-sm ${subTab === 'timeline' ? 'active' : ''}`}
          title="Career Timeline"
        >
          <Clock className="w-4 h-4" />
          Career Timeline
        </button>
        <button
          onClick={() => setSubTab('education')}
          className={`tab-btn tab-btn-sm ${subTab === 'education' ? 'active' : ''}`}
          title="Education"
        >
          <GraduationCap className="w-4 h-4" />
          Education
        </button>
        <button
          onClick={() => setSubTab('experience')}
          className={`tab-btn tab-btn-sm ${subTab === 'experience' ? 'active' : ''}`}
          title="Work Experience"
        >
          <Briefcase className="w-4 h-4" />
          Experience
        </button>
        <button
          onClick={() => setSubTab('certs')}
          className={`tab-btn tab-btn-sm ${subTab === 'certs' ? 'active' : ''}`}
          title="Professional Certificates"
        >
          <Award className="w-4 h-4" />
          Certificates
        </button>
      </div>

      {subTab === 'timeline' && (
        <div className="bg-white rounded-2xl border p-4">
          {events.length === 0 ? (
            <div className="text-center py-10 text-gray-600">No career events yet.</div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
              <ul className="space-y-8">
                {events.map((e, idx) => {
                  const style = eventStyles[e.type] || eventStyles.system;
                  return (
                    <li key={idx} className="relative pl-12">
                      <div className={`absolute left-2 w-4 h-4 rounded-full ${style.dot} border-2 border-white shadow`} />
                      <div>
                        <div className="text-xs text-gray-500">
                          {e.yearOnly ? new Date(e.date).getFullYear() : (e.date ? formatDate(e.date) : '')}
                        </div>
                        <CareerTimelineEventBody
                          title={e.title}
                          subtitle={e.subtitle}
                          details={e.details}
                          subtitleClassName={style.label}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {subTab === 'education' && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-card-header flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-blue-800" />
              Education & Qualifications
            </h3>
          </div>
          <EducationTab
            education={education}
            setEducation={setEducation}
            loading={tabDataLoading.education}
            fetchTabData={fetchTabData}
          />
        </div>
      )}

      {subTab === 'experience' && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-card-header flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-blue-800" />
              Work Experience
            </h3>
          </div>
          <WorkExperienceTab
            workExperience={workExperience}
            setWorkExperience={setWorkExperience}
            loading={tabDataLoading.experience}
            fetchTabData={fetchTabData}
          />
        </div>
      )}

      {subTab === 'certs' && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-card-header flex items-center gap-2">
              <Award className="w-4 h-4 text-blue-800" />
              Professional Certificates
            </h3>
          </div>
          <CertificatesTab
            certificates={certificates}
            setCertificates={setCertificates}
            loading={tabDataLoading.certificates}
            fetchTabData={fetchTabData}
          />
        </div>
      )}
    </div>
  );
};

// Compensation & Benefits Tab Component
const CompensationBenefitsTab = ({ 
  profile,
  hrProfile, 
  formData, 
  formErrors, 
  isEditing, 
  handleInputChange, 
  formatFieldValue, 
  maskSensitiveData,
  showSensitiveData,
  onToggleSensitiveData
}) => {
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = React.useState({});
  
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };


  const isStaffClassification = profile?.employment_classification === 'staff';

  return (
    <div className="space-y-4">
      {/* Current Compensation */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-card-header mb-3 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-blue-800" />
          Current Compensation
        </h3>
        {isStaffClassification ? (
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-6 text-center">
            <DollarSign className="w-8 h-8 text-gray-400 mx-auto mb-2" aria-hidden="true" />
            <p className="text-sm font-medium text-gray-900">Salary data coming soon</p>
            <p className="text-xs text-gray-500 mt-1">
              Your basic salary will appear here once payroll data is available for staff.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <SensitiveFieldDisplay
              label="Monthly Basic Salary"
              name="payroll_basic_salary"
              value={profile?.payroll_basic_salary}
              fieldName="basic_salary"
              type="currency"
              readOnly={true}
              helpText="Your base monthly salary before allowances"
              sensitiveFieldsVisible={sensitiveFieldsVisible}
              toggleFieldVisibility={toggleFieldVisibility}
            />
            <InputField
              label="Pay Frequency"
              name="pay_frequency"
              value="Monthly"
              isEditing={false}
              displayValue="Monthly"
              disabled={true}
            />
          </div>
        )}
      </div>

      {/* Benefits & Allowances */}
      <EmployeeAllowancesSection 
        employee={profile} 
        formatCurrency={formatCurrency}
        readOnly={true}
        theme="employee"
      />

      {/* Fixed Deductions */}
      <EmployeeDeductionsSection
        employee={profile}
        formatCurrency={formatCurrency}
        readOnly={true}
        theme="employee"
      />

      {/* Payment Details */}
      <div className="bg-gray-50 rounded-xl p-4">
        <h3 className="text-card-header mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-blue-800" />
          Payment Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField
            label="Bank Name"
            name="hrprofile_bank_name"
            value={formData.hrprofile_bank_name}
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.hrprofile_bank_name}
            displayValue={formatFieldValue(hrProfile?.hrprofile_bank_name)}
            placeholder="Enter bank name"
          />

          <InputField
            label="Branch Code"
            name="hrprofile_branch_code"
            value={formData.hrprofile_branch_code}
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.hrprofile_branch_code}
            displayValue={formatFieldValue(hrProfile?.hrprofile_branch_code)}
            placeholder="Enter branch code"
          />

          <SensitiveFieldDisplay
            label="Bank Account Number"
            name="hrprofile_bank_account_number"
            value={formData.hrprofile_bank_account_number}
            fieldName="bank_account_number"
            type="account"
            isEditing={isEditing}
            onChange={handleInputChange}
            error={formErrors.hrprofile_bank_account_number}
            placeholder="Enter account number"
            sensitiveFieldsVisible={sensitiveFieldsVisible}
            toggleFieldVisibility={toggleFieldVisibility}
          />

          <SensitiveFieldDisplay
            label="Account Holder Name"
            name="hrprofile_bank_account_holder_name"
            value={formData.hrprofile_bank_account_holder_name}
            fieldName="bank_account_holder_name"
            type="name"
            isEditing={isEditing}
            onChange={handleInputChange}
            readOnly={true}
            helpText={
              hrProfile?.hrprofile_bank_account_holder_name_verified_at
                ? `Verified by HR on ${formatDate(hrProfile.hrprofile_bank_account_holder_name_verified_at)}`
                : 'Unverified — upload a bank statement for HR verification'
            }
            sensitiveFieldsVisible={sensitiveFieldsVisible}
            toggleFieldVisibility={toggleFieldVisibility}
          />

          <SelectField
            label="Payment Mode"
            name="hrprofile_pay_mode"
            value={formData.hrprofile_pay_mode}
            isEditing={isEditing}
            onChange={handleInputChange}
            displayValue={formatFieldValue(hrProfile?.hrprofile_pay_mode)}
            options={[
              { value: 'giro', label: 'GIRO' },
              { value: 'bank_transfer', label: 'Bank Transfer' },
              { value: 'cheque', label: 'Cheque' }
            ]}
          />
        </div>
      </div>

    </div>
  );
};

// Legal & Compliance Tab Component
const ComplianceTab = ({
  hrProfile,
  profile,
}) => {
  return (
    <div className="space-y-6">
      {/* PDPA Consent Status Section - Primary Information */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-blue-800" />
          <h3 className="text-subsection-header">PDPA Consent Status</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Consent Status Badge */}
          <div>
            <label className="field-label">Consent Status</label>
            <StatusBadge
              status={hrProfile?.hrprofile_consent_obtained ? 'approved' : 'pending'}
              label={hrProfile?.hrprofile_consent_obtained ? 'Consent Obtained' : 'Consent Pending'}
              showIcon
            />
          </div>

          {/* Consent Date */}
          {hrProfile?.hrprofile_consent_date && (
            <div>
              <label className="field-label">Consent Date</label>
              <p className="field-display">
                {formatDateTime(hrProfile.hrprofile_consent_date)}
              </p>
            </div>
          )}
        </div>

        {/* Privacy Policy Link */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Link 
            to="/privacy-policy" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded px-3 py-2"
          >
            <ExternalLink className="w-4 h-4" />
            View Full Privacy Policy
          </Link>
          <p className="text-xs text-gray-500 mt-2">
            Review the complete privacy policy to understand how your personal data is collected, used, and protected.
          </p>
        </div>
      </div>

      {/* Your Data Rights Section */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <div className="flex items-center gap-2 mb-4">
          <UserCheck className="w-5 h-5 text-blue-800" />
          <h3 className="text-subsection-header">Your Data Rights</h3>
        </div>

        <p className="text-sm text-gray-700 mb-4">
          Under the Personal Data Protection Act (PDPA), you have the following rights regarding your personal data:
        </p>

        <div className="space-y-4">
          {/* Right to Access */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Right to Access</h4>
                <p className="text-sm text-gray-600">
                  You can view and access all your personal data stored in this HR Portal through your Profile page. 
                  All information is accessible in the respective tabs.
                </p>
              </div>
            </div>
          </div>

          {/* Right to Correction */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Edit3 className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Right to Correction</h4>
                <p className="text-sm text-gray-600">
                  You can request corrections to your personal data. Use the edit mode in your Profile to update 
                  information, or contact HR for assistance with sensitive data fields.
                </p>
              </div>
            </div>
          </div>

          {/* Right to Withdraw Consent */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Right to Withdraw Consent</h4>
                <p className="text-sm text-gray-600 mb-3">
                  You may withdraw your consent at any time by contacting HR. Note that withdrawal may affect our 
                  ability to fulfill employment obligations and statutory requirements.
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">Contact HR:</span>
                  <a 
                    href="mailto:hrm@winner-e.sg" 
                    className="text-blue-600 hover:text-blue-800 font-medium hover:underline"
                  >
                    hrm@winner-e.sg
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="mt-4 pt-4 border-t border-blue-200">
          <Link 
            to="/privacy-policy" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Learn more about your rights in the Privacy Policy
          </Link>
        </div>
      </div>

      {/* Digital Name Card Consent */}
      <NameCardConsentSection
        profile={profile}
        hrProfile={hrProfile}
        classification={profile?.employment_classification}
      />
    </div>
  );
};

const EmergencyContactsTab = ({ emergencyContacts, loading, profile, hrProfile, addHandlerRef }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_relationship_other: '',
    emergency_contact_primary_phone: '',  // E.164 format: +6590000000
    emergency_contact_secondary_phone: '',  // E.164 format: +6590000000
    emergency_contact_email: ''  // Optional email
  });
  
  // Determine if employee is foreign (not citizen/PR)
  const isForeignEmployee = useMemo(() => {
    const passType = hrProfile?.hrprofile_employment_pass_type;
    return passType && !['citizen', 'pr'].includes(passType);
  }, [hrProfile?.hrprofile_employment_pass_type]);
  const [formErrors, setFormErrors] = useState({});
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = useState({});
  
  // Use React Query mutations
  const createMutation = useCreateEmergencyContact();
  const updateMutation = useUpdateEmergencyContact();
  const deleteMutation = useDeleteEmergencyContact();

  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };

  // Toggle visibility of sensitive fields (phone numbers)
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const maskSensitiveData = (value, fieldName) => {
    if (!value) return 'Not provided';
    if (sensitiveFieldsVisible[fieldName]) return value;
    return maskValue(value, 'phone');
  };

  const resetForm = () => {
    setFormData({
      emergency_contact_name: '',
      emergency_contact_relationship: '',
      emergency_contact_relationship_other: '',
      emergency_contact_primary_phone: '',
      emergency_contact_secondary_phone: '',
      emergency_contact_email: ''
    });
    setFormErrors({});
    setIsAdding(false);
    setEditingContact(null);
  };

  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (!formData.emergency_contact_name.trim()) {
      errors.emergency_contact_name = 'Name is required';
    }
    
    // Relationship validation
    if (!formData.emergency_contact_relationship.trim()) {
      errors.emergency_contact_relationship = 'Relationship is required';
    } else if (!EMERGENCY_CONTACT_RELATIONSHIP_VALUES.has(formData.emergency_contact_relationship)) {
      errors.emergency_contact_relationship = formData.emergency_contact_relationship === 'parent'
        ? 'Please choose Father or Mother'
        : 'Please select a valid relationship';
    }
    
    // Primary phone validation - required and must be valid E.164 format
    if (!formData.emergency_contact_primary_phone.trim()) {
      errors.emergency_contact_primary_phone = 'Primary phone number is required';
    } else if (formData.emergency_contact_primary_phone.trim()) {
      try {
        if (!isValidPhoneNumber(formData.emergency_contact_primary_phone)) {
          errors.emergency_contact_primary_phone = 'Please enter a valid phone number in international format (e.g., +65 9000 0000)';
        }
      } catch (error) {
        errors.emergency_contact_primary_phone = 'Please enter a valid phone number in international format (e.g., +65 9000 0000)';
      }
    }
    
    // Secondary phone validation - optional but must be valid if provided
    if (formData.emergency_contact_secondary_phone && formData.emergency_contact_secondary_phone.trim()) {
      try {
        if (!isValidPhoneNumber(formData.emergency_contact_secondary_phone)) {
          errors.emergency_contact_secondary_phone = 'Please enter a valid phone number in international format (e.g., +65 9000 0000)';
        }
      } catch (error) {
        errors.emergency_contact_secondary_phone = 'Please enter a valid phone number in international format (e.g., +65 9000 0000)';
      }
    }
    
    // Email validation
    if (formData.emergency_contact_email && formData.emergency_contact_email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.emergency_contact_email)) {
        errors.emergency_contact_email = 'Please enter a valid email address';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    const backendData = { ...formData };
    delete backendData.emergency_contact_relationship_other;
    
    // Remove empty optional fields to avoid validation issues
    if (!backendData.emergency_contact_secondary_phone || !backendData.emergency_contact_secondary_phone.trim()) {
      delete backendData.emergency_contact_secondary_phone;
    }
    if (!backendData.emergency_contact_email || !backendData.emergency_contact_email.trim()) {
      delete backendData.emergency_contact_email;
    }
    
    try {
      if (editingContact) {
        await updateMutation.mutateAsync({ id: editingContact.id, data: backendData });
      } else {
        await createMutation.mutateAsync(backendData);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving emergency contact:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        
        // If backend returns field-specific errors, set them in formErrors
        if (typeof errorData === 'object' && !errorData.detail) {
          const backendErrors = {};
          Object.keys(errorData).forEach(key => {
            // Handle both string and array error messages
            const errorMessage = Array.isArray(errorData[key]) 
              ? errorData[key][0] 
              : errorData[key];
            backendErrors[key] = errorMessage;
          });
          setFormErrors(backendErrors);
        }
      } else if (error.response?.status === 409) {
        // Conflict - duplicate contact (handled by mutation onError, but set form error too)
        setFormErrors({
          emergency_contact_primary_phone: 'A contact with this phone number already exists. Please use a different phone number or edit the existing contact.'
        });
      }
      // Toast notifications are handled by mutation hooks
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    
    const relationship = contact.emergency_contact_relationship || '';

    setFormData({
      emergency_contact_name: contact.emergency_contact_name || '',
      emergency_contact_relationship: relationship,
      emergency_contact_relationship_other: '',
      emergency_contact_primary_phone: contact.emergency_contact_primary_phone || '',
      emergency_contact_secondary_phone: contact.emergency_contact_secondary_phone || '',
      emergency_contact_email: contact.emergency_contact_email || ''
    });
    setIsAdding(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Emergency Contact',
      message: 'Are you sure you want to delete this emergency contact?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    
    try {
      await deleteMutation.mutateAsync(id);
      // Toast and cache invalidation handled by mutation hook
    } catch (error) {
      console.error('Error deleting emergency contact:', error);
      // Error toast handled by mutation hook
    }
  };

  // Register Add Contact with accordion trailing CTA (must run before any early return)
  const handleAddContact = React.useCallback(() => {
    try {
      resetForm();
      setIsAdding(true);
    } catch (error) {
      console.error('Error opening add contact modal:', error);
    }
  }, []);

  React.useLayoutEffect(() => {
    if (!addHandlerRef) return undefined;
    addHandlerRef.current = handleAddContact;
    return () => {
      addHandlerRef.current = null;
    };
  }, [addHandlerRef, handleAddContact]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        Manage emergency contact information for your profile
      </p>

      {/* Emergency Contact Modal */}
      <EmergencyContactModal
        isOpen={isAdding}
        onClose={resetForm}
        formData={formData}
        formErrors={formErrors}
        editingContact={editingContact}
        saving={createMutation.isPending || updateMutation.isPending}
        isForeignEmployee={isForeignEmployee}
        onInputChange={handleInputChange}
        onSave={handleSave}
      />

      {/* Old Inline Form - Removed */}
      {false && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="text-base font-semibold text-gray-900 mb-3">
            {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
          </h4>
          
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                         <InputField
              label="Full Name"
              name="emergency_contact_name"
              value={formData.emergency_contact_name}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.emergency_contact_name}
              required
              placeholder="Enter full name"
            />

            <SelectField
              label="Relationship"
              name="emergency_contact_relationship"
              value={formData.emergency_contact_relationship}
              isEditing={true}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Select relationship' },
                { value: 'spouse', label: 'Spouse' },
                { value: 'parent', label: 'Parent' },
                { value: 'child', label: 'Child' },
                { value: 'sibling', label: 'Sibling' },
                { value: 'friend', label: 'Friend' },
                { value: 'relative', label: 'Relative' },
                { value: 'other', label: 'Other' }
              ]}
            />

            <InputField
              label="Phone Number"
              name="emergency_contact_phone"
              type="tel"
              value={formData.emergency_contact_phone}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.emergency_contact_phone}
              required
              placeholder="Enter phone number"
            />

            <InputField
              label="Email (Optional)"
              name="emergency_contact_email"
              type="email"
              value={formData.emergency_contact_email}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.emergency_contact_email}
              placeholder="Enter email address"
            />

          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={resetForm}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="btn btn-primary"
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2 text-white" />
              )}
              {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (editingContact ? 'Update Contact' : 'Add Contact')}
            </button>
          </div>
        </div>
      )}

      {/* Contacts List */}
      <div className="space-y-4">
        {!emergencyContacts || emergencyContacts.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <Contact className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No emergency contacts added yet</h3>
            <p className="text-sm text-gray-600">Add at least one emergency contact for safety purposes</p>
          </div>
        ) : (
          emergencyContacts.map((contact) => (
            <div key={contact.id} className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h4 className="text-base font-semibold text-gray-900">{contact.emergency_contact_name}</h4>
                    <InfoBadge color="blue" label={formatEmergencyContactRelationship(contact.emergency_contact_relationship)} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                      <Phone className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="font-mono">
                        {maskSensitiveData(contact.emergency_contact_primary_phone || contact.emergency_contact_phone, `phone_primary_${contact.id}`)}
                      </span>
                      <SensitiveRevealButton
                        isVisible={!!sensitiveFieldsVisible[`phone_primary_${contact.id}`]}
                        onClick={() => toggleFieldVisibility(`phone_primary_${contact.id}`)}
                        theme="employee"
                        placement="inline"
                        titleShow="Show primary phone number"
                        titleHide="Hide primary phone number"
                      />
                      <span className="text-xs text-gray-500">(Primary)</span>
                    </div>
                    {contact.emergency_contact_secondary_phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                        <Phone className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="font-mono">
                          {maskSensitiveData(contact.emergency_contact_secondary_phone, `phone_secondary_${contact.id}`)}
                        </span>
                        <SensitiveRevealButton
                          isVisible={!!sensitiveFieldsVisible[`phone_secondary_${contact.id}`]}
                          onClick={() => toggleFieldVisibility(`phone_secondary_${contact.id}`)}
                          theme="employee"
                          placement="inline"
                          titleShow="Show secondary phone number"
                          titleHide="Hide secondary phone number"
                        />
                        <span className="text-xs text-gray-500">(Secondary)</span>
                      </div>
                    )}
                    {contact.emergency_contact_email && (
                      <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
                        <Mail className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span>{contact.emergency_contact_email}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 md:ml-4">
                  <button
                    onClick={() => handleEdit(contact)}
                    className="icon-button"
                    title="Edit contact"
                    aria-label="Edit emergency contact"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(contact.id)}
                    className="icon-button-danger"
                    title="Delete contact"
                    aria-label="Delete emergency contact"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Prompt to Add Second Contact (Foreign Employees) */}
      {isForeignEmployee && emergencyContacts.length === 1 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-yellow-800 mb-2">
                Consider adding a <strong>Home Country contact</strong> (family member) as your second emergency contact.
              </p>
              <button
                type="button"
                onClick={() => {
                  try {
                    setIsAdding(true);
                  } catch (error) {
                    console.error('Error opening add home country contact modal:', error);
                  }
                }}
                className="btn btn-primary min-h-[44px]"
                aria-label="Add home country emergency contact"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Home Country Contact
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="employee"
      />
    </div>
  );
};

// Lightweight document viewer modal — shows the raw file (PDF or image) inline.
// Intentionally separate from DocumentVerificationModal which is an OCR editing tool.
const EducationTab = ({ education, setEducation, loading, fetchTabData }) => {
  const [editingEducation, setEditingEducation] = useState(null);
  const [formData, setFormData] = useState({
    education_institution_name: '',
    education_qualification_name: '',
    education_field_of_study: '',
    education_start_year: '',
    education_end_year: '',
    education_is_ongoing: false,
    education_country: 'Singapore',
    education_certificate_number: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [docViewer, setDocViewer] = useState({ open: false, edu: null });
  const [downloading, setDownloading] = useState(false);

  const handleEduDownload = async (edu) => {
    setDownloading(true);
    try {
      const res = await api.downloadEducation(edu.id);
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = edu.education_document_path ? edu.education_document_path.split('/').pop() : `education_${edu.id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };

  const getEducationStatusKey = (status) => {
    if (!status) return 'pending';
    const statusLower = status.toLowerCase();
    if (statusLower === 'verified') return 'verified';
    if (statusLower === 'invalid') return 'rejected';
    return 'pending';
  };

  const getEducationStatusLabel = (status) => {
    if (!status) return 'Pending HR';
    const statusLower = status.toLowerCase();
    if (statusLower === 'verified') return 'Verified';
    if (statusLower === 'invalid') return 'Rejected';
    return 'Pending HR';
  };

  const resetForm = () => {
    setFormData({
      education_institution_name: '',
      education_qualification_name: '',
      education_field_of_study: '',
      education_start_year: '',
      education_end_year: '',
      education_is_ongoing: false,
      education_country: 'Singapore',
      education_certificate_number: ''
    });
    setFormErrors({});
    setEditingEducation(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.education_institution_name.trim()) {
      errors.education_institution_name = 'Institution name is required';
    }
    if (!formData.education_qualification_name.trim()) {
      errors.education_qualification_name = 'Qualification name is required';
    }
    // Start year is optional (many certificates don't include start year)
    // If provided, must be valid (between 1950 and current year)
    if (formData.education_start_year && formData.education_start_year !== '') {
      const startYear = parseInt(formData.education_start_year);
      const currentYear = new Date().getFullYear();
      if (isNaN(startYear) || startYear < 1950 || startYear > currentYear) {
        errors.education_start_year = `Start year must be between 1950 and ${currentYear}`;
      }
    }
    // End year validation
    if (!formData.education_is_ongoing) {
      if (!formData.education_end_year || formData.education_end_year === '') {
        errors.education_end_year = 'End year is required for completed education';
      } else {
        const endYear = parseInt(formData.education_end_year);
        const startYear = formData.education_start_year ? parseInt(formData.education_start_year) : null;
        const currentYear = new Date().getFullYear();
        if (isNaN(endYear) || endYear < 1950 || endYear > currentYear) {
          errors.education_end_year = `End year must be between 1950 and ${currentYear}`;
        } else if (startYear && endYear < startYear) {
          errors.education_end_year = 'End year cannot be before start year';
        }
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || '' : value)
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const submitData = { ...formData };
      if (submitData.education_is_ongoing) {
        submitData.education_end_year = null;
      }
      
      if (editingEducation) {
        await api.updateEducation(editingEducation.id, submitData);
        await fetchTabData('education');
        resetForm();
      }
    } catch (error) {
      console.error('Error saving education:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (edu) => {
    setEditingEducation(edu);
    setFormData({
      education_institution_name: edu.education_institution_name || '',
      education_qualification_name: edu.education_qualification_name || '',
      education_field_of_study: edu.education_field_of_study || '',
      education_start_year: edu.education_start_year || '',
      education_end_year: edu.education_end_year || '',
      education_is_ongoing: edu.education_is_ongoing || false,
      education_country: edu.education_country || 'Singapore',
      education_certificate_number: edu.education_certificate_number || ''
    });
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Education Record',
      message: 'Are you sure you want to delete this education record?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    
    try {
      await api.deleteEducation(id);
      await fetchTabData('education');
    } catch (error) {
      console.error('Error deleting education:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner when records exist */}
      {education && education.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 mb-4">
          <p className="text-xs text-gray-600 text-center">
            To add more education, upload certificates in Documents → Uploads
          </p>
        </div>
      )}

      {/* Edit Form (only shown when editing existing record) */}
      {editingEducation && (
        <div className="bg-gray-50 rounded-2xl p-6">
          <h4 className="text-base font-semibold text-gray-900 mb-3">
            Edit Education Record
          </h4>
          
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
             <InputField
               label="Institution Name"
               name="education_institution_name"
              value={formData.education_institution_name}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.education_institution_name}
              required
              placeholder="e.g. National University of Singapore"
            />

            <div className="md:col-span-2">
              <InputField
                label="Qualification Name"
                name="education_qualification_name"
                value={formData.education_qualification_name}
                isEditing={true}
                onChange={handleInputChange}
                error={formErrors.education_qualification_name}
                required
                placeholder="e.g. Bachelor of Engineering in Civil Engineering"
              />
            </div>

            <InputField
              label="Field of Study"
              name="education_field_of_study"
              value={formData.education_field_of_study}
              isEditing={true}
              onChange={handleInputChange}
              placeholder="e.g. Civil Engineering"
            />

            <InputField
              label="Certificate Number"
              name="education_certificate_number"
              value={formData.education_certificate_number}
              isEditing={true}
              onChange={handleInputChange}
              placeholder="e.g. Certificate/Transcript number"
            />

            <InputField
              label="Start Year (Optional)"
              name="education_start_year"
              type="number"
              value={formData.education_start_year}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.education_start_year}
              placeholder="Many certificates don't include start year"
            />

            <InputField
              label="End Year"
              name="education_end_year"
              type="number"
              value={formData.education_end_year}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.education_end_year}
              disabled={formData.education_is_ongoing}
            />

            <InputField
              label="Country"
              name="education_country"
              value={formData.education_country}
              isEditing={true}
              onChange={handleInputChange}
            />

            <div>
              <label className="flex items-center gap-3 mt-6">
                <input
                  type="checkbox"
                  name="education_is_ongoing"
                  checked={formData.education_is_ongoing}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Currently pursuing this qualification</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={resetForm}
              className="btn btn-outline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              {saving ? (
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : 'Update Education'}
            </button>
          </div>
        </div>
      )}

      {/* Education List */}
      <div className="space-y-3">
        {!education || education.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl">
            <GraduationCap className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No education records yet</h3>
            <p className="text-sm text-gray-600">To add education, upload certificates in Documents → Uploads</p>
          </div>
        ) : (
          education.map((edu) => (
            <div key={edu.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{edu.education_qualification_name}</h4>
                    {edu.qualification_level_display && (
                      <InfoBadge color="blue" label={edu.qualification_level_display} />
                    )}
                    <StatusBadge
                      status={getEducationStatusKey(edu.education_verification_status)}
                      label={getEducationStatusLabel(edu.education_verification_status)}
                    />
                    {edu.education_attainment_use !== 'included' && (
                      <InfoBadge color="gray" label="Not used for highest education" />
                    )}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building className="w-4 h-4" />
                      <span>{edu.institution_display_name || edu.education_institution_name || 'N/A'}</span>
                    </div>
                    {edu.education_field_of_study && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <GraduationCap className="w-4 h-4" />
                        <span>{edu.education_field_of_study}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {formatEducationYearRange(edu.education_start_year, edu.education_end_year, edu.education_is_ongoing)}
                      </span>
                    </div>
                    {edu.education_certificate_number && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield className="w-4 h-4" />
                        <span>Certificate: {edu.education_certificate_number}</span>
                      </div>
                    )}
                    {edu.education_country !== 'Singapore' && (
                      <div className="flex items-center gap-2">
                        <InfoBadge color="gray" label={edu.education_country} />
                      </div>
                    )}
                  </div>
                </div>
                {edu.education_document_path && (
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <button
                      className="icon-button focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Preview Certificate"
                      onClick={() => setDocViewer({ open: true, edu })}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      className="icon-button focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Download Certificate"
                      onClick={() => handleEduDownload(edu)}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="employee"
      />
      <SimpleDocumentViewerModal
        isOpen={docViewer.open}
        onClose={() => setDocViewer({ open: false, edu: null })}
        filePath={docViewer.edu?.education_document_path}
        title={docViewer.edu?.education_qualification_name || 'Education Certificate'}
        statusBadge={docViewer.edu && (
          <StatusBadge
            status={getEducationStatusKey(docViewer.edu.education_verification_status)}
            label={getEducationStatusLabel(docViewer.edu.education_verification_status)}
            showIcon
          />
        )}
        onDownload={() => docViewer.edu && handleEduDownload(docViewer.edu)}
        downloading={downloading}
      />
    </div>
  );
};

const WorkExperienceTab = ({ workExperience, setWorkExperience, loading, fetchTabData }) => {
  const [experienceModal, setExperienceModal] = useState({ open: false, record: null });
  const [experienceToDelete, setExperienceToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const workExperienceSummary = useMemo(() => {
    if (!workExperience?.length) {
      return { employerCount: 0, totalMonths: 0, totalDays: 0 };
    }
    const totalDays = workExperience.reduce(
      (sum, exp) => sum + (exp.duration_days || 0),
      0,
    );
    const totalMonths = workExperience.reduce(
      (sum, exp) => sum + (exp.duration_months || 0),
      0,
    );
    return { employerCount: workExperience.length, totalMonths, totalDays };
  }, [workExperience]);

  const refreshExperience = async () => {
    if (typeof fetchTabData === 'function') {
      await fetchTabData('experience');
      return;
    }
    const response = await staffAPI.getWorkExperience();
    setWorkExperience(response.data?.results || response.data || []);
  };

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      if (experienceModal.record?.id) {
        await staffAPI.updateWorkExperience(experienceModal.record.id, payload);
        toast.success('Work experience updated');
      } else {
        await staffAPI.createWorkExperience(payload);
        toast.success('Work experience added');
      }
      setExperienceModal({ open: false, record: null });
      await refreshExperience();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || data?.error || 'Failed to save work experience');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!experienceToDelete) return;
    setSaving(true);
    try {
      await staffAPI.deleteWorkExperience(experienceToDelete.id);
      toast.success('Work experience deleted');
      setExperienceToDelete(null);
      await refreshExperience();
    } catch (error) {
      toast.error('Failed to delete work experience');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">Manage your professional work history</p>
        <button
          onClick={() => setExperienceModal({ open: true, record: null })}
          className="btn btn-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Experience
        </button>
      </div>

      {workExperience?.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-blue-800" />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-xl font-bold text-gray-900 truncate">
                  {formatExperienceFromDays(workExperienceSummary.totalDays)}
                </p>
                <p className="text-sm text-gray-600">Total Work Experience</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border p-4">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <Briefcase className="w-5 h-5 text-blue-800" />
              </div>
              <div className="ml-3 min-w-0">
                <p className="text-xl font-bold text-gray-900">{workExperienceSummary.employerCount}</p>
                <p className="text-sm text-gray-600">
                  {workExperienceSummary.employerCount === 1 ? 'Employer Listed' : 'Employers Listed'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {!workExperience || workExperience.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <Briefcase className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No work experience yet</h3>
            <p className="text-sm text-gray-500">Add roles from your CV or LinkedIn so HR can see your background.</p>
            <button
              type="button"
              onClick={() => setExperienceModal({ open: true, record: null })}
              className="btn btn-primary mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <Plus className="w-4 h-4" />
              Add your first role
            </button>
          </div>
        ) : (
          workExperience.map((exp) => {
            const bullets = splitWorkExperienceDescription(exp.work_exp_job_description);
            return (
              <div key={exp.id} className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h4 className="text-sm font-semibold text-gray-900">{exp.work_exp_designation}</h4>
                      {exp.work_exp_employment_type && (
                        <InfoBadge
                          color={exp.work_exp_employment_type === 'internship' ? 'blue' : 'gray'}
                          label={getWorkExpEmploymentTypeLabel(exp.work_exp_employment_type)}
                        />
                      )}
                      {exp.work_exp_industry && (
                        <InfoBadge color="gray" label={getWorkExpIndustryLabel(exp.work_exp_industry)} />
                      )}
                      {exp.work_exp_is_current && <InfoBadge color="green" label="Current" />}
                    </div>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Building className="w-4 h-4 shrink-0" />
                        <span>{exp.work_exp_company_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4 shrink-0" />
                        <span>
                          {formatWorkExperiencePeriod(exp.work_exp_start_date, exp.work_exp_end_date, exp.work_exp_is_current)}
                        </span>
                      </div>
                      {(exp.work_exp_location || (exp.work_exp_country && exp.work_exp_country !== 'Singapore')) && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span>{[exp.work_exp_location, exp.work_exp_country].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      {bullets.length > 0 && (
                        <ul className="mt-2 space-y-1 text-gray-600 list-disc pl-5">
                          {bullets.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setExperienceModal({ open: true, record: exp })}
                      className="icon-button focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Edit"
                      aria-label="Edit work experience"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExperienceToDelete(exp)}
                      className="icon-button-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                      title="Delete"
                      aria-label="Delete work experience"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <WorkExperienceFormModal
        isOpen={experienceModal.open}
        onClose={() => setExperienceModal({ open: false, record: null })}
        record={experienceModal.record}
        existingRecords={workExperience}
        theme="employee"
        showVerification={false}
        isSubmitting={saving}
        onSubmit={handleSubmit}
      />
      <ConfirmModal
        isOpen={Boolean(experienceToDelete)}
        title="Delete Work Experience"
        message={
          experienceToDelete
            ? `Delete ${experienceToDelete.work_exp_designation || 'this role'} at ${experienceToDelete.work_exp_company_name}?`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        theme="employee"
        isLoading={saving}
        onConfirm={handleDelete}
        onCancel={() => setExperienceToDelete(null)}
      />
    </div>
  );
};

const CertificatesTab = ({ certificates, setCertificates, loading, fetchTabData }) => {
  const [certViewer, setCertViewer] = useState({ open: false, cert: null });
  const [downloading, setDownloading] = useState(false);

  const isExpired = (cert) => {
    if (cert.cert_is_lifetime || !cert.cert_expiry_date) return false;
    return new Date(cert.cert_expiry_date) < new Date();
  };

  const isExpiringSoon = (cert) => {
    if (cert.cert_is_lifetime || !cert.cert_expiry_date) return false;
    const expiryDate = new Date(cert.cert_expiry_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 90 && daysUntilExpiry > 0;
  };

  const handleCertDownload = async (cert) => {
    setDownloading(true);
    try {
      const res = await api.downloadCertificate(cert.id);
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cert.cert_document_path ? cert.cert_document_path.split('/').pop() : `certificate_${cert.id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Banner when records exist */}
      {certificates && certificates.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3 mb-4">
          <p className="text-xs text-gray-600 text-center">
            To add more certificates, upload in Documents → Uploads
          </p>
        </div>
      )}

      <div className="space-y-3">
        {!certificates || certificates.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl">
            <Award className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">No certificates yet</h3>
            <p className="text-sm text-gray-600">To add professional certificates, upload in Documents → Uploads</p>
          </div>
        ) : (
          certificates.map((cert) => (
            <div key={cert.id} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{cert.cert_name}</h4>
                    {cert.cert_verification_status?.toLowerCase() === 'verified' && (
                      <StatusBadge status="verified" />
                    )}
                    {isExpired(cert) && (
                      <StatusBadge status="expired" />
                    )}
                    {isExpiringSoon(cert) && (
                      <StatusBadge status="expiring_soon" />
                    )}
                  </div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Building className="w-4 h-4" />
                      <span>{cert.cert_issuing_organization}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Issued: {formatDate(cert.cert_issue_date)}
                        {cert.cert_expiry_date && !cert.cert_is_lifetime && 
                          ` · Expires: ${formatDate(cert.cert_expiry_date)}`
                        }
                      </span>
                    </div>
                    {cert.cert_certificate_number && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield className="w-4 h-4" />
                        <span>Certificate: {cert.cert_certificate_number}</span>
                      </div>
                    )}
                  </div>
                </div>
                {cert.cert_document_path && (
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <button
                      className="icon-button focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Preview Certificate"
                      onClick={() => setCertViewer({ open: true, cert })}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      className="icon-button focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      title="Download Certificate"
                      onClick={() => handleCertDownload(cert)}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <SimpleDocumentViewerModal
        isOpen={certViewer.open}
        onClose={() => setCertViewer({ open: false, cert: null })}
        filePath={certViewer.cert?.cert_document_path}
        title={certViewer.cert?.cert_name || 'Professional Certificate'}
        statusBadge={certViewer.cert?.cert_verification_status?.toLowerCase() === 'verified' && (
          <StatusBadge status="verified" showIcon />
        )}
        onDownload={() => certViewer.cert && handleCertDownload(certViewer.cert)}
        downloading={downloading}
      />
    </div>
  );
};

// Family & Dependents Tab Component - Extended Childcare Leave Management
const FamilyDependentsTab = ({ 
  dependents, 
  setDependents, 
  childcareEligibility, 
  setChildcareEligibility, 
  loading, 
  fetchTabData,
  readOnly = false,
  renderHeader,
  hrProfile = null
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingDependent, setEditingDependent] = useState(null);
  const [sensitiveFieldsVisible, setSensitiveFieldsVisible] = useState({});
  const [formData, setFormData] = useState({
    dependent_full_name: '',
    dependent_date_of_birth: '',
    dependent_citizenship_status: '',
    dependent_nric_fin: '',
    dependent_passport_number: '',
    dependent_birth_certificate_number: '',
    dependent_special_needs: false,
    is_financially_dependent: true,
    dependency_start_date: '',
    employee_consent_obtained: false,
    dependent_consent_obtained: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };

  // Auto-check consent when form opens if privacy policy consent exists
  useEffect(() => {
    if (isAdding && hrProfile?.hrprofile_consent_obtained) {
      setFormData(prev => ({
        ...prev,
        employee_consent_obtained: true
      }));
    }
  }, [isAdding, hrProfile?.hrprofile_consent_obtained]);

  const citizenshipStatuses = [
    { value: 'yes', label: 'Singapore Citizen' },
    { value: 'no', label: 'Non-Citizen' }
  ];


  const resetForm = () => {
    // Auto-check consent if privacy policy consent exists
    const hasPrivacyPolicyConsent = hrProfile?.hrprofile_consent_obtained === true;
    setFormData({
      dependent_full_name: '',
      dependent_date_of_birth: '',
      dependent_citizenship_status: '',
      dependent_nric_fin: '',
      dependent_passport_number: '',
      dependent_birth_certificate_number: '',
      dependent_special_needs: false,
      is_financially_dependent: true,
      dependency_start_date: '',
      adoption_certificate_number: '',
      formal_intent_to_adopt_date: '',
      employee_consent_obtained: hasPrivacyPolicyConsent, // Auto-check if privacy policy consent exists
      dependent_consent_obtained: false
    });
    setFormErrors({});
    setIsAdding(false);
    setEditingDependent(null);
  };

  // Toggle visibility of sensitive fields (dates)
  const toggleFieldVisibility = (fieldName) => {
    setSensitiveFieldsVisible(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const maskDate = (dateValue, fieldName) => {
    if (!dateValue) return 'Not provided';
    if (sensitiveFieldsVisible[fieldName]) return formatDate(dateValue);
    return maskValue(dateValue, 'date');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.dependent_full_name.trim()) {
      errors.dependent_full_name = 'Full name is required';
    }
    if (!formData.dependent_date_of_birth) {
      errors.dependent_date_of_birth = 'Date of birth is required';
    }
    if (!formData.dependent_citizenship_status) {
      errors.dependent_citizenship_status = 'Citizenship status is required';
    }
    // Consent is auto-inherited from privacy policy if employee has given it
    // Backend will handle auto-setting consent, so we allow submission if privacy policy consent exists
    const hasPrivacyPolicyConsent = hrProfile?.hrprofile_consent_obtained === true;
    if (!formData.employee_consent_obtained && !hasPrivacyPolicyConsent) {
      errors.employee_consent_obtained = 'Employee consent is required (or privacy policy consent must be obtained first)';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      if (editingDependent) {
        await api.updateEmployeeDependent(editingDependent.id, formData);
        toast.success('Dependent updated successfully!');
      } else {
        await api.createEmployeeDependent(formData);
        toast.success('Dependent added successfully!');
      }
      await fetchTabData('family');
      resetForm();
    } catch (error) {
      console.error('Error saving dependent:', error);
      toast.error('Failed to save dependent information');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (dependent) => {
    setEditingDependent(dependent);
    setFormData({
      dependent_full_name: dependent.dependent_full_name || '',
      dependent_date_of_birth: dependent.dependent_date_of_birth || '',
      dependent_citizenship_status: dependent.dependent_citizenship_status || '',
      dependent_nric_fin: dependent.dependent_nric_fin || '',
      dependent_passport_number: dependent.dependent_passport_number || '',
      dependent_birth_certificate_number: dependent.dependent_birth_certificate_number || '',
      dependent_special_needs: dependent.dependent_special_needs || false,
      is_financially_dependent: dependent.is_financially_dependent !== false,
      dependency_start_date: dependent.dependency_start_date || '',
      adoption_certificate_number: dependent.adoption_certificate_number || '',
      formal_intent_to_adopt_date: dependent.formal_intent_to_adopt_date || '',
      employee_consent_obtained: dependent.employee_consent_obtained || false,
      dependent_consent_obtained: dependent.dependent_consent_obtained || false
    });
    setIsAdding(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm({
      title: 'Delete Dependent',
      message: 'Are you sure you want to delete this dependent? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    
    try {
      await api.deleteEmployeeDependent(id);
      toast.success('Dependent deleted successfully');
      await fetchTabData('family');
    } catch (error) {
      console.error('Error deleting dependent:', error);
      toast.error('Failed to delete dependent');
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const isChildcareEligible = (dependent) => {
    const age = calculateAge(dependent.dependent_date_of_birth);
    const isChild = ['biological_child', 'adopted_child', 'step_child', 'foster_child'].includes(dependent.relationship_type);
    const isCitizen = dependent.dependent_citizenship_status === 'yes';
    return age !== null && age >= 7 && age <= 12 && isChild && isCitizen;
  };

  // Determine verification status for a dependent based on available fields
  const getDependentVerificationStatus = (dependent) => {
    let status = 'pending';
    let label = 'Pending verification';

    const raw = (dependent.verification_status || dependent.status || '').toString().toLowerCase();
    const documentVerified = dependent.document_verified === true || dependent.is_verified === true;
    if (documentVerified || raw === 'verified' || raw === 'approved' || raw === 'validated') {
      status = 'verified';
      label = 'Verified';
    } else if (raw === 'rejected' || raw === 'failed' || raw === 'invalid') {
      status = 'rejected';
      label = 'Verification failed';
    } else if (raw === 'pending' || raw === 'in_review' || raw === 'processing') {
      status = 'pending';
      label = 'Pending verification';
    } else if (dependent.dependent_birth_certificate_number) {
      status = 'verified';
      label = 'Verified';
    }

    return { status, label };
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-6 bg-gray-200 rounded w-32"></div>
                  <div className="h-5 bg-gray-200 rounded-full w-20"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-40"></div>
                  <div className="h-4 bg-gray-200 rounded w-36"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {renderHeader && renderHeader()}
      
      {/* Description */}
      <p className="text-sm text-gray-600">Manage your family members and dependents</p>

      {/* Add/Edit Form */}
      {isAdding && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="text-base font-semibold text-gray-900 mb-3">
            {editingDependent ? 'Edit Dependent' : 'Add Dependent'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <InputField
              label="Full Name"
              name="dependent_full_name"
              value={formData.dependent_full_name}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.dependent_full_name}
              required
              placeholder="Enter full name"
            />

            <InputField
              label="Date of Birth"
              name="dependent_date_of_birth"
              type="date"
              value={formData.dependent_date_of_birth}
              isEditing={true}
              onChange={handleInputChange}
              error={formErrors.dependent_date_of_birth}
              required
            />

            <SelectField
              label="Citizenship Status"
              name="dependent_citizenship_status"
              value={formData.dependent_citizenship_status}
              isEditing={true}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Select citizenship status' },
                ...citizenshipStatuses
              ]}
            />

            <InputField
              label="NRIC/FIN (Optional)"
              name="dependent_nric_fin"
              value={formData.dependent_nric_fin}
              isEditing={true}
              onChange={handleInputChange}
              placeholder="e.g., T1234567J"
            />

            <InputField
              label="Passport Number (Optional)"
              name="dependent_passport_number"
              value={formData.dependent_passport_number}
              isEditing={true}
              onChange={handleInputChange}
            />

            <InputField
              label="Birth Certificate Number (Optional)"
              name="dependent_birth_certificate_number"
              value={formData.dependent_birth_certificate_number}
              isEditing={true}
              onChange={handleInputChange}
            />

            {/* Adoption Fields - Show only for adopted children */}
            {(formData.relationship_type === 'adopted_child' || formData.adoption_certificate_number || formData.formal_intent_to_adopt_date) && (
              <>
                <InputField
                  label="Adoption Certificate Number"
                  name="adoption_certificate_number"
                  value={formData.adoption_certificate_number}
                  isEditing={true}
                  onChange={handleInputChange}
                  helpText="For paternity leave eligibility verification"
                />
                <InputField
                  label="Formal Intent to Adopt Date"
                  name="formal_intent_to_adopt_date"
                  type="date"
                  value={formData.formal_intent_to_adopt_date}
                  isEditing={true}
                  onChange={handleInputChange}
                  helpText="MOM requires 3+ months service before this date for paternity leave"
                />
              </>
            )}

            <InputField
              label="Dependency Start Date"
              name="dependency_start_date"
              type="date"
              value={formData.dependency_start_date}
              isEditing={true}
              onChange={handleInputChange}
              placeholder="Usually birth date for children"
            />

            <div className="md:col-span-3 space-y-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="dependent_special_needs"
                  checked={formData.dependent_special_needs}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Dependent has special needs
                </span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="is_financially_dependent"
                  checked={formData.is_financially_dependent}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Financially dependent on me
                </span>
              </label>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="employee_consent_obtained"
                  checked={formData.employee_consent_obtained}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  <strong className="text-red-600">*</strong> I consent to storing this dependent's personal information for leave entitlement administration and emergency contact purposes
                  {hrProfile?.hrprofile_consent_obtained && formData.employee_consent_obtained && (
                    <span className="text-xs text-green-600 ml-2">(Auto-checked via Privacy Policy consent)</span>
                  )}
                </span>
              </label>
              {formErrors.employee_consent_obtained && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.employee_consent_obtained}
                </p>
              )}

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="dependent_consent_obtained"
                  checked={formData.dependent_consent_obtained}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus-visible:ring-blue-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  Dependent's consent obtained (if old enough to provide consent)
                </span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={resetForm}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? (
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : (editingDependent ? 'Update Dependent' : 'Add Dependent')}
            </button>
          </div>
        </div>
      )}

      {/* Dependents List */}
      <div className="space-y-4">
        {!dependents || dependents.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl">
            <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No dependents added yet</h3>
            <p className="text-sm text-gray-600">Upload your child's birth certificate under Documents & Files to add dependents automatically.</p>
          </div>
        ) : (
          dependents.map((dependent) => {
            const age = calculateAge(dependent.dependent_date_of_birth);
            const isEligible = isChildcareEligible(dependent);
            const vStatus = getDependentVerificationStatus(dependent);
            const isSpouse = dependent.relationship_type === 'spouse';
            const isChild = ['biological_child', 'adopted_child', 'step_child', 'foster_child'].includes(dependent.relationship_type);
            
            return (
              <div key={dependent.id} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h4 className="text-base font-semibold text-gray-900">{dependent.dependent_full_name}</h4>
                      
                      {/* Relationship Type Badge */}
                      {isSpouse && (
                        <InfoBadge color="pink" label="Spouse" />
                      )}
                      {isChild && (
                        <InfoBadge color="blue" label="Child" />
                      )}
                      
                      {/* Childcare Eligibility Badge (only for children) */}
                      {isChild && isEligible && (
                        <InfoBadge color="green" label="Childcare Eligible" />
                      )}
                      
                      {/* Special Needs Badge (only for children) */}
                      {isChild && dependent.dependent_special_needs && (
                        <InfoBadge color="purple" label="Special Needs" />
                      )}
                    </div>
                    
                    {/* Display logic based on relationship type */}
                    <div className="space-y-2">
                      {isSpouse ? (
                        // Spouse-specific display
                        <>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Heart className="w-4 h-4 text-pink-500" />
                            <span>Married {dependent.marriage_date ? maskDate(dependent.marriage_date, `marriage_date_${dependent.id}`) : 'Date unknown'}</span>
                            {dependent.marriage_date && (
                              <SensitiveRevealButton
                                isVisible={!!sensitiveFieldsVisible[`marriage_date_${dependent.id}`]}
                                onClick={() => toggleFieldVisibility(`marriage_date_${dependent.id}`)}
                                theme="employee"
                                placement="inline"
                                titleShow="Show marriage date"
                                titleHide="Hide marriage date"
                              />
                            )}
                          </div>
                        </>
                      ) : (
                        // Child-specific display
                        <>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Calendar className="w-4 h-4 text-green-500" />
                            <span>{age !== null ? `${age} years old` : 'Age unknown'}</span>
                            {dependent.dependent_date_of_birth && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                ({maskDate(dependent.dependent_date_of_birth, `dob_${dependent.id}`)})
                                <SensitiveRevealButton
                                  isVisible={!!sensitiveFieldsVisible[`dob_${dependent.id}`]}
                                  onClick={() => toggleFieldVisibility(`dob_${dependent.id}`)}
                                  theme="employee"
                                  placement="inline"
                                  titleShow="Show date of birth"
                                  titleHide="Hide date of birth"
                                />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Globe className="w-4 h-4 text-indigo-500" />
                            <span>{citizenshipStatuses.find(c => c.value === dependent.dependent_citizenship_status)?.label || dependent.dependent_citizenship_status}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Extended Childcare Leave Eligibility Details (only for children) */}
                    {isChild && isEligible && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Heart className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Extended Childcare Leave Eligible</span>
                        </div>
                        <p className="text-xs text-green-700">
                          This child qualifies for Extended Childcare Leave (2 days per year) as they are aged 7-12 years and a Singapore citizen.
                        </p>
                      </div>
                    )}

                    
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {/* Mobile: Tick icon for verified status */}
                    {vStatus.label === 'Verified' && (
                      <CheckCircle className="w-5 h-5 text-green-600 block md:hidden" />
                    )}
                    
                    {/* Desktop: Full badge text */}
                    <span className={vStatus.label === 'Verified' ? 'hidden md:inline-flex' : ''}>
                      <StatusBadge status={vStatus.status} label={vStatus.label} />
                    </span>
                  </div>
                  {/* Read-only view: edit/delete removed to keep documents as source of truth */}
                </div>
              </div>
            );
          })
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="employee"
      />
    </div>
  );
};

// Documents Tab Component - Central Document Hub
const DocumentsTab = ({ profile, hrProfile, fetchProfileData, activeTab }) => {
  const [uploadingFiles, setUploadingFiles] = useState(new Set());
  const [ocrProcessing, setOcrProcessing] = useState({});
  const [otherDocumentUploading, setOtherDocumentUploading] = useState(false);

  const confirmCallbackRef = useRef(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const showConfirm = (config) => {
    return new Promise((resolve) => {
      confirmCallbackRef.current = resolve;
      setConfirmDialog(config);
    });
  };

  // Use nested tab persistence for sub-tabs and category filter
  const validCategories = ['medical', 'banking', 'identity', 'family', 'education', 'professional', 'other'];
  const { subTab: subTabState, category: categoryState } = useNestedTabPersistence('profile-documents', activeTab === 'documents' ? activeTab : null, {
    subTab: { 
      default: 'letters', 
      valid: ['letters', 'uploads'], 
      param: 'subTab' 
    },
    category: { 
      default: null, 
      valid: validCategories, 
      param: 'category' 
    }
  });
  const [subTab, setSubTab] = subTabState;
  const [selectedCategory, setSelectedCategory] = categoryState;
  
  // View mode state with responsive detection (matching Leave.js pattern)
  // Compute default based on screen size if no saved preference exists
  const getDefaultViewMode = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('profile-documents-view-mode');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed === 'grid' || parsed === 'list') return parsed;
        } catch (e) {
          // Invalid saved value, compute default
        }
      }
      // No saved preference - compute based on screen size
      return window.innerWidth < 768 ? 'grid' : 'list';
    }
    return 'grid'; // SSR fallback
  };
  
  const [viewMode, setViewMode] = usePersistence('profile-documents-view-mode', getDefaultViewMode());

  const getDefaultLettersViewMode = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('profile-letters-view-mode');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed === 'grid' || parsed === 'list') return parsed;
        } catch (e) {}
      }
      return window.innerWidth < 768 ? 'grid' : 'list';
    }
    return 'grid';
  };

  const [lettersViewMode, setLettersViewMode] = usePersistence('profile-letters-view-mode', getDefaultLettersViewMode());
  
  // Clear category when switching to letters tab (category only applies to uploads)
  useEffect(() => {
    if (subTab === 'letters' && selectedCategory !== null) {
      setSelectedCategory(null);
    }
  }, [subTab, selectedCategory, setSelectedCategory]);
  
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  const [documentLeaveMapping, setDocumentLeaveMapping] = useState({});
  const [letters, setLetters] = useState([]);
  const [lettersLoading, setLettersLoading] = useState(false);
  const [letterPreview, setLetterPreview] = useState({ open: false, letter: null });
  // Child Certificate Verification Modal
  const [verificationModal, setVerificationModal] = useState({ open: false, documentApprovalId: null, documentType: null, initialOcrData: {}, viewMode: 'edit' });
  const [otherDocumentViewer, setOtherDocumentViewer] = useState({ open: false, document: null });
  const [otherDocumentDownloading, setOtherDocumentDownloading] = useState(false);
  // Marriage Role Selection Modal
  const [roleSelectionModal, setRoleSelectionModal] = useState({ open: false, documentApprovalId: null, ocrData: {}, recommendedRole: null });
  const [serverDocsUnavailable, setServerDocsUnavailable] = useState(false);
  
  // Use the unified documents hook - automatically fetches and normalizes all document types
  const { documents: hookDocuments, loading: documentsLoading, error: documentsError, refetch: refetchDocuments } = useDocuments({
    enabled: subTab === 'uploads'
  });
  
  // Local documents state for optimistic updates (merges with hook documents)
  const [documents, setDocuments] = useState([]);

  const handleDownloadOtherDocument = async () => {
    const document = otherDocumentViewer.document;
    if (!document?.id) return;

    try {
      setOtherDocumentDownloading(true);
      const response = await api.downloadDocumentApproval(document.id);
      const blob = new Blob(
        [response.data],
        { type: response.headers['content-type'] || 'application/octet-stream' }
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = document.name || `document_${document.id}`;
      window.document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      anchor.remove();
    } catch (error) {
      console.error('Download failed', error);
      toast.error('Failed to download document');
    } finally {
      setOtherDocumentDownloading(false);
    }
  };
  
  // Sync hook documents to local state
  useEffect(() => {
    if (hookDocuments && hookDocuments.length >= 0) {
      setDocuments(hookDocuments);
    }
  }, [hookDocuments]);
  
  // ID Card Samples Modal
  const [idCardSamplesModal, setIdCardSamplesModal] = useState({ open: false, type: null });

  // Fetch available leave types for the current user
  const fetchAvailableLeaveTypes = async () => {
    try {
      const response = await api.getLeaveTypes();
      const userLeaveTypes = response.data.results || response.data || [];
      setAvailableLeaveTypes(userLeaveTypes);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  // Fetch my HR letters and asset acknowledgement letters; merge and sort by generated_at desc
  const fetchMyLetters = async () => {
    try {
      setLettersLoading(true);
      const hrRes = await api.getHRLetters({ ordering: '-generated_at' });
      const hrData = hrRes.data?.results || hrRes.data || [];
      const hrList = (hrData || []).map((l) => ({ ...l, source: 'hr_letter' }));
      let assetList = [];
      try {
        const assetAckRes = await api.getAssetAcknowledgementLetters({ ordering: '-generated_at' });
        const assetAckData = assetAckRes.data?.results || assetAckRes.data || [];
        assetList = (assetAckData || []).map((l) => ({
          ...l,
          source: 'asset_ack',
          letter_type_display: 'Asset Receipt Acknowledgement',
          valid_until: null,
        }));
      } catch (assetErr) {
        console.warn('Asset acknowledgement letters not available:', assetErr);
      }
      const merged = [...hrList, ...assetList].sort(
        (a, b) => new Date(b.generated_at || 0) - new Date(a.generated_at || 0)
      );
      setLetters(merged);
    } catch (e) {
      console.error('Error fetching HR letters:', e);
      toast.error('Failed to load letters. Please try again.');
      setLetters([]);
    } finally {
      setLettersLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableLeaveTypes();
    fetchMyLetters();
  }, []);

  // Handle error state for documents
  useEffect(() => {
    if (documentsError) {
      console.error('Failed to fetch documents:', documentsError);
      // Check if it's a 404 (endpoint not available)
      if (documentsError?.response?.status === 404) {
        setServerDocsUnavailable(true);
      } else {
        setServerDocsUnavailable(false);
      }
    } else {
      setServerDocsUnavailable(false);
    }
  }, [documentsError]);

  const handlePreviewLetter = (letter) => {
    if (!letter?.id) return;
    setLetterPreview({ open: true, letter });
  };

  const handleDownloadLetter = async (letterId, source = 'hr_letter') => {
    try {
      const isAssetAck = source === 'asset_ack';
      const res = isAssetAck
        ? await api.downloadAssetAcknowledgementLetter(letterId)
        : await api.downloadHRLetter(letterId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isAssetAck ? `asset_ack_${letterId}.pdf` : `hr_letter_${letterId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      console.error('Download failed', e);
      toast.error('Failed to download letter. Please try again.');
    }
  };

  const getExpiryStatus = (validUntil) => {
    const status = getLetterExpiryStatus(validUntil);
    if (!status) return null;
    const icon = status.tone === 'valid' ? CheckCircle : status.tone === 'expiring' ? AlertCircle : XCircle;
    return { ...status, icon };
  };

  // Leave type trigger mappings for Singapore MOM compliance
  const leaveDocumentTriggers = {
    'birth_certificate': {
      leaveTypes: ['Maternity Leave', 'Paternity Leave', 'Childcare Leave', 'Extended Childcare Leave', 'Shared Parental Leave', 'Unpaid Infant Care Leave'],
      message: 'Birth certificate enables family-related leave benefits!'
    },
    'adoption_papers': {
      leaveTypes: ['Adoption Leave', 'Shared Parental Leave'],
      message: 'Adoption documents enable adoption leave benefits!'
    },
    'death_certificate': {
      leaveTypes: ['Compassionate Leave'],
      message: 'Enables compassionate leave during difficult times'
    },
    'marriage_certificate': {
      leaveTypes: ['Marriage Leave', 'Paternity Leave', 'Shared Parental Leave'],
      message: 'Marriage certificate enables marriage leave benefit!'
    },
    'dependent_pass': {
      leaveTypes: [],
      message: 'Dependent pass uploaded successfully!'
    }
  };

  const getDocumentTypeIcon = (approvalType) => {
    const map = {
      'id_card_front': { icon: IdCard, color: 'text-green-600', bg: 'bg-green-100' },
      'id_card_back': { icon: IdCard, color: 'text-emerald-600', bg: 'bg-emerald-100' },
      'passport': { icon: Globe, color: 'text-blue-600', bg: 'bg-blue-100' },
      'driving_license': { icon: Car, color: 'text-indigo-600', bg: 'bg-indigo-100' },
      'birth_certificate': { icon: Baby, color: 'text-green-600', bg: 'bg-green-100' },
      'marriage_certificate': { icon: Heart, color: 'text-pink-600', bg: 'bg-pink-100' },
      'education_certificate': { icon: GraduationCap, color: 'text-purple-600', bg: 'bg-purple-100' },
      'professional_certificate': { icon: Award, color: 'text-green-600', bg: 'bg-green-100' },
      'medical_certificate': { icon: HeartPulse, color: 'text-red-600', bg: 'bg-red-100' },
      'bank_statement': { icon: Receipt, color: 'text-emerald-600', bg: 'bg-emerald-100' },
      'ns_call_up': { icon: Shield, color: 'text-amber-600', bg: 'bg-amber-100' },
      'death_certificate': { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
      'adoption_papers': { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
      'other_document': { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
    };
    return map[approvalType] || { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100' };
  };

  // Document categories with contextual information and leave triggers
  // Ordered by frequency of use: most common at top, least common at bottom
  // Ordered by how often an employee actually uploads here, most frequent first:
  //   1) Career certs — professional/safety licences renew & new ones get added often
  //   2) Education certs
  //   3) Life-event / leave-triggering family docs (birth, marriage)
  //   4) One-time onboarding banking & identity docs (NRIC, passport — rarely re-uploaded)
  //   5) Misc medical / other documents
  const documentCategories = [
    { 
      id: 'professional', 
      name: 'Professional Certificates', 
      color: 'green',
      icon: Award,
      description: 'Industry certifications, Licenses',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Safety certificates', 'Professional licenses', 'Training certificates'],
      triggerTypes: []
    },
    { 
      id: 'education', 
      name: 'Education Certificates', 
      color: 'purple',
      icon: GraduationCap,
      description: 'Degrees, Diplomas, Transcripts',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Degree certificates', 'Diploma'],
      triggerTypes: []
    },
    { 
      id: 'family', 
      name: 'Family Documents', 
      color: 'pink',
      icon: Users,
      description: 'Birth certificates, Marriage certificates, Death certificates',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Birth certificates', 'Marriage certificate', 'Death certificate'],
      triggerTypes: ['birth_certificate', 'death_certificate', 'marriage_certificate'],
      leaveAlert: '🍼 Uploading family documents may enable additional leave benefits!',
      // NEW: Multiple dropzones within this category
      subCategories: [
        {
          id: 'birth_certificate',
          name: 'Birth Certificates',
          description: 'Child birth certificates for childcare leave benefits',
          icon: Baby,
          color: 'pink',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          leaveTypes: ['Maternity Leave', 'Paternity Leave', 'Childcare Leave', 'Extended Childcare Leave', 'Shared Parental Leave', 'Unpaid Infant Care Leave'],
          message: 'Birth certificate uploaded! Processing for childcare leave eligibility...',
          workflowType: 'child_certificate'
        },
        {
          id: 'marriage_certificate', 
          name: 'Marriage Certificate',
          description: 'Marriage certificate for marriage leave benefits',
          icon: Heart,
          color: 'pink',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          leaveTypes: ['Marriage Leave', 'Paternity Leave', 'Shared Parental Leave'],
          message: 'Marriage certificate uploaded! Processing for marriage leave eligibility...',
          workflowType: 'marriage_certificate'
        },
        {
          id: 'death_certificate',
          name: 'Compassionate Leave',
          description: 'Upload the death certificate for your Compassionate Leave. You have up to 30 days; HR will verify it.',
          icon: Heart,
          color: 'gray',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          leaveTypes: ['Compassionate Leave'],
          message: 'Death certificate uploaded! It will be linked to your Compassionate Leave application.',
          workflowType: 'death_certificate'
        }
      ]
    },
    { 
      id: 'banking', 
      name: 'Banking Documents', 
      color: 'emerald',
      icon: Landmark,
      description: 'Bank statements, Account details',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Bank statements', 'Void checks', 'Account verification'],
      triggerTypes: [],
      subCategories: [
        {
          id: 'bank_statement',
          name: 'Bank Statement',
          description: 'Upload a bank statement or account verification letter for OCR processing',
          icon: Receipt,
          color: 'green',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          examples: ['Bank statement'],
          workflowType: 'bank_statement',
          required: true
        }
      ]
    },
    { 
      id: 'identity', 
      name: 'Identity Documents', 
      color: 'blue',
      icon: Fingerprint,
      description: 'Passport, NRIC, Work Permits',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Passport copy', 'NRIC/FIN', 'Work permit', 'Visa'],
      triggerTypes: [],
      // NEW: Multiple dropzones within this category
      subCategories: [
        {
          id: 'id_card_front',
          name: 'ID Card (Front)',
          description: 'Front side of NRIC/FIN',
          icon: IdCard,
          color: 'blue',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          examples: ['NRIC/FIN front'],
          workflowType: 'id_card_front',
          required: true
        },
        {
          id: 'id_card_back',
          name: 'ID Card (Back)',
          description: 'Back side of NRIC/FIN',
          icon: IdCard,
          color: 'blue',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          examples: ['NRIC/FIN back', 'Screenshot of Digital Work Pass'],
          workflowType: 'id_card_back',
          required: true
        },
        {
          id: 'passport',
          name: 'Passport',
          description: 'Passport copy for identification and travel purposes',
          icon: Globe,
          color: 'blue',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          examples: ['Passport photo page'],
          workflowType: 'passport_upload'
        },
        {
          id: 'driving_license',
          name: 'Driving Licence',
          description: 'Upload your Singapore driving licence; we\'ll extract all your licence classes for you to review.',
          icon: Car,
          color: 'teal',
          acceptedTypes: '.pdf,.jpg,.jpeg,.png',
          examples: ['Driving licence (front)', 'Driving licence (back)'],
          workflowType: 'driving_license'
        }
      ]
    },
    { 
      id: 'medical', 
      name: 'Medical Documents', 
      color: 'red',
      icon: HeartPulse,
      description: 'Health checkup reports, Vaccination records',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Health checkup reports', 'Vaccination records'],
      triggerTypes: [],
      leaveAlert: null
    },
    { 
      id: 'other', 
      name: 'Other Documents', 
      color: 'gray',
      icon: FileText,
      description: 'Reference letters and supporting files kept securely on record',
      acceptedTypes: '.pdf,.jpg,.jpeg,.png',
      examples: ['Reference letters', 'Other supporting documents'],
      triggerTypes: []
    }
  ];

  const filteredCategories = selectedCategory === null 
    ? documentCategories 
    : documentCategories.filter(cat => cat.id === selectedCategory);

  // Intelligent document type detection based on filename
  const detectDocumentType = (filename, categoryId) => {
    const name = filename.toLowerCase();
    const category = documentCategories.find(cat => cat.id === categoryId);
    
    if (!category) {
      return null;
    }

    // Family document detection
    if (categoryId === 'family') {
      if (name.includes('marriage') || name.includes('wed')) {
        return 'marriage_certificate';
      }
      if (name.includes('birth')) {
        return 'birth_certificate';
      }
      if (name.includes('adoption') || name.includes('adopt')) {
        return 'adoption_papers';
      }
      if (name.includes('death') || name.includes('decease')) {
        return 'death_certificate';
      }
      if (name.includes('dependent') && name.includes('pass')) {
        return 'dependent_pass';
      }
    }

    // Identity document detection
    if (categoryId === 'identity') {
      if (name.includes('passport')) {
        return 'passport';
      }
      if (name.includes('driving') || name.includes('licence') || name.includes('license')) {
        return 'driving_license';
      }
      if (name.includes('front') || name.includes('nric') || name.includes('fin') || name.includes('work permit')) {
        return 'id_card_front';
      }
      if (name.includes('back') || name.includes('reverse')) {
        return 'id_card_back';
      }
      // Default to front if it's clearly an ID document
      if (name.includes('id') || name.includes('card')) {
        return 'id_card_front';
      }
    }

    // Education document detection
    if (categoryId === 'education' || categoryId === 'qualification') {
      // Check for secure filename pattern: edu_cert_*
      if (name.startsWith('edu_cert_') || name.includes('education_cert')) {
        return 'education_certificate';
      }
      if (name.includes('degree') || name.includes('diploma') || name.includes('transcript') || 
          name.includes('certificate') || name.includes('academic')) {
        return 'education_certificate';
      }
    }

    // Professional document detection
    if (categoryId === 'professional' || categoryId === 'qualification') {
      // Check for secure filename pattern: prof_cert_*
      if (name.startsWith('prof_cert_') || name.includes('professional_cert')) {
        return 'professional_certificate';
      }
      if (name.includes('card') || name.includes('license card') || name.includes('id card')) {
        return 'professional_card';
      }
      if (name.includes('certificate') || name.includes('license') || name.includes('training') || 
          name.includes('safety') || name.includes('professional')) {
        return 'professional_certificate';
      }
    }

    // Medical document detection
    if (categoryId === 'medical') {
      // Check for secure filename pattern: mc_* (medical certificate)
      if (name.startsWith('mc_') || name.includes('medical_cert')) {
        return 'medical_certificate';
      }
      if (name.includes('medical') || name.includes('mc') || name.includes('certificate')) {
        return 'medical_certificate';
      }
    }

    return null;
  };

  const resolveVerificationDocumentType = (doc) => {
    const approvalType = doc?.approvalType;
    if (
      approvalType === 'birth_certificate' || approvalType === 'marriage_certificate' ||
      approvalType === 'death_certificate' ||
      approvalType === 'id_card_front' || approvalType === 'id_card_back' ||
      approvalType === 'education_certificate' || approvalType === 'professional_certificate' ||
      approvalType === 'medical_certificate' || approvalType === 'bank_statement' ||
      approvalType === 'passport' || approvalType === 'professional_card' ||
      approvalType === 'driving_license'
    ) {
      return approvalType;
    }
    const detectedByName = detectDocumentType(doc.name, doc.category);
    if (detectedByName) {
      return detectedByName;
    }
    if (doc.category === 'family') {
      const ocrKeys = Object.keys(doc.ocrData || {}).map(k => k.toLowerCase());
      const marriageKeys = ['bridegroom_full_name', 'bride_full_name', 'marriage_date', 'marriage_certificate_number'];
      const birthKeys = ['child_full_name', 'child_date_of_birth', 'birth_certificate_number', 'mother_full_name', 'father_full_name'];
      if (marriageKeys.some(k => ocrKeys.includes(k))) return 'marriage_certificate';
      if (birthKeys.some(k => ocrKeys.includes(k))) return 'birth_certificate';
    }
    return null;
  };

  // Get enabled leave types from uploaded documents, filtered by employment classification
  const getEnabledLeaveTypes = () => {
    const enabledTypes = new Set();
    
    // Get list of available leave type names for this user (already filtered by backend)
    const availableLeaveTypeNames = availableLeaveTypes.map(lt => lt.leave_type_name);
    
    documents.forEach(doc => {
      const docType = detectDocumentType(doc.name, doc.category);
      if (docType && leaveDocumentTriggers[docType]) {
        leaveDocumentTriggers[docType].leaveTypes.forEach(type => {
          // Only include leave types that are actually available to this user
          // (backend already filters by employment classification)
          if (availableLeaveTypeNames.includes(type)) {
            enabledTypes.add(type);
          }
        });
      }
    });
    
    return Array.from(enabledTypes);
  };

  // Helper function to determine if a leave type should be shown for the employee's classification
  const shouldShowLeaveType = (leaveType, employmentClassification) => {
    // For now, since we don't have access to the LeaveType model's employment_classification field 
    // in the frontend, we'll implement a basic filter based on leave type naming conventions
    
    // If classification is not set, default to allowing all leave types
    if (!employmentClassification) {
      return true;
    }

    // Check for classification-specific leave type names
    const leaveTypeLower = leaveType.toLowerCase();
    
    // If the leave type explicitly mentions a classification, filter accordingly
    if (leaveTypeLower.includes('staff') && employmentClassification !== 'staff') {
      return false;
    }
    
    if (leaveTypeLower.includes('worker') && employmentClassification !== 'worker') {
      return false;
    }
    
    if (leaveTypeLower.includes('executive') && employmentClassification !== 'executive_director') {
      return false;
    }
    
    // For common leave types that don't specify classification, show to all
    const universalLeaveTypes = [
      'Annual Leave',
      'Sick Leave', 
      'Medical Leave',
      'Hospitalisation Leave',
      'Maternity Leave',
      'Paternity Leave',
      'Childcare Leave',
      'Extended Childcare Leave',
      'Shared Parental Leave',
      'Unpaid Infant Care Leave',
      'Adoption Leave',
      'Compassionate Leave',
      'Marriage Leave'
    ];
    
    // If it's a universal leave type, allow for all classifications
    if (universalLeaveTypes.includes(leaveType)) {
      return true;
    }
    
    // Default to showing the leave type if no specific filtering rules apply
    return true;
  };

  const handleFileUpload = async (
    files,
    categoryId,
    workflowType = null,
    uploadOptions = {},
  ) => {
    const fileArray = Array.from(files);
    
    // Define allowed MIME types for different document categories
    const getAllowedMimeTypes = (categoryId) => {
      const commonTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      
      // All categories support the same file types for now
      // Can be customized per category if needed in the future
      return commonTypes;
    };

    // Helper function to get user-friendly file type names
    const getFileTypeDisplayName = (mimeType) => {
      const typeMap = {
        'application/pdf': 'PDF',
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPG', 
        'image/png': 'PNG'
      };
      return typeMap[mimeType] || mimeType;
    };
    
    for (const file of fileArray) {
      // File size validation
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }

      // MIME type validation
      const allowedMimeTypes = getAllowedMimeTypes(categoryId);
      console.log(`🔍 File validation - Name: ${file.name}, Type: ${file.type}, Size: ${file.size} bytes`);
      
      if (!allowedMimeTypes.includes(file.type)) {
        const allowedTypesDisplay = allowedMimeTypes.map(getFileTypeDisplayName).join(', ');
        toast.error(
          `❌ Invalid file type: ${file.name}\n` +
          `Detected: ${file.type || 'Unknown'}\n` +
          `Allowed: ${allowedTypesDisplay}`,
          {
            duration: 5000,
            style: {
              fontSize: '14px',
              padding: '12px 16px',
              maxWidth: '400px',
              whiteSpace: 'pre-line'
            }
          }
        );
        continue;
      }

      const fileId = Date.now() + Math.random();
      setUploadingFiles(prev => new Set(prev).add(fileId));

      try {
        // Determine document type - use workflowType if provided (from sub-categories), otherwise detect
        let docType = workflowType;
        if (!docType) {
          docType = detectDocumentType(file.name, categoryId);
        }
        
        // For identity documents, set the sub-category based on workflowType
        let subCategory = null;
        if (categoryId === 'identity' && workflowType) {
          subCategory = workflowType.replace('_upload', '').replace('_', ' ');
        }
        
        const isChildCertificate = workflowType === 'child_certificate' || (docType === 'birth_certificate' && categoryId === 'family');
        const isMarriageCertificate = workflowType === 'marriage_certificate' || (docType === 'marriage_certificate' && categoryId === 'family');
        const isIdCard = workflowType === 'id_card_front' || workflowType === 'id_card_back';
        const isEducationCertificate = categoryId === 'education' || docType === 'education_certificate';
        const isProfessionalCertificate = categoryId === 'professional' || docType === 'professional_certificate';
        const isBankStatement = workflowType === 'bank_statement' || (categoryId === 'banking' && docType === 'bank_statement');
        const isPassport = workflowType === 'passport_upload' || (categoryId === 'identity' && docType === 'passport');
        const isDrivingLicense = workflowType === 'driving_license';
        const isDeathCertificate = workflowType === 'death_certificate';
        const isOtherDocument = categoryId === 'other';

        if (isOtherDocument) {
          setOtherDocumentUploading(true);
          try {
            const response = await api.uploadOtherDocument(file);
            if (response.data.success) {
              toast.success(`${file.name} saved securely`);
              await refetchDocuments();
            }
          } finally {
            setOtherDocumentUploading(false);
          }
        } else if (isDrivingLicense) {
          // Employee self-uploads their driving licence. Upload is the HR
          // handoff: the file appears on Pending Verification immediately.
          // The modal is for confirming OCR classes, not for creating the
          // queue item.
          const response = await api.uploadDrivingLicence(file);
          if (response.data.success) {
            const docApprovalId = response.data.document_approval_id;
            const staffId = response.data.staff_id;
            toast.success('Driving licence uploaded. Review the extracted classes — HR can already see this in their queue.', { duration: 4000 });
            setOcrProcessing(prev => ({ ...prev, driving_license: 'Analysing your driving licence...' }));
            try {
              await api.sendDrivingLicenceToN8N(docApprovalId, file, staffId);
            } catch (ocrErr) {
              console.error('Driving licence OCR failed (non-fatal):', ocrErr);
            }
            setOcrProcessing(prev => { const next = { ...prev }; delete next.driving_license; return next; });
            setVerificationModal({
              open: true,
              documentApprovalId: docApprovalId,
              documentType: 'driving_license',
              initialOcrData: {},
              viewMode: 'edit',
            });
            refetchDocuments();
          }
        } else if (isDeathCertificate) {
          // Employee self-uploads the death certificate for Compassionate Leave
          // (anytime within the 30-day window). The backend auto-links it to their
          // pending compassionate leave application; HR verifies it later.
          const response = await api.uploadDeathCertificate(file);
          if (response.data.success) {
            const docApprovalId = response.data.document_approval_id;
            const staffId = response.data.staff_id;
            const linkedAppId = response.data.leave_application_id;
            toast.success('✅ Death certificate uploaded', { duration: 3000 });
            setOcrProcessing(prev => ({ ...prev, death_certificate: 'Processing your document...' }));
            try {
              await api.sendDeathCertToN8N(docApprovalId, file, staffId);
            } catch (ocrErr) {
              console.error('Death certificate OCR failed (non-fatal):', ocrErr);
            }
            setOcrProcessing(prev => { const next = { ...prev }; delete next.death_certificate; return next; });
            if (linkedAppId) {
              toast.success(`Linked to your Compassionate Leave application #${linkedAppId}. HR will verify it.`, { duration: 6000 });
            } else {
              toast.warning('Uploaded. If you haven\'t applied for Compassionate Leave yet, please do so and this certificate will be linked automatically.', { icon: '\u26a0\ufe0f', duration: 8000 });
            }
            refetchDocuments();
          }
        } else if (isChildCertificate) {
          // Handle child certificate upload with n8n integration
          const formData = new FormData();
          formData.append('file', file);
          formData.append('child_name', ''); // Will be filled by OCR
          formData.append('notes', 'Birth certificate uploaded for childcare leave eligibility');
          if (uploadOptions.confirmAdditionalChild) {
            formData.append('confirm_additional_child', 'true');
          }
          
          const response = await api.post('/child-certificate/upload/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          if (response.data.success) {
            // Stage 1: Upload confirmation
            toast.success(
              '✅ Child Certificate uploaded successfully',
              { duration: 3000 }
            );

            // Stage 2: Inline processing indicator
            setOcrProcessing(prev => ({ ...prev, child_certificate: 'Analysing your birth certificate...' }));
            
            // Add to documents list with special status
            const newDocument = {
              id: response.data.document_approval_id,
              name: file.name,
              category: categoryId,
              subCategory: subCategory,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
              status: 'processing_ocr',
              documentApprovalId: response.data.document_approval_id,
              isChildCertificate: true
            };
            
            setDocuments(prev => [...prev, newDocument]);
            
            // Send to n8n for OCR processing and handle immediate response
            try {
              const n8nResp = await api.sendChildCertToN8N(
                response.data.document_approval_id,
                file,
                response.data.staff_id
              );
              console.log('✅ Child certificate sent to n8n for OCR processing');

              // Normalize n8n response: handle array root and custom key names
              const rawPayload = n8nResp?.data;
              const firstItem = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
              const n8nPayload = (firstItem && firstItem.data) ? firstItem.data : firstItem || {};
              const extracted =
                n8nPayload.ocr_data ||
                n8nPayload.extracted_data ||
                n8nPayload.birth_certificate_data ||
                (n8nPayload.data ? n8nPayload.data.birth_certificate_data : null) ||
                null;
              console.log('[Birth OCR] n8n payload keys:', n8nPayload ? Object.keys(n8nPayload) : 'none');
              console.log('[Birth OCR] extracted keys:', extracted ? Object.keys(extracted) : 'none');

              if (extracted && Object.keys(extracted).length > 0) {
                // Forward OCR to backend webhook to persist in confidential DB
                try {
                  const payloadToBackend = {
                    child_full_name: extracted.child_full_name || extracted.child_name || '',
                    child_date_of_birth: extracted.child_date_of_birth || extracted.date_of_birth || '',
                    mother_full_name: extracted.mother_full_name || extracted.mother_name || '',
                    father_full_name: extracted.father_full_name || extracted.father_name || '',
                    birth_certificate_number: extracted.birth_certificate_number || extracted.certificate_number || '',
                    child_citizenship_status: extracted.child_citizenship_status || extracted.citizenship || '',
                    child_place_of_birth: extracted.child_place_of_birth || extracted.place_of_birth || '',
                    confidence_score: extracted.confidence_score || extracted.confidence || 95.0
                  };
                  console.log('[Birth OCR] Persisting via webhook with:', payloadToBackend);
                  await api.post('/birth-certificate/ocr-webhook/', {
                    document_approval_id: response.data.document_approval_id,
                    ocr_data: payloadToBackend
                  });
                  console.log('✅ OCR data persisted via webhook');
                  
                  setOcrProcessing(prev => { const next = { ...prev }; delete next.child_certificate; return next; });
                } catch (persistErr) {
                  console.error('⚠️ Failed to persist OCR via webhook:', persistErr);
                  setOcrProcessing(prev => { const next = { ...prev }; delete next.child_certificate; return next; });
                }

                // Open verification modal with initial OCR data for immediate display
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'birth_certificate',
                  initialOcrData: {
                    child_full_name: extracted.child_full_name || extracted.child_name || '',
                    child_date_of_birth: extracted.child_date_of_birth || extracted.date_of_birth || '',
                    mother_full_name: extracted.mother_full_name || extracted.mother_name || '',
                    father_full_name: extracted.father_full_name || extracted.father_name || '',
                    birth_certificate_number: extracted.birth_certificate_number || extracted.certificate_number || '',
                    child_citizenship_status: extracted.child_citizenship_status || extracted.citizenship || '',
                    child_place_of_birth: extracted.child_place_of_birth || extracted.place_of_birth || ''
                  },
                  viewMode: 'edit'
                });
              } else {
                setOcrProcessing(prev => { const next = { ...prev }; delete next.child_certificate; return next; });
                toast.error('Could not read your certificate. Please enter details manually.');
                
                // Fallback: open modal and let it fetch from backend
                setTimeout(() => {
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: 'birth_certificate',
                    initialOcrData: {},
                    viewMode: 'edit'
                  });
                }, 3000);
              }
            } catch (n8nError) {
              console.error('❌ Failed to send child certificate to n8n:', n8nError);
              console.error('n8n Error details:', n8nError.response?.data || n8nError.message);
              setOcrProcessing(prev => { const next = { ...prev }; delete next.child_certificate; return next; });
              toast.error('Could not read your certificate. Please enter details manually.');
              // Still open modal to allow manual entry
              setTimeout(() => {
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'birth_certificate',
                  initialOcrData: {},
                  viewMode: 'edit'
                });
              }, 3000);
            }
          }
        } else if (isMarriageCertificate) {
          // Handle marriage certificate upload with n8n integration
          const formData = new FormData();
          formData.append('file', file);
          formData.append('spouse_name', ''); // Will be filled by OCR
          formData.append('marriage_date', ''); // Will be filled by OCR
          formData.append('notes', 'Marriage certificate uploaded for marriage leave eligibility');
          
          const response = await api.post('/marriage-certificate/upload/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          if (response.data.success) {
            // Stage 1: Upload confirmation
            toast.success(
              '✅ Marriage Certificate uploaded successfully',
              { duration: 3000 }
            );

            // Stage 2: Inline processing indicator
            setOcrProcessing(prev => ({ ...prev, marriage_certificate: 'Analysing your marriage certificate...' }));
            
            // Add to documents list with special status
            const newDocument = {
              id: response.data.document_approval_id,
              name: file.name,
              category: categoryId,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
              status: 'processing_ocr',
              documentApprovalId: response.data.document_approval_id,
              isMarriageCertificate: true,
              ocrData: {} // Will be populated with OCR data
            };
            
            setDocuments(prev => [...prev, newDocument]);
            
            // Send to n8n for OCR processing and handle immediate response
            try {
              const n8nResp = await api.sendMarriageCertToN8N(
                response.data.document_approval_id,
                file,
                response.data.staff_id
              );
              console.log('✅ Marriage certificate sent to n8n for OCR processing');

              // Normalize n8n response: handle array root and custom key names
              const rawPayload = n8nResp?.data;
              console.log('[Marriage OCR] Raw n8n response:', rawPayload);
              
              const firstItem = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
              console.log('[Marriage OCR] First item:', firstItem);
              
              const n8nPayload = (firstItem && firstItem.data) ? firstItem.data : firstItem || {};
              console.log('[Marriage OCR] n8n payload:', n8nPayload);
              
              // Try multiple extraction paths based on the actual response structure
              let extracted = null;
              
              // Path 1: Direct marriage_data (from your console logs)
              if (n8nPayload.marriage_data && Object.keys(n8nPayload.marriage_data).length > 0) {
                extracted = n8nPayload.marriage_data;
                console.log('[Marriage OCR] Found data in marriage_data path');
              }
              // Path 2: Traditional OCR paths
              else if (n8nPayload.ocr_data && Object.keys(n8nPayload.ocr_data).length > 0) {
                extracted = n8nPayload.ocr_data;
                console.log('[Marriage OCR] Found data in ocr_data path');
              }
              else if (n8nPayload.extracted_data && Object.keys(n8nPayload.extracted_data).length > 0) {
                extracted = n8nPayload.extracted_data;
                console.log('[Marriage OCR] Found data in extracted_data path');
              }
              else if (n8nPayload.marriage_certificate_data && Object.keys(n8nPayload.marriage_certificate_data).length > 0) {
                extracted = n8nPayload.marriage_certificate_data;
                console.log('[Marriage OCR] Found data in marriage_certificate_data path');
              }
              else if (n8nPayload.data && n8nPayload.data.marriage_certificate_data && Object.keys(n8nPayload.data.marriage_certificate_data).length > 0) {
                extracted = n8nPayload.data.marriage_certificate_data;
                console.log('[Marriage OCR] Found data in data.marriage_certificate_data path');
              }
              
              console.log('[Marriage OCR] n8n payload keys:', n8nPayload ? Object.keys(n8nPayload) : 'none');
              console.log('[Marriage OCR] extracted keys:', extracted ? Object.keys(extracted) : 'none');
              console.log('[Marriage OCR] extracted data:', extracted);

              if (extracted && Object.keys(extracted).length > 0) {
                // Store OCR data in the document for future modal access
                const ocrData = {
                  bridegroom_full_name: extracted.bridegroom_full_name || extracted.groom_name || '',
                  bride_full_name: extracted.bride_full_name || extracted.bride_name || '',
                  marriage_date: extracted.marriage_date || extracted.wedding_date || '',
                  marriage_certificate_number: extracted.marriage_certificate_number || extracted.certificate_number || '',
                  marriage_venue: extracted.marriage_venue || extracted.venue || '',
                  witness_1_name: extracted.witness_1_name || extracted.witness1_name || '',
                  witness_2_name: extracted.witness_2_name || extracted.witness2_name || ''
                };
                
                // Update the document in the list with OCR data
                // Keep status as 'processing_ocr' until employee submits after review
                setDocuments(prev => prev.map(doc => 
                  doc.id === response.data.document_approval_id 
                    ? { ...doc, ocrData, status: 'processing_ocr' }
                    : doc
                ));
                
                // Background role detection
                const recommendedRole = getRecommendedMarriageRole(ocrData);
                console.log('[Marriage OCR] Background role detection:', { recommendedRole, ocrData });
                
                setOcrProcessing(prev => { const next = { ...prev }; delete next.marriage_certificate; return next; });

                if (recommendedRole) {
                  // Auto-process with detected role
                  console.log(`[Marriage OCR] Auto-detected role: ${recommendedRole}`);
                  await processMarriageCertificateWithRole(response.data.document_approval_id, profile.staff_id, extracted, recommendedRole);
                  
                  // Open verification modal for review with detected role
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: 'marriage_certificate',
                    initialOcrData: ocrData,
                    detectedRole: recommendedRole,
                    viewMode: 'edit'
                  });
                } else {
                  // Show role selection modal
                  console.log('[Marriage OCR] No role detected, showing role selection modal');
                  setRoleSelectionModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    ocrData: ocrData,
                    recommendedRole: null
                  });
                }
              } else {
                setOcrProcessing(prev => { const next = { ...prev }; delete next.marriage_certificate; return next; });
                toast.error('Could not read your certificate. Please enter details manually.');
                
                // Fallback: open modal and let it fetch from backend
                setTimeout(() => {
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: 'marriage_certificate',
                    initialOcrData: {},
                    viewMode: 'edit'
                  });
                }, 3000);
              }
            } catch (n8nError) {
              console.error('❌ Failed to send marriage certificate to n8n:', n8nError);
              console.error('n8n Error details:', n8nError.response?.data || n8nError.message);
              setOcrProcessing(prev => { const next = { ...prev }; delete next.marriage_certificate; return next; });
              toast.error('Could not read your certificate. Please enter details manually.');
              // Still open modal to allow manual entry
              setTimeout(() => {
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'marriage_certificate',
                  initialOcrData: {},
                  viewMode: 'edit'
                });
              }, 3000);
            }
          }
        } else if (isIdCard) {
          // Handle ID card upload with n8n integration
          console.log('🆔 ID Card upload detected - workflowType:', workflowType);
          const cardSide = workflowType === 'id_card_front' ? 'front' : 'back';
          console.log('🆔 Card side determined:', cardSide);
          
          const formData = new FormData();
          formData.append('file', file);
          formData.append('card_side', cardSide);
          
          console.log('🆔 Calling /id-card/upload/ endpoint...');
          const response = await api.post('/id-card/upload/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          if (response.data.success) {
            // Stage 1: Upload confirmation (immediate)
            toast.success(
              `✅ ID Card (${cardSide === 'front' ? 'Front' : 'Back'}) uploaded successfully`,
              { duration: 3000 }
            );

            // Stage 2: Inline processing indicator
            setOcrProcessing(prev => ({ ...prev, [workflowType]: 'Analysing your ID card...' }));
            
            // Add to documents list with special status
            const newDocument = {
              id: response.data.document_approval_id,
              name: file.name,
              category: categoryId,
              subCategory: subCategory,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
              status: 'processing_ocr',
              documentApprovalId: response.data.document_approval_id,
              isIdCard: true,
              cardSide: cardSide
            };
            
            setDocuments(prev => [...prev, newDocument]);
            
            // Send to n8n for OCR processing and handle immediate response
            try {
              console.log('🆔 Sending to n8n webhook...');
              const n8nResp = await api.sendIdCardToN8N(
                response.data.document_approval_id,
                file,
                response.data.staff_id,
                cardSide
              );
              console.log(`✅ ID Card (${cardSide}) sent to n8n for OCR processing`);

              // Normalize n8n response: handle array root and custom key names
              const rawPayload = n8nResp?.data;
              const firstItem = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
              const n8nPayload = (firstItem && firstItem.data) ? firstItem.data : firstItem || {};
              
              // Extract OCR data from various possible response structures
              let extracted =
                n8nPayload.ocr_data ||
                n8nPayload.extracted_data ||
                n8nPayload.id_card_data ||
                (n8nPayload.data ? n8nPayload.data.id_card_data : null) ||
                null;
              
              // If n8n returns OCR data directly at root level (keys start with 'ocr_')
              if (!extracted && n8nPayload && Object.keys(n8nPayload).some(key => key.startsWith('ocr_'))) {
                extracted = n8nPayload;
              }
              
              console.log('[ID Card OCR] n8n payload keys:', n8nPayload ? Object.keys(n8nPayload) : 'none');
              console.log('[ID Card OCR] extracted keys:', extracted ? Object.keys(extracted) : 'none');
              console.log('[ID Card OCR] extracted data:', extracted);

              if (extracted && Object.keys(extracted).length > 0) {
                // Map OCR fields: handle both 'ocr_*' prefixed keys and non-prefixed keys
                const mapOcrField = (extracted, ocrKey, altKey, altKey2 = null) => {
                  // Try ocr_ prefixed key first (from n8n)
                  if (extracted[ocrKey] !== undefined && extracted[ocrKey] !== null) {
                    return extracted[ocrKey];
                  }
                  // Try alternative keys without ocr_ prefix
                  if (altKey && extracted[altKey] !== undefined && extracted[altKey] !== null) {
                    return extracted[altKey];
                  }
                  // Try second alternative key
                  if (altKey2 && extracted[altKey2] !== undefined && extracted[altKey2] !== null) {
                    return extracted[altKey2];
                  }
                  return '';
                };

                const payloadToBackend = {
                  // Existing fields
                  id_card_name: mapOcrField(extracted, 'ocr_id_card_name', 'id_card_name', 'name'),
                  id_card_nric_fin_number: mapOcrField(extracted, 'ocr_id_card_nric_fin_number', 'id_card_nric_fin_number', 'nric_fin_number') || mapOcrField(extracted, 'ocr_id_card_nric_fin_number', 'nric', ''),
                  id_card_wp_number: mapOcrField(extracted, 'ocr_id_card_wp_number', 'id_card_wp_number', 'wp_number'),
                  id_card_type: mapOcrField(extracted, 'ocr_id_card_type', 'id_card_type', 'type'),
                  id_card_issue_date: mapOcrField(extracted, 'ocr_id_card_issue_date', 'id_card_issue_date', 'issue_date'),
                  id_card_nationality: mapOcrField(extracted, 'ocr_id_card_nationality', 'id_card_nationality', 'nationality'),
                  // High priority fields
                  id_card_race: mapOcrField(extracted, 'ocr_id_card_race', 'id_card_race', 'race'),
                  id_card_dob: mapOcrField(extracted, 'ocr_id_card_dob', 'id_card_dob', 'dob'),
                  id_card_sex: mapOcrField(extracted, 'ocr_id_card_sex', 'id_card_sex', 'sex'),
                  occupation: mapOcrField(extracted, 'ocr_occupation', 'occupation', ''),
                  date_of_application: mapOcrField(extracted, 'ocr_id_card_application_date', 'date_of_application', 'ocr_date_of_application'),
                  date_of_expiry: mapOcrField(extracted, 'ocr_id_card_expiry_date', 'date_of_expiry', 'ocr_date_of_expiry'),
                  // Metadata fields
                  id_card_color: mapOcrField(extracted, 'ocr_id_card_color', 'id_card_color', 'color'),
                  employer_name: mapOcrField(extracted, 'ocr_employer_name', 'employer_name', ''),
                  sector: mapOcrField(extracted, 'ocr_sector', 'sector', ''),
                  card_serial_number: mapOcrField(extracted, 'ocr_card_serial_number', 'card_serial_number', 'serial_number'),
                  // Address fields (back card only)
                  id_card_address: mapOcrField(extracted, 'ocr_id_card_address', 'id_card_address', 'address'),
                  id_card_postal_code: mapOcrField(extracted, 'ocr_id_card_postal_code', 'id_card_postal_code', 'postal_code'),
                  id_card_residential_unit_number: mapOcrField(extracted, 'ocr_id_card_residential_unit_number', 'id_card_residential_unit_number', 'residential_unit_number'),
                  confidence_score: extracted.confidence_score || extracted.confidence || extracted.ocr_confidence_score || 95.0
                };

                // Forward OCR to backend webhook to persist in confidential DB
                try {
                  console.log('[ID Card OCR] Persisting via webhook with:', payloadToBackend);
                  await api.post('/id-card/ocr-webhook/', {
                    document_approval_id: response.data.document_approval_id,
                    card_side: cardSide,
                    ocr_data: payloadToBackend
                  });
                  console.log('✅ ID Card OCR data persisted via webhook');
                  
                  setOcrProcessing(prev => { const next = { ...prev }; delete next[workflowType]; return next; });
                  
                  setDocuments(prev => prev.map(doc => 
                    doc.id === response.data.document_approval_id 
                      ? { ...doc, status: 'processing_ocr', ocrData: payloadToBackend }
                      : doc
                  ));
                  
                  // Open verification modal for employee review
                  setTimeout(() => {
                    setVerificationModal({
                      open: true,
                      documentApprovalId: response.data.document_approval_id,
                      documentType: cardSide === 'front' ? 'id_card_front' : 'id_card_back',
                      initialOcrData: payloadToBackend,
                      viewMode: 'edit'
                    });
                  }, 1000);
                } catch (persistErr) {
                  console.error('⚠️ Failed to persist ID Card OCR via webhook:', persistErr);
                  setOcrProcessing(prev => { const next = { ...prev }; delete next[workflowType]; return next; });
                  toast.warning('Could not save extracted details. Please enter manually.');
                  setTimeout(() => {
                    setVerificationModal({
                      open: true,
                      documentApprovalId: response.data.document_approval_id,
                      documentType: cardSide === 'front' ? 'id_card_front' : 'id_card_back',
                      initialOcrData: payloadToBackend,
                      viewMode: 'edit'
                    });
                  }, 1000);
                }
              } else {
                console.warn('[ID Card OCR] No OCR data extracted from n8n response');
                setOcrProcessing(prev => { const next = { ...prev }; delete next[workflowType]; return next; });
                toast.error('Could not read your ID card. Please enter details manually.');
                setTimeout(() => {
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: cardSide === 'front' ? 'id_card_front' : 'id_card_back',
                    initialOcrData: {},
                    viewMode: 'edit'
                  });
                }, 1000);
              }
            } catch (n8nError) {
              console.error('❌ Failed to send ID card to n8n:', n8nError);
              console.error('n8n Error details:', n8nError.response?.data || n8nError.message);
              setOcrProcessing(prev => { const next = { ...prev }; delete next[workflowType]; return next; });
              toast.error('Could not read your ID card. Please enter details manually.');
              setTimeout(() => {
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: cardSide === 'front' ? 'id_card_front' : 'id_card_back',
                  initialOcrData: {},
                  viewMode: 'edit'
                });
              }, 1000);
            }
          }
        } else if (isEducationCertificate || isProfessionalCertificate) {
          // Handle qualification certificate upload (education or professional) with n8n integration
          const documentType = isEducationCertificate ? 'education_certificate' : 'professional_certificate';
          const documentTypeDisplay = isEducationCertificate ? 'Education Certificate' : 'Professional Certificate';
          
          console.log(`📜 ${documentTypeDisplay} upload detected - categoryId: ${categoryId}, docType: ${docType}`);
          
          // Upload to backend
          const uploadEndpoint = isEducationCertificate 
            ? '/education-certificate/upload/' 
            : '/professional-certificate/upload/';
          
          const formData = new FormData();
          formData.append('file', file);
          
          console.log(`📜 Calling ${uploadEndpoint}...`);
          const response = await api.post(uploadEndpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          if (response.data.success) {
            // Stage 1: Upload confirmation
            toast.success(
              `✅ ${documentTypeDisplay} uploaded successfully`,
              { duration: 3000 }
            );

            // Stage 2: Inline processing indicator
            const ocrKey = documentType;
            setOcrProcessing(prev => ({ ...prev, [ocrKey]: 'Analysing your certificate...' }));
            
            // Add to documents list with special status
            const newDocument = {
              id: response.data.document_approval_id,
              name: file.name,
              category: categoryId,
              subCategory: subCategory,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
              status: 'processing_ocr',
              documentApprovalId: response.data.document_approval_id,
              documentType: documentType,
              isQualification: true
            };
            
            setDocuments(prev => [...prev, newDocument]);
            
            // Send to n8n for OCR processing via backend proxy (avoids CORS)
            try {
              console.log(`📜 Sending ${documentType} to n8n webhook via backend proxy...`);
              
              // Use backend proxy endpoint to avoid CORS issues
              const formData = new FormData();
              formData.append('file', file);
              formData.append('document_approval_id', response.data.document_approval_id);
              formData.append('document_type', documentType);
              
              const n8nResp = await api.post('/qualification/trigger-ocr/', formData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
                // Multi-page certificates run one LLM OCR pass per page in n8n —
                // must outlast the backend's 180s n8n timeout (default 30s axios
                // timeout silently dropped the OCR result)
                timeout: 200000,
              });
              console.log(`✅ ${documentTypeDisplay} sent to n8n for OCR processing`);

              // Backend proxy returns: { success: true, data: <n8n_response> }
              // Extract the actual n8n response from the proxy wrapper
              const proxyResponse = n8nResp?.data;
              const n8nResponseData = proxyResponse?.data || proxyResponse || {};
              
              // Normalize n8n response: handle array root and custom key names
              // The n8n response might be nested: { data: { ocr_data: {...} } } or directly { ocr_data: {...} }
              const rawPayload = n8nResponseData;
              const n8nPayload = firstMeaningfulQualificationN8nPage(rawPayload);
              
              console.log(`[${documentTypeDisplay} OCR] Proxy response structure:`, {
                proxyResponse: proxyResponse,
                n8nResponseData: n8nResponseData,
                n8nPayload: n8nPayload
              });
              
              // Extract OCR data from various possible response structures
              // N8N may return: { education: [{ ocr_institution_name: "...", ... }], professional_certs: [] }
              let extracted = proxyResponse?.ocr_data || null;
              
              // Try standard paths first
              if (!extracted && n8nPayload.ocr_data) {
                extracted = n8nPayload.ocr_data;
              } else if (n8nPayload.extracted_data) {
                extracted = n8nPayload.extracted_data;
              } else if (n8nPayload.qualification_data) {
                extracted = n8nPayload.qualification_data;
              } else if (documentType === 'education_certificate' && n8nPayload.education) {
                // Handle N8N response structure: { education: [{ ocr_institution_name: "...", ... }] }
                const educationArray = Array.isArray(n8nPayload.education) ? n8nPayload.education : [n8nPayload.education];
                if (educationArray.length > 0 && educationArray[0]) {
                  extracted = educationArray[0];
                  console.log(`[${documentTypeDisplay} OCR] Extracted from education array`);
                }
              } else if (documentType === 'professional_certificate' && n8nPayload.professional_certs) {
                // Handle N8N response structure: { professional_certs: [{ ocr_cert_name: "...", ... }] }
                const certsArray = Array.isArray(n8nPayload.professional_certs) ? n8nPayload.professional_certs : [n8nPayload.professional_certs];
                if (certsArray.length > 0 && certsArray[0]) {
                  extracted = certsArray[0];
                  console.log(`[${documentTypeDisplay} OCR] Extracted from professional_certs array`);
                }
              } else if (documentType === 'professional_certificate' && n8nPayload.education) {
                // Generic certificate OCR can return professional/training certs under `education`.
                const educationArray = Array.isArray(n8nPayload.education) ? n8nPayload.education : [n8nPayload.education];
                if (educationArray.length > 0 && educationArray[0]) {
                  extracted = educationArray[0];
                  console.log(`[${documentTypeDisplay} OCR] Extracted from fallback education array`);
                }
              }
              
              // If n8n returns OCR data directly at root level (fallback)
              if (!extracted && n8nPayload && Object.keys(n8nPayload).length > 0) {
                // Check if it's an object with ocr_ prefixed fields
                const hasOcrFields = Object.keys(n8nPayload).some(key => key.startsWith('ocr_'));
                if (hasOcrFields) {
                  extracted = n8nPayload;
                  console.log(`[${documentTypeDisplay} OCR] Extracted from root level (ocr_ prefixed fields)`);
                }
              }
              
              console.log(`[${documentTypeDisplay} OCR] n8n payload keys:`, n8nPayload ? Object.keys(n8nPayload) : 'none');
              console.log(`[${documentTypeDisplay} OCR] extracted keys:`, extracted ? Object.keys(extracted) : 'none');
              console.log(`[${documentTypeDisplay} OCR] extracted data:`, extracted);

              if (extracted && Object.keys(extracted).length > 0) {
                // Normalize OCR data to match backend expectations
                let normalizedOcrData = {};
                
                if (documentType === 'education_certificate') {
                  // Normalize education certificate fields
                  // Handle both ocr_ prefixed fields (from N8N) and non-prefixed fields
                  const getField = (ocrKey, altKeys = []) => {
                    // Try ocr_ prefixed key first (N8N format)
                    if (extracted[ocrKey] !== undefined && extracted[ocrKey] !== null && extracted[ocrKey] !== '') {
                      return extracted[ocrKey];
                    }
                    // Try without ocr_ prefix
                    const baseKey = ocrKey.replace('ocr_', '');
                    if (extracted[baseKey] !== undefined && extracted[baseKey] !== null && extracted[baseKey] !== '') {
                      return extracted[baseKey];
                    }
                    // Try alternative keys
                    for (const altKey of altKeys) {
                      if (extracted[altKey] !== undefined && extracted[altKey] !== null && extracted[altKey] !== '') {
                        return extracted[altKey];
                      }
                    }
                    return '';
                  };
                  
                  normalizedOcrData = {
                    institution_name: getField('ocr_institution_name', ['institution_name', 'school_name', 'university_name', 'college_name']),
                    qualification_name: getField('ocr_qualification_name', ['qualification_name', 'degree_name', 'diploma_name', 'certificate_name']),
                    field_of_study: getField('ocr_field_of_study', ['field_of_study', 'major', 'subject', 'discipline']),
                    start_year: extracted.ocr_start_year || extracted.start_year || (extracted.start_date ? new Date(extracted.start_date).getFullYear() : null),
                    end_year: extracted.ocr_end_year || extracted.end_year || (extracted.end_date ? new Date(extracted.end_date).getFullYear() : extracted.graduation_year || null),
                    country: getField('ocr_country', ['country', 'country_name']) || 'Singapore',
                    certificate_number: getField('ocr_certificate_number', ['certificate_number', 'cert_number', 'credential_number', 'document_number'])
                  };
                } else {
                  // Normalize professional certificate fields
                  // Handle both ocr_ prefixed fields (from N8N) and non-prefixed fields
                  const getField = (ocrKey, altKeys = []) => {
                    // Try ocr_ prefixed key first (N8N format)
                    if (extracted[ocrKey] !== undefined && extracted[ocrKey] !== null && extracted[ocrKey] !== '') {
                      return extracted[ocrKey];
                    }
                    // Try without ocr_ prefix
                    const baseKey = ocrKey.replace('ocr_', '');
                    if (extracted[baseKey] !== undefined && extracted[baseKey] !== null && extracted[baseKey] !== '') {
                      return extracted[baseKey];
                    }
                    // Try alternative keys
                    for (const altKey of altKeys) {
                      if (extracted[altKey] !== undefined && extracted[altKey] !== null && extracted[altKey] !== '') {
                        return extracted[altKey];
                      }
                    }
                    return '';
                  };
                  
                  normalizedOcrData = {
                    cert_name: getField('ocr_cert_name', ['cert_name', 'certificate_name', 'name', 'ocr_field_of_study', 'field_of_study', 'ocr_qualification_name', 'qualification_name']),
                    cert_short_name: getField('ocr_cert_short_name', ['cert_short_name', 'short_name', 'abbreviation']),
                    issuing_organization: getField('ocr_issuing_organization', ['issuing_organization', 'issuer', 'organization', 'authority', 'ocr_institution_name', 'institution_name', 'school_name', 'university_name', 'college_name']),
                    cert_category: getField('ocr_cert_category', ['cert_category', 'category', 'type']),
                    issue_date: extracted.ocr_issue_date || extracted.issue_date || extracted.issued_date || extracted.date_issued || extracted.ocr_end_date || extracted.end_date || null,
                    expiry_date: extracted.ocr_expiry_date || extracted.expiry_date || extracted.expires_date || extracted.expiration_date || extracted.valid_until || null,
                    certificate_number: getField('ocr_certificate_number', ['certificate_number', 'cert_number', 'number', 'license_number']),
                    cert_country: getField('ocr_cert_country', ['cert_country', 'country', 'country_name']) || 'Singapore'
                  };
                }
                
                // Forward OCR to backend webhook
                try {
                  console.log(`[${documentTypeDisplay} OCR] Persisting via webhook with:`, normalizedOcrData);
                  await api.post('/qualification/ocr-webhook/', {
                    document_approval_id: response.data.document_approval_id,
                    document_type: documentType,
                    ocr_data: normalizedOcrData
                  });
                  console.log('✅ OCR data persisted via webhook');
                  
                  setOcrProcessing(prev => { const next = { ...prev }; delete next[ocrKey]; return next; });
                } catch (persistErr) {
                  console.error('⚠️ Failed to persist OCR via webhook:', persistErr);
                  setOcrProcessing(prev => { const next = { ...prev }; delete next[ocrKey]; return next; });
                  toast.error('Could not save extracted details. Please try again.');
                }

                // Open verification modal with initial OCR data for immediate display
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: documentType,
                  initialOcrData: normalizedOcrData,
                  viewMode: 'edit'
                });
              } else {
                setOcrProcessing(prev => { const next = { ...prev }; delete next[ocrKey]; return next; });
                toast.error('Could not read your certificate. Please enter details manually.');
                
                // Fallback: open modal and let it fetch from backend
                setTimeout(() => {
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: documentType,
                    initialOcrData: {},
                    viewMode: 'edit'
                  });
                }, 3000);
              }
            } catch (n8nError) {
              console.error(`❌ Failed to send ${documentTypeDisplay} to n8n:`, n8nError);
              console.error('n8n Error details:', n8nError.response?.data || n8nError.message);
              setOcrProcessing(prev => { const next = { ...prev }; delete next[ocrKey]; return next; });
              toast.error('Could not read your certificate. Please enter details manually.');
              // Still open modal to allow manual entry
              setTimeout(() => {
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: documentType,
                  initialOcrData: {},
                  viewMode: 'edit'
                });
              }, 3000);
            }
          }
        } else if (isBankStatement) {
          // Handle bank statement upload with n8n OCR integration
          console.log('🏦 Bank Statement upload detected - workflowType:', workflowType);
          
          const formData = new FormData();
          formData.append('file', file);
          
          console.log('🏦 Calling /bank-statement/upload/ endpoint...');
          const response = await api.post('/bank-statement/upload/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          if (response.data.success) {
            // Stage 1: Upload confirmation (immediate)
            toast.success(
              `✅ Bank Statement uploaded successfully`,
              { duration: 3000 }
            );

            // Stage 2: Inline processing indicator
            setOcrProcessing(prev => ({ ...prev, bank_statement: 'Analysing your bank statement...' }));
            
            // Add to documents list with special status
            const newDocument = {
              id: response.data.document_approval_id,
              name: file.name,
              category: categoryId,
              subCategory: 'bank_statement',
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
              status: 'processing_ocr',
              documentApprovalId: response.data.document_approval_id,
              documentType: 'bank_statement',
              isBankStatement: true
            };
            
            setDocuments(prev => [...prev, newDocument]);
            
            // Send to n8n for OCR processing via backend proxy
            try {
              console.log('🏦 Sending bank statement to n8n webhook via backend proxy...');
              
              const proxyFormData = new FormData();
              proxyFormData.append('file', file);
              proxyFormData.append('document_approval_id', response.data.document_approval_id);
              
              const n8nResp = await api.post('/bank-statement/trigger-ocr/', proxyFormData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
              console.log('✅ Bank Statement sent to n8n for OCR processing');

              // Extract n8n response data
              const proxyResponse = n8nResp?.data;
              const n8nResponseData = proxyResponse?.data || proxyResponse || {};
              const rawPayload = n8nResponseData;
              const firstItem = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
              const n8nPayload = (firstItem && firstItem.data) ? firstItem.data : firstItem || {};
              
              console.log('[Bank Statement OCR] Proxy response structure:', {
                proxyResponse,
                n8nResponseData,
                n8nPayload
              });
              
              // Extract OCR data from various possible response structures
              let extracted = null;
              
              if (n8nPayload.ocr_data) {
                extracted = n8nPayload.ocr_data;
              } else if (n8nPayload.extracted_data) {
                extracted = n8nPayload.extracted_data;
              } else if (n8nPayload.bank_details) {
                extracted = n8nPayload.bank_details;
              } else if (n8nPayload.bank_data) {
                extracted = n8nPayload.bank_data;
              }
              
              // If n8n returns OCR data directly at root level
              if (!extracted && n8nPayload && Object.keys(n8nPayload).length > 0) {
                const hasOcrFields = Object.keys(n8nPayload).some(key => key.startsWith('ocr_'));
                const hasBankFields = Object.keys(n8nPayload).some(key => 
                  key.includes('bank') || key.includes('account') || key.includes('branch')
                );
                if (hasOcrFields || hasBankFields) {
                  extracted = n8nPayload;
                  console.log('[Bank Statement OCR] Extracted from root level');
                }
              }
              
              console.log('[Bank Statement OCR] extracted keys:', extracted ? Object.keys(extracted) : 'none');
              console.log('[Bank Statement OCR] extracted data:', extracted);

              if (extracted && Object.keys(extracted).length > 0) {
                // Normalize OCR data fields
                const getField = (ocrKey, altKeys = []) => {
                  if (extracted[ocrKey] !== undefined && extracted[ocrKey] !== null && extracted[ocrKey] !== '') {
                    return extracted[ocrKey];
                  }
                  const baseKey = ocrKey.replace('ocr_', '');
                  if (extracted[baseKey] !== undefined && extracted[baseKey] !== null && extracted[baseKey] !== '') {
                    return extracted[baseKey];
                  }
                  for (const altKey of altKeys) {
                    if (extracted[altKey] !== undefined && extracted[altKey] !== null && extracted[altKey] !== '') {
                      return extracted[altKey];
                    }
                  }
                  return '';
                };
                
                const normalizedOcrData = {
                  ocr_bank_name: getField('ocr_bank_name', ['bank_name', 'bank', 'financial_institution']),
                  ocr_bank_account_number: getField('ocr_bank_account_number', ['bank_account_number', 'account_number', 'account_no']),
                  ocr_branch_code: getField('ocr_branch_code', ['branch_code', 'branch_no', 'branch_number', 'sort_code']),
                  ocr_account_holder_name: getField('ocr_account_holder_name', ['account_holder_name', 'account_holder', 'holder_name', 'name'])
                };
                
                // Forward OCR to backend webhook to persist
                try {
                  console.log('[Bank Statement OCR] Persisting via webhook with:', normalizedOcrData);
                  await api.post('/bank-statement/ocr-webhook/', {
                    document_approval_id: response.data.document_approval_id,
                    ocr_data: normalizedOcrData
                  });
                  console.log('✅ Bank Statement OCR data persisted via webhook');
                  
                  setOcrProcessing(prev => { const next = { ...prev }; delete next.bank_statement; return next; });
                } catch (persistErr) {
                  console.error('⚠️ Failed to persist Bank Statement OCR via webhook:', persistErr);
                  setOcrProcessing(prev => { const next = { ...prev }; delete next.bank_statement; return next; });
                  toast.error('Could not save extracted details. Please try again.');
                }

                // Open verification modal with OCR data
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'bank_statement',
                  initialOcrData: normalizedOcrData,
                  viewMode: 'edit'
                });
              } else {
                setOcrProcessing(prev => { const next = { ...prev }; delete next.bank_statement; return next; });
                toast.error('Could not read your bank statement. Please enter details manually.');
                
                // Fallback: open modal and let it fetch from backend
                setTimeout(() => {
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: 'bank_statement',
                    initialOcrData: {},
                    viewMode: 'edit'
                  });
                }, 3000);
              }
            } catch (n8nError) {
              console.error('❌ Failed to send bank statement to n8n:', n8nError);
              console.error('n8n Error details:', n8nError.response?.data || n8nError.message);
              setOcrProcessing(prev => { const next = { ...prev }; delete next.bank_statement; return next; });
              
              // Check for duplicate submission (409)
              if (n8nError.response?.status === 409) {
                toast.error(n8nError.response.data?.error || 'A bank statement is already pending review.');
              } else {
                toast.error('Could not read your bank statement. Please enter details manually.');
              }
              
              // Still open modal to allow manual entry
              setTimeout(() => {
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'bank_statement',
                  initialOcrData: {},
                  viewMode: 'edit'
                });
              }, 3000);
            }
          }
        } else if (isPassport) {
          // Handle passport upload with n8n OCR integration
          console.log('🛂 Passport upload detected - workflowType:', workflowType);
          
          const formData = new FormData();
          formData.append('file', file);
          
          console.log('🛂 Calling /passport/upload/ endpoint...');
          const response = await api.post('/passport/upload/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          if (response.data.success) {
            // Stage 1: Upload confirmation (immediate)
            toast.success(
              `✅ Passport uploaded successfully`,
              { duration: 3000 }
            );

            // Stage 2: Inline processing indicator
            setOcrProcessing(prev => ({ ...prev, passport_upload: 'Analysing your passport...' }));
            
            // Add to documents list with special status
            const newDocument = {
              id: response.data.document_approval_id,
              name: file.name,
              category: categoryId,
              subCategory: 'passport',
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
              status: 'processing_ocr',
              documentApprovalId: response.data.document_approval_id,
              documentType: 'passport',
              isPassport: true
            };
            
            setDocuments(prev => [...prev, newDocument]);
            
            // Send to n8n for OCR processing via backend proxy
            try {
              console.log('🛂 Sending passport to n8n webhook via backend proxy...');
              
              const proxyFormData = new FormData();
              proxyFormData.append('file', file);
              proxyFormData.append('document_approval_id', response.data.document_approval_id);
              
              const n8nResp = await api.post('/passport/trigger-ocr/', proxyFormData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
              console.log('✅ Passport sent to n8n for OCR processing');

              // Extract n8n response data
              const proxyResponse = n8nResp?.data;
              const n8nResponseData = proxyResponse?.data || proxyResponse || {};
              const rawPayload = n8nResponseData;
              const firstItem = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
              const n8nPayload = (firstItem && firstItem.data) ? firstItem.data : firstItem || {};
              
              console.log('[Passport OCR] Proxy response structure:', {
                proxyResponse,
                n8nResponseData,
                n8nPayload
              });
              
              // Extract OCR data from various possible response structures
              let extracted = null;
              
              if (n8nPayload.ocr_data) {
                extracted = n8nPayload.ocr_data;
              } else if (n8nPayload.extracted_data) {
                extracted = n8nPayload.extracted_data;
              } else if (n8nPayload.passport_details) {
                extracted = n8nPayload.passport_details;
              } else if (n8nPayload.passport_data) {
                extracted = n8nPayload.passport_data;
              }
              
              // If n8n returns OCR data directly at root level
              if (!extracted && n8nPayload && Object.keys(n8nPayload).length > 0) {
                const hasOcrFields = Object.keys(n8nPayload).some(key => key.startsWith('ocr_passport'));
                const hasPassportFields = Object.keys(n8nPayload).some(key => 
                  key.includes('passport') || key.includes('nationality') || key.includes('mrz')
                );
                if (hasOcrFields || hasPassportFields) {
                  extracted = n8nPayload;
                  console.log('[Passport OCR] Extracted from root level');
                }
              }
              
              console.log('[Passport OCR] extracted keys:', extracted ? Object.keys(extracted) : 'none');
              console.log('[Passport OCR] extracted data:', extracted);

              if (extracted && Object.keys(extracted).length > 0) {
                // Normalize OCR data fields
                const getField = (ocrKey, altKeys = []) => {
                  if (extracted[ocrKey] !== undefined && extracted[ocrKey] !== null && extracted[ocrKey] !== '') {
                    return extracted[ocrKey];
                  }
                  const baseKey = ocrKey.replace('ocr_passport_', '');
                  if (extracted[baseKey] !== undefined && extracted[baseKey] !== null && extracted[baseKey] !== '') {
                    return extracted[baseKey];
                  }
                  for (const altKey of altKeys) {
                    if (extracted[altKey] !== undefined && extracted[altKey] !== null && extracted[altKey] !== '') {
                      return extracted[altKey];
                    }
                  }
                  return '';
                };
                
                const normalizedOcrData = {
                  ocr_passport_first_name: getField('ocr_passport_first_name', ['first_name', 'given_names', 'given_name']),
                  ocr_passport_last_name: getField('ocr_passport_last_name', ['last_name', 'surname', 'family_name']),
                  ocr_passport_number: getField('ocr_passport_number', ['passport_number', 'document_number', 'number']),
                  ocr_passport_nationality: getField('ocr_passport_nationality', ['nationality', 'country_code', 'issuing_country']),
                  ocr_passport_date_of_birth: getField('ocr_passport_date_of_birth', ['date_of_birth', 'dob', 'birth_date']),
                  ocr_passport_gender: getField('ocr_passport_gender', ['gender', 'sex']),
                  ocr_passport_expiry_date: getField('ocr_passport_expiry_date', ['expiry_date', 'expiration_date', 'date_of_expiry']),
                  ocr_passport_mrz_line1: getField('ocr_passport_mrz_line1', ['mrz_line1', 'mrz_line_1', 'mrz1']),
                  ocr_passport_mrz_line2: getField('ocr_passport_mrz_line2', ['mrz_line2', 'mrz_line_2', 'mrz2'])
                };
                
                // Forward OCR to backend webhook to persist
                try {
                  console.log('[Passport OCR] Persisting via webhook with:', normalizedOcrData);
                  await api.post('/passport/ocr-webhook/', {
                    document_approval_id: response.data.document_approval_id,
                    ocr_data: normalizedOcrData
                  });
                  console.log('✅ Passport OCR data persisted via webhook');
                  
                  setOcrProcessing(prev => { const next = { ...prev }; delete next.passport_upload; return next; });
                } catch (persistErr) {
                  console.error('⚠️ Failed to persist Passport OCR via webhook:', persistErr);
                  setOcrProcessing(prev => { const next = { ...prev }; delete next.passport_upload; return next; });
                  toast.error('Could not save extracted details. Please try again.');
                }

                // Open verification modal with OCR data
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'passport',
                  initialOcrData: normalizedOcrData,
                  viewMode: 'edit'
                });
              } else {
                setOcrProcessing(prev => { const next = { ...prev }; delete next.passport_upload; return next; });
                setDocuments(prev => prev.map(doc =>
                  doc.documentApprovalId === response.data.document_approval_id
                    ? { ...doc, status: 'pending' }
                    : doc
                ));
                toast.warning('Could not read your passport. Please enter details manually.');
                
                // Fallback: open modal and let it fetch from backend
                setTimeout(() => {
                  setVerificationModal({
                    open: true,
                    documentApprovalId: response.data.document_approval_id,
                    documentType: 'passport',
                    initialOcrData: {},
                    viewMode: 'edit'
                  });
                }, 3000);
              }
            } catch (n8nError) {
              console.error('❌ Failed to send passport to n8n:', n8nError);
              console.error('n8n Error details:', n8nError.response?.data || n8nError.message);
              setOcrProcessing(prev => { const next = { ...prev }; delete next.passport_upload; return next; });
              setDocuments(prev => prev.map(doc =>
                doc.documentApprovalId === response.data.document_approval_id
                  ? { ...doc, status: 'pending' }
                  : doc
              ));
              
              // Check for duplicate submission (409)
              if (n8nError.response?.status === 409) {
                toast.error(n8nError.response.data?.error || 'A passport is already pending review.');
              } else {
                toast.warning('Could not read your passport. Please enter details manually.');
              }
              
              // Still open modal to allow manual entry
              setTimeout(() => {
                setVerificationModal({
                  open: true,
                  documentApprovalId: response.data.document_approval_id,
                  documentType: 'passport',
                  initialOcrData: {},
                  viewMode: 'edit'
                });
              }, 3000);
            }
          }
        } else {
          // Handle regular document upload (existing logic)
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const newDocument = {
            id: fileId,
            name: file.name,
            category: categoryId,
            subCategory: subCategory,
            size: file.size,
            type: file.type,
            uploadedAt: new Date().toISOString(),
            status: 'uploaded'
          };

          setDocuments(prev => [...prev, newDocument]);
          
          // Special handling for identity documents
          if (categoryId === 'identity' && subCategory) {
            toast.success(
              <div>
                <p className="font-medium">{file.name} uploaded successfully!</p>
                <p className="text-sm mt-1 text-blue-700">🆔 {subCategory} document added to your profile</p>
                <p className="text-xs mt-1">
                  This document will be used for identification and compliance purposes
                </p>
              </div>,
              { duration: 4000 }
            );
          } else if (docType && leaveDocumentTriggers[docType]) {
            // Check if this document triggers any leave types
            const trigger = leaveDocumentTriggers[docType];
            toast.success(
              <div>
                <p className="font-medium">{file.name} uploaded successfully!</p>
                <p className="text-sm mt-1 text-green-700">🎉 {trigger.message}</p>
                <p className="text-xs mt-1">
                  Available leave types: {trigger.leaveTypes.join(', ')}
                </p>
              </div>,
              { duration: 6000 }
            );
          } else {
            toast.success(`${file.name} uploaded successfully!`);
          }
        }
      } catch (error) {
        console.error('Upload error:', error);
        const errData = error.response?.data;
        
        // Birth certificates are child-specific. A different file can be added for
        // another child after confirmation, whether the existing certificate is
        // approved or still with HR. Exact-file duplicates never reach this path.
        const canConfirmAdditionalChild = (
          error.response?.status === 409
          && ['approved_exists', 'pending_document'].includes(errData?.duplicate_type)
          && errData?.requires_confirmation
          && !uploadOptions.confirmAdditionalChild
        );
        if (canConfirmAdditionalChild) {
          const confirmed = await showConfirm({
            title: 'Additional Birth Certificate',
            message: errData.error,
            confirmLabel: 'Yes, Different Child',
            cancelLabel: 'Cancel',
            variant: 'primary',
          });
          if (confirmed) {
            await handleFileUpload(
              [file],
              categoryId,
              workflowType,
              { confirmAdditionalChild: true },
            );
          }
        } else if (errData?.error) {
          toast.error(`Upload failed: ${errData.error}`);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } finally {
        setUploadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileId);
          return newSet;
        });
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Smart marriage role recommendation based on name matching
  const getRecommendedMarriageRole = (ocrData) => {
    if (!profile || !ocrData) return null;
    
    // Get employee name from profile - combine first_name and last_name
    const employeeName = `${profile?.first_name || ''} ${profile?.last_name || ''}`.toLowerCase().trim();
    
    // Get names from OCR data
    const bridegroomName = (ocrData.bridegroom_full_name || ocrData.groom_name || '').toLowerCase().trim();
    const brideName = (ocrData.bride_full_name || ocrData.bride_name || '').toLowerCase().trim();
    
    // Get employee gender for validation
    const gender = profile?.gender || hrProfile?.gender;
    
    // Debug logging
    console.log('[Marriage Role Detection] Profile data:', {
      first_name: profile?.first_name,
      last_name: profile?.last_name,
      employeeName: employeeName,
      gender: gender,
      ocrData: ocrData
    });
    
    // Helper function for name matching (exact or partial)
    const nameMatches = (name1, name2) => {
      if (!name1 || !name2) return false;
      if (name1 === name2) return true;
      
      const words1 = name1.split(' ').filter(w => w.length > 2);
      const words2 = name2.split(' ').filter(w => w.length > 2);
      return words1.some(word => 
        words2.some(ocrWord => ocrWord.includes(word) || word.includes(ocrWord))
      );
    };
    
    // Check bridegroom match with gender cross-validation
    if (nameMatches(employeeName, bridegroomName)) {
      const genderMatch = gender === 'male' || gender === 'M';
      console.log('[Marriage Role Detection] Bridegroom name match:', {
        employeeName,
        bridegroomName,
        gender,
        genderMatch: genderMatch ? '✓ Matches (male)' : '⚠ Mismatch (not male)'
      });
      return 'bridegroom'; // Always return bridegroom if name matches
    }
    
    // Check bride match with gender cross-validation
    if (nameMatches(employeeName, brideName)) {
      const genderMatch = gender === 'female' || gender === 'F';
      console.log('[Marriage Role Detection] Bride name match:', {
        employeeName,
        brideName,
        gender,
        genderMatch: genderMatch ? '✓ Matches (female)' : '⚠ Mismatch (not female)'
      });
      return 'bride'; // Always return bride if name matches
    }
    
    // Only use gender fallback if NO name matches
    console.log('[Marriage Role Detection] No name matches found, using gender fallback:', { gender });
    if (gender === 'male') return 'bridegroom';
    if (gender === 'female') return 'bride';
    
    return null; // Can't determine role
  };

  // Marriage role selection is now integrated into DocumentVerificationModal
  // No separate modal needed
  const showMarriageRoleSelectionModal = (recommendedRole, ocrData) => {
    // This function is no longer needed as role selection is integrated into DocumentVerificationModal
    console.log('Role selection is now handled in DocumentVerificationModal');
    return Promise.resolve(null);
  };

  // Process marriage certificate with selected role
  const processMarriageCertificateWithRole = async (documentApprovalId, staffId, extracted, employeeRole) => {
    try {
      // Validate that we have OCR data with required fields
      if (!extracted || Object.keys(extracted).length === 0) {
        console.log('[Marriage OCR] No OCR data available, skipping webhook call');
        toast.warning('⚠️ OCR data not available. Please review the document manually.', { duration: 3000 });
        return;
      }

      // Prepare OCR data for backend
      const payloadToBackend = {
        bridegroom_full_name: extracted.bridegroom_full_name || extracted.groom_name || '',
        bride_full_name: extracted.bride_full_name || extracted.bride_name || '',
        marriage_date: extracted.marriage_date || extracted.wedding_date || '',
        marriage_certificate_number: extracted.marriage_certificate_number || extracted.certificate_number || '',
        marriage_venue: extracted.marriage_venue || extracted.venue || '',
        witness_1_name: extracted.witness_1_name || extracted.witness1_name || '',
        witness_2_name: extracted.witness_2_name || extracted.witness2_name || '',
        confidence_score: extracted.confidence_score || extracted.confidence || 95.0
      };

      // Validate required fields before sending to webhook
      const requiredFields = ['bridegroom_full_name', 'bride_full_name', 'marriage_date', 'marriage_certificate_number'];
      const missingFields = requiredFields.filter(field => !payloadToBackend[field]);
      
      if (missingFields.length > 0) {
        console.log('[Marriage OCR] Missing required fields:', missingFields);
        toast.warning(`⚠️ OCR data incomplete. Missing: ${missingFields.join(', ')}. Please review manually.`, { duration: 4000 });
        return;
      }

      // Add employee role to payload
      const payloadWithRole = {
        document_approval_id: documentApprovalId,
        staff_id: staffId,
        employee_role: employeeRole,
        ocr_data: payloadToBackend
      };

      console.log('[Marriage OCR] Persisting via webhook with role:', payloadWithRole);
      
      // Send to backend webhook
      await api.post('/marriage-certificate/ocr-webhook/', payloadWithRole);
      console.log('✅ Marriage OCR data persisted via webhook with role');
      
      // Show success message
      toast.success(
        `💍 Marriage certificate processed! You've been identified as the ${employeeRole === 'bridegroom' ? 'Bridegroom' : 'Bride'}.`,
        { duration: 3000 }
      );
      
      // Refresh profile data to show updated marriage information
      setTimeout(async () => {
        try {
          await fetchProfileData();
          console.log('✅ Profile data refreshed after marriage certificate processing');
        } catch (refreshError) {
          console.error('❌ Failed to refresh profile data:', refreshError);
        }
      }, 1000); // Small delay to ensure backend processing is complete
      
    } catch (persistErr) {
      console.error('⚠️ Failed to persist marriage OCR via webhook:', persistErr);
      toast.error('❌ Unable to save marriage certificate data. Please try again or contact HR.');
    }
  };

     const getColorClasses = (color) => {
     const colors = {
       blue: 'bg-blue-50 border-blue-200 text-blue-800',
       purple: 'bg-purple-50 border-purple-200 text-purple-800',
       green: 'bg-green-50 border-green-200 text-green-800',
       emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
       red: 'bg-red-50 border-red-200 text-red-800',
       pink: 'bg-pink-50 border-pink-200 text-pink-800',
       amber: 'bg-amber-50 border-amber-200 text-amber-800',
       orange: 'bg-orange-50 border-orange-200 text-orange-800',
       indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
       teal: 'bg-teal-50 border-teal-200 text-teal-800',
       gray: 'bg-gray-50 border-gray-200 text-gray-800'
     };
     return colors[color] || colors.gray;
   };

   const getIconColorClass = (color) => {
     const colors = {
       blue: 'text-blue-600',
       purple: 'text-purple-600',
       green: 'text-green-600',
       emerald: 'text-emerald-600',
       red: 'text-red-600',
       pink: 'text-pink-600',
       amber: 'text-amber-600',
       orange: 'text-orange-600',
       indigo: 'text-indigo-600',
       teal: 'text-teal-600',
       gray: 'text-gray-600'
     };
     return colors[color] || colors.gray;
   };

   const getBgColorClass = (color) => {
     const colors = {
       blue: 'bg-blue-100',
       purple: 'bg-purple-100',
       green: 'bg-green-100',
       emerald: 'bg-emerald-100',
       red: 'bg-red-100',
       pink: 'bg-pink-100',
       amber: 'bg-amber-100',
       orange: 'bg-orange-100',
       indigo: 'bg-indigo-100',
       teal: 'bg-teal-100',
       gray: 'bg-gray-100'
     };
     return colors[color] || colors.gray;
   };

   // Renders the verification-status row for a document card. Shows a per-status
   // count breakdown (e.g. "4 Verified", or "2 Verified" + "1 Pending HR") once
   // documents are uploaded. When nothing is uploaded it shows "Required" only
   // for cards the employee must provide; optional cards render no row.
   const renderDropzoneStatusRow = (docs, isRequired = false) => {
     const entries = getDropzoneStatusEntries(docs);
     if (entries.length === 0 && !isRequired) return null;
     return (
       <div className="mt-3 pt-3 border-t border-black/10">
         <div className="flex items-center justify-between gap-2 text-xs">
           <span className="font-medium opacity-75">Status:</span>
           <div className="flex flex-wrap items-center justify-end gap-1.5">
             {entries.length === 0 ? (
               <StatusBadge status="required" showIcon />
             ) : (
               entries.map(({ status, count }) => (
                 <StatusBadge
                   key={status}
                   status={status}
                   label={`${count} ${getLabelForStatus(status)}`}
                   showIcon
                 />
               ))
             )}
           </div>
         </div>
       </div>
     );
   };

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4">
          <button
            onClick={() => setSubTab('letters')}
            className={`tab-btn tab-btn-sm ${subTab === 'letters' ? 'active' : ''}`}
          >
            <FileText className="w-4 h-4" />
            Official HR Letters
          </button>
          <button
            onClick={() => setSubTab('uploads')}
            className={`tab-btn tab-btn-sm ${subTab === 'uploads' ? 'active' : ''}`}
          >
            <Upload className="w-4 h-4" />
            Document Uploads
          </button>
        </div>

      {subTab === 'letters' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate min-w-0">Official HR Letters</h3>
                  <CountBadge count={(letters || []).length} theme="employee" size="md" />
                </div>
                <div className="hidden md:flex flex-shrink-0">
                  <PillToggle
                    value={lettersViewMode}
                    onChange={setLettersViewMode}
                    options={[
                      { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                      { id: 'list', label: 'List view', icon: List },
                    ]}
                    variant="icon"
                    theme="employee"
                    ariaLabel="View mode selection"
                  />
                </div>
              </div>
            </div>

          {lettersLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">Loading letters...</span>
            </div>
          ) : (!letters || letters.length === 0) ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No official HR letters yet</h3>
              <p className="text-sm text-gray-500">Letters generated for you will appear here.</p>
            </div>
          ) : (
            <>
            {lettersViewMode === 'list' && (
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="table-header">
                      <tr>
                        <th className="table-header-cell">Generated</th>
                        <th className="table-header-cell">Letter Type</th>
                        <th className="table-header-cell">Reference</th>
                        <th className="table-header-cell">Expiry Status</th>
                        <th className="table-header-cell">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(letters || []).map((l) => {
                        const expiryInfo = l.valid_until ? getExpiryStatus(l.valid_until) : null;
                        const ExpiryIcon = expiryInfo?.icon;
                        return (
                          <tr key={l.id} className="text-sm table-row">
                            <td className="px-4 py-2">{l.generated_at ? formatDate(l.generated_at) : '-'}</td>
                            <td className="px-4 py-2 font-medium text-gray-900">
                              {l.letter_type_display || l.template_code || 'HR Letter'}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-600">
                              {l.letter_reference || '-'}
                            </td>
                            <td className="px-4 py-2">
                              {expiryInfo ? (
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${expiryInfo.color}`}>
                                  {ExpiryIcon && <ExpiryIcon className="w-3 h-3" />}
                                  {expiryInfo.label}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handlePreviewLetter(l)}
                                  className="icon-button"
                                  title="Preview letter"
                                  aria-label="Preview letter"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDownloadLetter(l.id, l.source)}
                                  className="icon-button"
                                  title="Download letter"
                                  aria-label="Download letter"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {lettersViewMode === 'grid' && (
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(letters || []).map((l) => {
                    const expiryInfo = l.valid_until ? getExpiryStatus(l.valid_until) : null;
                    const ExpiryIcon = expiryInfo?.icon;
                    const LetterIcon = getLetterTypeIcon(l);
                    const letterTitle = l.letter_type_display || l.template_code || 'HR Letter';
                    return (
                      <div
                        key={l.id}
                        className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3 cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => handlePreviewLetter(l)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePreviewLetter(l);
                          }
                        }}
                        aria-label={`Preview ${letterTitle}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <LetterIcon className="w-5 h-5 text-blue-800" aria-hidden />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                  {letterTitle}
                                </h4>
                                {l.letter_reference && (
                                  <p className="text-xs font-mono text-gray-500 mt-0.5 truncate">{l.letter_reference}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              {expiryInfo ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${expiryInfo.color}`}>
                                  {ExpiryIcon && <ExpiryIcon className="w-3 h-3" />}
                                  {expiryInfo.label}
                                </span>
                              ) : l.valid_until === null ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                  No Expiry
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="mb-2">
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {l.generated_at ? formatDate(l.generated_at) : 'Unknown date'}
                            </p>
                          </div>
                        </div>
                        <div
                          className="card-action-footer flex-nowrap"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => handleDownloadLetter(l.id, l.source)}
                            className="icon-button"
                            title="Download letter"
                            aria-label="Download letter"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </>
          )}
          </div>

          {letterPreview.open && letterPreview.letter && (
            <LetterPreviewModal
              letterId={letterPreview.letter.id}
              source={letterPreview.letter.source === 'asset_ack' ? 'asset_ack' : 'hr_letter'}
              letterTypeDisplay={
                letterPreview.letter.letter_type_display
                || letterPreview.letter.template_code
                || 'HR Letter'
              }
              letterReference={letterPreview.letter.letter_reference}
              assetTag={letterPreview.letter.asset_tag}
              theme="employee"
              allLettersHref={null}
              onClose={() => setLetterPreview({ open: false, letter: null })}
              onDownload={() => handleDownloadLetter(
                letterPreview.letter.id,
                letterPreview.letter.source,
              )}
            />
          )}
        </div>
      )}

      {subTab === 'uploads' && (
        <>
          {/* Category Filter - Horizontal scroll on mobile, wrap on desktop */}
          <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`tab-btn tab-btn-sm ${selectedCategory === null ? 'active' : ''}`}
            >
              <Filter className="w-4 h-4" />
              All
            </button>
            {documentCategories.map(category => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(selectedCategory === category.id ? null : category.id);
                  }}
                  className={`tab-btn tab-btn-sm ${selectedCategory === category.id ? 'active' : ''}`}
                >
                  <IconComponent className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>
          

          


          {/* Document Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map(category => {
              const IconComponent = category.icon;
              // Education & Professional certificates are normalized under the
              // 'qualification' category but render in their own cards, so match
              // them by approvalType as well as the direct category id.
              const categoryDocs = documents.filter(doc => {
                if (doc.category === category.id) return true;
                if (category.id === 'education') return doc.approvalType === 'education_certificate';
                if (category.id === 'professional') return doc.approvalType === 'professional_certificate';
                return false;
              });
              
              // Special handling for Family Documents, Identity Documents, and Banking Documents with sub-categories
              if ((category.id === 'family' || category.id === 'identity' || category.id === 'banking') && category.subCategories) {
                return category.subCategories.map(subCat => {
                  const SubIconComponent = subCat.icon;
                  const subCatDocs = documents.filter(doc => 
                    doc.category === category.id && 
                    (doc.workflowType === subCat.workflowType || 
                     doc.subCategory === subCat.id ||
                     doc.approvalType === subCat.workflowType ||
                     doc.approvalType === subCat.id)
                  );
                  
                  return (
                    <div key={subCat.id} className={`border-2 border-dashed rounded-xl p-6 ${getColorClasses(subCat.color)}`}>
                      <div>
                        {/* Header with icon, title, and View Samples button */}
                        <div className="flex items-center justify-between gap-2 mb-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                              <SubIconComponent className={`w-6 h-6 ${getIconColorClass(subCat.color)}`} />
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold truncate">{subCat.name}</h3>
                          </div>
                          {category.id === 'identity' && (subCat.id === 'id_card_front' || subCat.id === 'id_card_back' || subCat.id === 'passport') && (
                            <button
                              onClick={() => setIdCardSamplesModal({ open: true, type: subCat.id })}
                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors border border-blue-200"
                              title={`View ${subCat.id === 'passport' ? 'passport' : subCat.id === 'id_card_front' ? 'front' : 'back'} sample images`}
                            >
                              <Eye className="w-4 h-4" />
                              Sample
                            </button>
                          )}
                        </div>
                        <p className="text-sm opacity-75 mb-4">{subCat.description}</p>
                        
                        {/* Upload Area */}
                        {ocrProcessing[subCat.workflowType] ? (
                          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                            <div className="flex items-start gap-3">
                              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-blue-800">{ocrProcessing[subCat.workflowType]}</p>
                                <p className="text-xs text-blue-600 mt-1">Please don't navigate away</p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              type="file"
                              multiple
                              accept={subCat.acceptedTypes}
                              onChange={(e) => handleFileUpload(e.target.files, category.id, subCat.workflowType)}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                            <div className="bg-white rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors text-center">
                              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                              <p className="text-sm text-gray-600">
                                Drop files here or <span className="text-blue-600 font-medium">browse</span>
                              </p>
                              <p className="text-xs text-gray-500 mt-1">Max 5MB per file</p>
                            </div>
                          </div>
                        )}
                        
                        {/* Examples */}
                        {subCat.examples && subCat.examples.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-medium mb-2">Examples:</p>
                            <ul className="text-xs opacity-75 space-y-1">
                              {subCat.examples.map((example, index) => (
                                <li key={index} className="flex items-center">
                                  <div className="w-1 h-1 bg-current rounded-full mr-2"></div>
                                  {example}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Leave Types for this sub-category */}
                        {subCat.leaveTypes && subCat.leaveTypes.length > 0 && (
                          <div className="mt-4 p-3 bg-white/80 rounded-lg border border-white/40">
                            <div className="flex items-start gap-2">
                              <Heart className={`w-4 h-4 ${getIconColorClass(subCat.color)} mt-0.5 flex-shrink-0`} />
                              <div className="text-left">
                                <p className={`text-xs font-medium ${getIconColorClass(subCat.color)} mb-1`}>Leave Benefits</p>
                                <p className={`text-xs ${getIconColorClass(subCat.color)}`}>Uploading {subCat.name.toLowerCase()} enables:</p>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {subCat.leaveTypes.map(leaveType => (
                                    <span 
                                      key={leaveType}
                                      className={`inline-block px-2 py-1 ${getBgColorClass(subCat.color)} ${getIconColorClass(subCat.color)} text-xs rounded-full border ${getColorClasses(subCat.color).split(' ')[2]}`}
                                    >
                                      {leaveType}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Document Count */}
                        {subCatDocs.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-black/10">
                            <p className="text-sm font-medium">
                              {subCatDocs.length} document{subCatDocs.length !== 1 ? 's' : ''} uploaded
                            </p>
                          </div>
                        )}
                        
                        {/* Verification status — shows a per-status count breakdown
                            (e.g. "4 Verified") for every document sub-card. */}
                        {renderDropzoneStatusRow(subCatDocs, subCat.required)}
                      </div>
                    </div>
                  );
                });
              }
              
              // Regular category rendering for non-family categories
              return (
                <div key={category.id} className={`border-2 border-dashed rounded-xl p-6 ${getColorClasses(category.color)}`}>
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                          <IconComponent className={`w-6 h-6 ${getIconColorClass(category.color)}`} />
                        </div>
                        <h3 className="text-lg font-semibold">{category.name}</h3>
                      </div>
                    </div>
                    <p className="text-sm opacity-75 mb-4">{category.description}</p>
                    {/* Upload Area */}
                    {(() => {
                      const ocrKey = category.id === 'education' ? 'education_certificate'
                        : category.id === 'professional' ? 'professional_certificate'
                        : null;
                      const processingMessage = ocrKey
                        ? ocrProcessing[ocrKey]
                        : (category.id === 'other' && otherDocumentUploading
                          ? 'Saving your document securely...'
                          : null);
                      return processingMessage ? (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-start gap-3">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-blue-800">{processingMessage}</p>
                              <p className="text-xs text-blue-600 mt-1">Please don't navigate away</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="relative">
                          <input
                            type="file"
                            multiple
                            accept={category.acceptedTypes}
                            onChange={(e) => handleFileUpload(e.target.files, category.id)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <div className="bg-white rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors text-center">
                            <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">
                              Drop files here or <span className="text-blue-600 font-medium">browse</span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Max 5MB per file</p>
                          </div>
                        </div>
                      );
                    })()}
                                            {/* Examples */}
                        <div className="mt-4">
                          <p className="text-xs font-medium mb-2">Examples:</p>
                          <ul className="text-xs opacity-75 space-y-1">
                            {category.examples.map((example, index) => (
                              <li key={index} className="flex items-center">
                                <div className="w-1 h-1 bg-current rounded-full mr-2"></div>
                                {example}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Identity Document Specific Info */}
                        {category.id === 'identity' && (
                          <div className="mt-4 p-3 bg-white/80 rounded-lg border border-white/40">
                            <div className="flex items-start gap-2">
                              <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="text-left">
                                <p className="text-xs font-medium text-blue-800 mb-1">Identity Verification</p>
                                <p className="text-xs text-blue-700">
                                  Upload both sides of ID cards and passport for complete verification
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                    {/* Leave Benefits Alert */}
                    {category.leaveAlert && category.triggerTypes && category.triggerTypes.length > 0 && (
                      <div className="mt-4 p-3 bg-white/80 rounded-lg border border-white/40">
                        <div className="flex items-start gap-2">
                          <Heart className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-left">
                            <p className="text-xs font-medium text-green-800 mb-1">Leave Benefits</p>
                            <p className="text-xs text-green-700">{category.leaveAlert}</p>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {category.triggerTypes.map(triggerType => {
                                if (leaveDocumentTriggers[triggerType]) {
                                  return leaveDocumentTriggers[triggerType].leaveTypes.map(leaveType => (
                                    <span 
                                      key={`${triggerType}-${leaveType}`}
                                      className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-200"
                                    >
                                      {leaveType}
                                    </span>
                                  ));
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Document Count */}
                    {categoryDocs.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-black/10">
                        <p className="text-sm font-medium">
                          {categoryDocs.length} document{categoryDocs.length !== 1 ? 's' : ''} uploaded
                        </p>
                      </div>
                    )}
                    {/* Verification status — per-status count breakdown (e.g. "2 Verified"). */}
                    {renderDropzoneStatusRow(categoryDocs, category.required)}
                  </div>
                </div>
              );
            })}
          </div>

          

                    {/* All Uploaded Documents Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">All Uploaded Documents</h3>
                  <CountBadge count={documents.length} theme="employee" size="md" />
                </div>
                <div className="flex items-center gap-4">
                  {/* View Mode Toggle - Matching Leave.js pattern */}
                  <div className="hidden md:flex">
                    <PillToggle
                      value={viewMode}
                      onChange={setViewMode}
                      options={[
                        { id: 'grid', label: 'Grid view', icon: Grid3X3 },
                        { id: 'list', label: 'List view', icon: List },
                      ]}
                      variant="icon"
                      theme="employee"
                      ariaLabel="View mode selection"
                    />
                  </div>
                </div>
              </div>
              {serverDocsUnavailable && (
                <div className="mt-4 p-4 rounded-xl bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Server document list endpoint is unavailable. Showing session uploads only.
                  </div>
                </div>
              )}
            </div>
            {/* List View */}
            {viewMode === 'list' && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Document Type</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Uploaded</th>
                    <th className="table-header-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documentsLoading ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-gray-600">
                        <div className="flex items-center justify-center gap-3">
                          <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                          <span className="text-sm">Loading documents...</span>
                        </div>
                      </td>
                    </tr>
                  ) : (documents || []).map((doc) => {
                    const category = documentCategories.find(cat => cat.id === doc.category);

                    return (
                      <tr key={doc.id} className="table-row">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {(() => {
                              const { icon: DocIcon, color, bg } = getDocumentTypeIcon(doc.approvalType);
                              return (
                                <div className={`w-8 h-8 ${bg} rounded-full flex items-center justify-center`}>
                                  <DocIcon className={`w-4 h-4 ${color}`} />
                                </div>
                              );
                            })()}
                            <div className="min-w-0">
                              <span className="text-sm text-gray-900 font-medium">
                                {getDocumentDisplayName(doc.approvalType)}
                              </span>
                              {(() => {
                                const subtitle = getDocumentSubtitle(doc.approvalType, doc.ocrSummary, doc.name);
                                return subtitle ? (
                                  <p className="text-xs text-gray-700 mt-0.5 truncate max-w-xs" title={subtitle}>{subtitle}</p>
                                ) : null;
                              })()}
                              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs" title={doc.name}>{doc.name}</p>
                              {doc.rejectionReason && (
                                <p
                                  className="text-xs text-red-600 mt-0.5 truncate max-w-xs"
                                  title={doc.rejectionReason}
                                >
                                  {doc.rejectionReason}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={getEmployeeDocumentListStatus(doc.status, doc.approvalType).status} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-900">{doc.uploadedAt ? formatDateTimeCompact(doc.uploadedAt) : '-'}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button className="icon-button" title="Preview" onClick={() => {
                              if (doc.approvalType === 'other_document') {
                                if (!doc.filePath) {
                                  toast.error('Document preview is unavailable');
                                  return;
                                }
                                setOtherDocumentViewer({ open: true, document: doc });
                                return;
                              }

                              // Determine document type for verification modal
                              const documentType = resolveVerificationDocumentType(doc);
                              
                              // If we can't determine a specific document type, show a message instead of opening modal
                              if (!documentType) {
                                toast.error(`Document type not supported for verification: ${doc.name}`);
                                return;
                              }

                              // Profile-sourced records (e.g. bulk-imported certificates) are already
                              // verified credentials with no uploaded document and a synthetic id
                              // (e.g. `prof_cert_295`). They cannot be opened in the OCR verification
                              // modal — its endpoints require a numeric DocumentApproval id and would 404.
                              if (doc.isProfileSourced || typeof doc.id !== 'number') {
                                toast.info('This is a verified profile record with no uploaded document to review.');
                                return;
                              }
                              
                              // Determine if document is editable (not submitted/verified)
                              const docStatus = doc.status?.toString().toLowerCase() || '';
                              const isEditable = !['submitted', 'verified', 'approved', 'validated'].some(s => 
                                docStatus.includes(s)
                              );
                              
                              // Open verification modal - editable if not submitted/verified
                              setVerificationModal({
                                open: true,
                                documentApprovalId: doc.id,
                                documentType: documentType,
                                initialOcrData: documentType === 'medical_certificate' 
                                  ? { ...(doc.ocrData || {}), application_id: doc.application_id }
                                  : (doc.ocrData || {}), // Pass any existing OCR data
                                viewMode: isEditable ? 'edit' : 'view' // Edit mode if not submitted/verified
                              });
                            }}>
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="icon-button" title="Download" onClick={async () => {
                              try {
                                const res = await api.downloadDocumentApproval(doc.id);
                                const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = doc.name || `document_${doc.id}`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              } catch (e) {
                                console.error('Download failed', e);
                              }
                            }}>
                              <Download className="w-4 h-4" />
                            </button>
                            {/* Show delete button only if document is NOT submitted/verified/approved */}
                            {(() => {
                              const docStatus = doc.status?.toString().toLowerCase() || '';
                              const canDelete = !['submitted', 'verified', 'approved', 'validated'].some(s => 
                                docStatus.includes(s)
                              );
                              
                              if (!canDelete) return null;
                              
                              return (
                                <button 
                                  className="icon-button-danger" 
                                  title="Delete"
                                  aria-label="Delete"
                                  onClick={async () => {
                                    const confirmed = await showConfirm({
                                      title: 'Delete Document',
                                      message: 'Delete this document? This action cannot be undone.',
                                      confirmLabel: 'Delete',
                                      variant: 'danger',
                                    });
                                    if (!confirmed) return;
                                    try {
                                      // Determine document type using same logic as preview button
                                      let documentType = null;
                                      
                                      // 1) Prefer explicit approvalType
                                      if (doc.approvalType === 'birth_certificate' || doc.approvalType === 'marriage_certificate' || 
                                          doc.approvalType === 'id_card_front' || doc.approvalType === 'id_card_back' ||
                                          doc.approvalType === 'education_certificate' || doc.approvalType === 'professional_certificate' ||
                                          doc.approvalType === 'medical_certificate') {
                                        documentType = doc.approvalType;
                                      } else {
                                        // 2) Try filename/category detection
                                        const detectedByName = detectDocumentType(doc.name, doc.category);
                                        if (detectedByName) {
                                          documentType = detectedByName;
                                        } else if (doc.category === 'family') {
                                          // 3) Infer from OCR keys if available
                                          const ocrKeys = Object.keys(doc.ocrData || {}).map(k => k.toLowerCase());
                                          const marriageKeys = ['bridegroom_full_name','bride_full_name','marriage_date','marriage_certificate_number'];
                                          const birthKeys = ['child_full_name','child_date_of_birth','birth_certificate_number','mother_full_name','father_full_name'];
                                          const hasMarriageSignal = marriageKeys.some(k => ocrKeys.includes(k));
                                          const hasBirthSignal = birthKeys.some(k => ocrKeys.includes(k));
                                          if (hasMarriageSignal) {
                                            documentType = 'marriage_certificate';
                                          } else if (hasBirthSignal) {
                                            documentType = 'birth_certificate';
                                          }
                                        }
                                      }
                                      
                                      // Delete based on document type
                                      if (documentType === 'birth_certificate') {
                                        await api.deleteChildCertificate(doc.id);
                                      } else if (documentType === 'marriage_certificate') {
                                        await api.deleteMarriageCertificate(doc.id);
                                      } else if (documentType === 'id_card_front' || documentType === 'id_card_back') {
                                        // ID cards use the same delete endpoint as generic documents
                                        await api.deleteDocument(doc.id);
                                      } else {
                                        // Generic documents
                                        await api.deleteDocument(doc.id);
                                      }
                                      
                                      // Remove from local state
                                      setDocuments(prev => prev.filter(d => d.id !== doc.id));
                                      
                                      // Show success message
                                      toast.success('Document deleted successfully');
                                      
                                      // Refetch documents to ensure UI is in sync
                                      refetchDocuments();
                                    } catch (e) {
                                      console.error('Delete failed', e);
                                      toast.error(e?.response?.data?.error || 'Failed to delete document. Please try again.');
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                {(!documents || documents.length === 0) && !documentsLoading && (
                  <div className="text-center py-12 text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <FileText className="w-12 h-12 text-gray-300" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">No documents uploaded</p>
                        <p className="text-xs text-gray-500">Upload your first document to get started</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="p-6">
                {documentsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-600">Loading documents...</span>
                    </div>
                  </div>
                ) : (!documents || documents.length === 0) ? (
                  <div className="text-center py-12 text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <FileText className="w-12 h-12 text-gray-300" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">No documents uploaded</p>
                        <p className="text-xs text-gray-500">Upload your first document to get started</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(documents || []).map((doc) => {
                      const category = documentCategories.find(cat => cat.id === doc.category);

                      const documentDisplayName = getDocumentDisplayName(doc.approvalType);
                      const documentSubtitle = getDocumentSubtitle(doc.approvalType, doc.ocrSummary, doc.name);
                      
                      // Helper function for preview handler
                      const handlePreview = () => {
                        if (doc.approvalType === 'other_document') {
                          if (!doc.filePath) {
                            toast.error('Document preview is unavailable');
                            return;
                          }
                          setOtherDocumentViewer({ open: true, document: doc });
                          return;
                        }

                        const documentType = resolveVerificationDocumentType(doc);
                        
                        if (!documentType) {
                          toast.error(`Document type not supported for verification: ${doc.name}`);
                          return;
                        }

                        // Profile-sourced records (e.g. bulk-imported certificates) are already
                        // verified credentials with no uploaded document and a synthetic id
                        // (e.g. `prof_cert_295`). They cannot be opened in the OCR verification
                        // modal — its endpoints require a numeric DocumentApproval id and would 404.
                        if (doc.isProfileSourced || typeof doc.id !== 'number') {
                          toast.info('This is a verified profile record with no uploaded document to review.');
                          return;
                        }
                        
                        const docStatus = doc.status?.toString().toLowerCase() || '';
                        const isEditable = !['submitted', 'verified', 'approved', 'validated'].some(s => 
                          docStatus.includes(s)
                        );
                        
                        setVerificationModal({
                          open: true,
                          documentApprovalId: doc.id,
                          documentType: documentType,
                          initialOcrData: documentType === 'medical_certificate' 
                            ? { ...(doc.ocrData || {}), application_id: doc.application_id }
                            : (doc.ocrData || {}),
                          viewMode: isEditable ? 'edit' : 'view'
                        });
                      };
                      
                      // Helper function for download handler
                      const handleDownload = async () => {
                        try {
                          const res = await api.downloadDocumentApproval(doc.id);
                          const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = doc.name || `document_${doc.id}`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          a.remove();
                        } catch (e) {
                          console.error('Download failed', e);
                          toast.error('Failed to download document');
                        }
                      };
                      
                      // Helper function for delete handler
                      const handleDelete = async () => {
                        const confirmed = await showConfirm({
                          title: 'Delete Document',
                          message: 'Delete this document? This action cannot be undone.',
                          confirmLabel: 'Delete',
                          variant: 'danger',
                        });
                        if (!confirmed) return;
                        try {
                          let documentType = null;
                          
                          if (doc.approvalType === 'birth_certificate' || doc.approvalType === 'marriage_certificate' || 
                              doc.approvalType === 'id_card_front' || doc.approvalType === 'id_card_back' ||
                              doc.approvalType === 'education_certificate' || doc.approvalType === 'professional_certificate' ||
                              doc.approvalType === 'medical_certificate') {
                            documentType = doc.approvalType;
                          } else {
                            const detectedByName = detectDocumentType(doc.name, doc.category);
                            if (detectedByName) {
                              documentType = detectedByName;
                            } else if (doc.category === 'family') {
                              const ocrKeys = Object.keys(doc.ocrData || {}).map(k => k.toLowerCase());
                              const marriageKeys = ['bridegroom_full_name','bride_full_name','marriage_date','marriage_certificate_number'];
                              const birthKeys = ['child_full_name','child_date_of_birth','birth_certificate_number','mother_full_name','father_full_name'];
                              const hasMarriageSignal = marriageKeys.some(k => ocrKeys.includes(k));
                              const hasBirthSignal = birthKeys.some(k => ocrKeys.includes(k));
                              if (hasMarriageSignal) {
                                documentType = 'marriage_certificate';
                              } else if (hasBirthSignal) {
                                documentType = 'birth_certificate';
                              }
                            }
                          }
                          
                          if (documentType === 'birth_certificate') {
                            await api.deleteChildCertificate(doc.id);
                          } else if (documentType === 'marriage_certificate') {
                            await api.deleteMarriageCertificate(doc.id);
                          } else if (documentType === 'id_card_front' || documentType === 'id_card_back') {
                            await api.deleteDocument(doc.id);
                          } else {
                            await api.deleteDocument(doc.id);
                          }
                          
                          setDocuments(prev => prev.filter(d => d.id !== doc.id));
                          toast.success('Document deleted successfully');
                          refetchDocuments();
                        } catch (e) {
                          console.error('Delete failed', e);
                          toast.error(e?.response?.data?.error || 'Failed to delete document. Please try again.');
                        }
                      };
                      
                      const docStatus = doc.status?.toString().toLowerCase() || '';
                      const canDelete = !['submitted', 'verified', 'approved', 'validated'].some(s => 
                        docStatus.includes(s)
                      );
                      const isRejected = normalizeDocumentStatus(doc.status) === 'rejected';
                      const isHistoricalRejection = isRejected && hasLaterReplacementUpload(doc, documents);
                      
                      return (
                        <div key={doc.id} className={`bg-white border rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3 ${
                          isRejected && !isHistoricalRejection ? 'border-red-200' : 'border-gray-200'
                        }`}>
                          <div className="flex items-start justify-between gap-2 flex-1">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                              {(() => {
                                const { icon: DocIcon, color, bg } = getDocumentTypeIcon(doc.approvalType);
                                return (
                                  <div className={`w-10 h-10 ${bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                                    <DocIcon className={`w-5 h-5 ${color}`} />
                                  </div>
                                );
                              })()}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">{documentDisplayName}</h4>
                                {isHistoricalRejection && (
                                  <div className="mt-1">
                                    <InfoBadge
                                      color="gray"
                                      label="Earlier upload"
                                      title="A later upload of this document type is already on file"
                                    />
                                  </div>
                                )}
                                {documentSubtitle && (
                                  <p className="text-xs text-gray-700 mt-0.5 truncate" title={documentSubtitle}>{documentSubtitle}</p>
                                )}
                                <p className="text-xs text-gray-400 mt-0.5 truncate" title={doc.name}>{doc.name}</p>
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <StatusBadge status={getEmployeeDocumentListStatus(doc.status, doc.approvalType).status} />
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 truncate">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {doc.uploadedAt ? formatDateTimeCompact(doc.uploadedAt) : 'Not available'}
                          </p>

                          {doc.rejectionReason && (
                            <RejectionFeedback
                              reason={doc.rejectionReason}
                              collapsed={isHistoricalRejection}
                              label={isHistoricalRejection ? 'Previous feedback' : 'Rejection reason'}
                            />
                          )}
                          
                          <div className="card-action-footer flex-nowrap">
                            <button
                              onClick={handlePreview}
                              className="icon-button"
                              title="Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleDownload}
                              className="icon-button"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            {canDelete && (
                              <button
                                onClick={handleDelete}
                                className="icon-button-danger"
                                title="Delete"
                                aria-label="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}



      {/* ID Card Samples Modal */}
      {idCardSamplesModal.open && (
        <Modal
          isOpen
          onClose={() => setIdCardSamplesModal({ open: false, type: null })}
          size="lg"
          theme="employee"
          customHeader={(
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0 gap-2">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-blue-900 flex-shrink-0" />
                <h3 className="text-base sm:text-xl font-bold text-gray-900 truncate">
                  {idCardSamplesModal.type === 'passport' 
                    ? 'Passport Sample Images' 
                    : idCardSamplesModal.type === 'id_card_back' 
                      ? 'ID Card Back Sample Images' 
                      : 'ID Card Front Sample Images'
                  }
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIdCardSamplesModal({ open: false, type: null })}
                className="modal-close flex-shrink-0"
                title="Close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        >
              <div className="mb-6">
                <p className="text-gray-600 text-left">
                  {idCardSamplesModal.type === 'passport' 
                    ? 'These are sample images showing what your passport documents should look like. Please upload clear, high-quality images of your passport photo page.'
                    : idCardSamplesModal.type === 'id_card_back' 
                      ? 'These are sample images showing what the back side of your ID card documents should look like. You can upload either a physical card back side or a digital work pass screenshot from the SGWorkPass app. Please upload clear, high-quality images.'
                      : 'These are sample images showing what the front side of your ID card documents should look like. Please upload clear, high-quality images of the front side.'
                  }
                </p>
              </div>

              {idCardSamplesModal.type === 'id_card_back' ? (
                /* Two Samples for ID Card Back Side */
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Visit Pass / EP Back Sample */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="text-center mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Visit Pass / EP (Back)</h4>
                      <p className="text-xs text-gray-600 mb-4">Physical card back side</p>
                      <div className="w-full h-48 bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden">
                        <img 
                          src={epBackImage} 
                          alt="Visit Pass Back Sample" 
                          className="w-full h-full object-contain hidden"
                          onError={(e) => {
                            console.log('Failed to load ep_back.png');
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          onLoad={(e) => {
                            console.log('Successfully loaded ep_back.png');
                            e.target.style.display = 'block';
                            e.target.nextSibling.style.display = 'none';
                          }}
                        />
                        <div className="text-center hidden">
                          <Shield className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                          <p className="text-sm text-blue-700 font-medium">Visit Pass Sample Image</p>
                          <p className="text-xs text-blue-600">ep_back.png</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Digital Work Pass Sample */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="text-center mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Digital Work Pass</h4>
                      <p className="text-xs text-gray-600 mb-4">Digital pass from SGWorkPass app</p>
                      <div className="w-full h-48 bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden">
                        <img 
                          src={digitalWorkPassImage} 
                          alt="Digital Work Pass Sample" 
                          className="w-full h-full object-contain hidden"
                          onError={(e) => {
                            console.log('Failed to load digital_work_pass.jpg');
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          onLoad={(e) => {
                            console.log('Successfully loaded digital_work_pass.jpg');
                            e.target.style.display = 'block';
                            e.target.nextSibling.style.display = 'none';
                          }}
                        />
                        <div className="text-center hidden">
                          <Shield className="w-12 h-12 text-purple-600 mx-auto mb-2" />
                          <p className="text-sm text-purple-700 font-medium">Digital Work Pass Sample</p>
                          <p className="text-xs text-purple-600">digital_work_pass.jpg</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : idCardSamplesModal.type === 'passport' ? (
                /* Single Passport Sample */
                <div className="max-w-2xl mx-auto">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="text-center mb-6">
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">Passport Sample</h4>
                      <p className="text-sm text-gray-600 mb-4">Sample passport photo page</p>
                      <div className="w-full h-64 bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden">
                        <img 
                          src={passportSampleImage} 
                          alt="Passport Sample" 
                          className="w-full h-full object-contain hidden"
                          onError={(e) => {
                            console.log('Failed to load passport_sample.png');
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          onLoad={(e) => {
                            console.log('Successfully loaded passport_sample.png');
                            e.target.style.display = 'block';
                            e.target.nextSibling.style.display = 'none';
                          }}
                        />
                        <div className="text-center hidden">
                          <Shield className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                          <p className="text-sm text-blue-700 font-medium">Passport Sample Image</p>
                          <p className="text-xs text-blue-600">passport_sample.png</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Three Samples for ID Card Front */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Employment Pass Sample */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-center mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {idCardSamplesModal.type === 'passport' ? 'Passport (Main)' : 'Employment Pass'}
                    </h4>
                    <div className="w-full h-48 bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden">
                      <img 
                        src={idCardSamplesModal.type === 'id_card_back' ? epBackImage : epFrontImage} 
                        alt={`Employment Pass ${idCardSamplesModal.type === 'id_card_back' ? 'Back' : 'Front'} Sample`} 
                        className="w-full h-full object-contain hidden"
                        onError={(e) => {
                          console.log(`Failed to load ep_${idCardSamplesModal.type === 'id_card_back' ? 'back' : 'front'}.png`);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          console.log(`Successfully loaded ep_${idCardSamplesModal.type === 'id_card_back' ? 'back' : 'front'}.png`);
                          e.target.style.display = 'block';
                          e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="text-center">
                        <Shield className="w-12 h-12 text-green-600 mx-auto mb-2" />
                        <p className="text-sm text-green-700 font-medium">EP Sample Image</p>
                        <p className="text-xs text-green-600">ep_front.png</p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* S Pass Sample */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-center mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {idCardSamplesModal.type === 'passport' ? 'Passport (Alternative)' : 'S Pass'}
                    </h4>
                    <div className="w-full h-48 bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden">
                      <img 
                        src={idCardSamplesModal.type === 'id_card_back' ? spassBackImage : spassFrontImage} 
                        alt={`S Pass ${idCardSamplesModal.type === 'id_card_back' ? 'Back' : 'Front'} Sample`} 
                        className="w-full h-full object-contain hidden"
                        onError={(e) => {
                          console.log(`Failed to load spass_${idCardSamplesModal.type === 'id_card_back' ? 'back' : 'front'}.png`);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          console.log(`Successfully loaded spass_${idCardSamplesModal.type === 'id_card_back' ? 'back' : 'front'}.png`);
                          e.target.style.display = 'block';
                          e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="text-center">
                        <Shield className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                        <p className="text-sm text-blue-700 font-medium">S Pass Sample Image</p>
                        <p className="text-xs text-blue-600">spass_front.png</p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Work Permit Sample */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="text-center mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      {idCardSamplesModal.type === 'passport' ? 'Passport (Example)' : 'Work Permit'}
                    </h4>
                    <div className="w-full h-48 bg-white rounded-lg border border-gray-300 flex items-center justify-center overflow-hidden">
                      <img 
                        src={idCardSamplesModal.type === 'id_card_back' ? wpBackImage : wpFrontImage} 
                        alt={`Work Permit ${idCardSamplesModal.type === 'id_card_back' ? 'Back' : 'Front'} Sample`} 
                        className="w-full h-full object-contain hidden"
                        onError={(e) => {
                          console.log(`Failed to load wp_${idCardSamplesModal.type === 'id_card_back' ? 'back' : 'front'}.png`);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          console.log(`Successfully loaded wp_${idCardSamplesModal.type === 'id_card_back' ? 'back' : 'front'}.png`);
                          e.target.style.display = 'block';
                          e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="text-center">
                        <Shield className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                        <p className="text-sm text-emerald-700 font-medium">WP Sample Image</p>
                        <p className="text-xs text-emerald-600">wp_front.png</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
                )
              }
              
              {/* Upload Tips for Passport */}
              {idCardSamplesModal.type === 'passport' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-2">Upload Tips for Passport:</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Ensure the entire passport photo page is visible and clearly readable</li>
                        <li>• Avoid shadows, glare, or blurry images that obscure text</li>
                        <li>• Make sure the "PASSPORT" title and country name are clearly visible</li>
                        <li>• Capture the passport holder's photo clearly without shadows or reflections</li>
                        <li>• Ensure all personal details (name, passport number, DOB, nationality) are legible</li>
                        <li>• Include the machine readable zone (MRZ) at the bottom in the photo</li>
                        <li>• Make sure issue and expiry dates are clearly readable</li>
                        <li>• Ensure the Singapore coat of arms and security features are visible</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Tips for ID Card (Back) */}
              {idCardSamplesModal.type === 'id_card_back' && (
                <div className="mt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Physical ID Card (Back) Tips */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="font-medium text-blue-900 mb-2">Physical ID Card (Back)</h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Ensure the entire back side is visible and clearly readable</li>
                            <li>• Avoid shadows, glare, or blurry images that obscure text</li>
                            <li>• Make sure the "VISIT PASS" title at the top is clearly visible</li>
                            <li>• Ensure all personal details (name, FIN, DOB, sex, nationality) are legible</li>
                            <li>• Capture the fingerprint image clearly without smudging</li>
                            <li>• Include the QR code and barcode at the bottom in the photo</li>
                            <li>• Make sure the date stamp is readable</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Digital Work Pass Tips */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="font-medium text-blue-900 mb-2">Digital Work Pass</h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Ensure the entire screen is visible and clearly readable</li>
                            <li>• Avoid shadows, glare, or blurry screenshots that obscure text</li>
                            <li>• Ensure all pass details (name, FIN, pass expiry, employer, etc.) are clearly visible</li>
                            <li>• Make sure the screen brightness is adequate and text is legible</li>
                            <li>• Capture the full screen without cutting off any information</li>
                            <li>• Ensure the app interface elements are clearly visible</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Tips for ID Card (Front) */}
              {idCardSamplesModal.type === 'id_card_front' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-900 mb-2">Upload Tips for ID Card (Front):</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Ensure the entire front side is visible and clearly readable</li>
                        <li>• Avoid shadows, glare, or blurry images that obscure text</li>
                        <li>• Make sure the pass type title (EP/S Pass/WP) at the top is clearly visible</li>
                        <li>• Ensure the Ministry of Manpower logo is not cut off</li>
                        <li>• Capture the photo clearly without shadows or reflections</li>
                        <li>• Make sure all personal details (name, pass number) are legible</li>
                        <li>• Include employer information section in the photo</li>
                        <li>• Ensure holographic security features are visible</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
        </Modal>
      )}

      {/* Marriage Role Selection Modal */}
      {roleSelectionModal.open && (
        <Modal
          isOpen
          onClose={() => setRoleSelectionModal({ open: false, documentApprovalId: null, ocrData: {}, recommendedRole: null })}
          size="sm"
          theme="employee"
          customHeader={(
            <div className="modal-header">
              <div className="flex items-center gap-2">
                <UserCheck className="w-5 h-5" />
                <h3 className="modal-title">Select Your Role</h3>
              </div>
              <button
                onClick={() => setRoleSelectionModal({ open: false, documentApprovalId: null, ocrData: {}, recommendedRole: null })}
                className="modal-close"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        >
                <p className="text-sm text-gray-600 mb-6">
                  We couldn't automatically determine your role from your name. Please select whether you are the Bridegroom or Bride on this certificate.
                </p>
                
                <div className="space-y-3 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-sm font-medium text-blue-900 mb-1">Bridegroom:</p>
                    <p className="text-sm text-blue-700">{roleSelectionModal.ocrData.bridegroom_full_name || 'Not detected'}</p>
                  </div>
                  <div className="p-4 bg-pink-50 rounded-lg border border-pink-100">
                    <p className="text-sm font-medium text-pink-900 mb-1">Bride:</p>
                    <p className="text-sm text-pink-700">{roleSelectionModal.ocrData.bride_full_name || 'Not detected'}</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // Close role selection modal
                      setRoleSelectionModal({ open: false, documentApprovalId: null, ocrData: {}, recommendedRole: null });
                      // Open verification modal with selected role - webhook will be called on submit
                      setVerificationModal({
                        open: true,
                        documentApprovalId: roleSelectionModal.documentApprovalId,
                        documentType: 'marriage_certificate',
                        initialOcrData: roleSelectionModal.ocrData,
                        detectedRole: 'bridegroom', // Pass selected role to verification modal
                        viewMode: 'edit'
                      });
                    }}
                    className="flex-1 px-4 py-2 bg-blue-800 text-white rounded-full hover:bg-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all duration-200 font-medium text-sm"
                    aria-label="I am the Bridegroom"
                  >
                    <span className="mr-2">👨</span>
                    I am the Bridegroom
                  </button>
                  <button
                    onClick={() => {
                      // Close role selection modal
                      setRoleSelectionModal({ open: false, documentApprovalId: null, ocrData: {}, recommendedRole: null });
                      // Open verification modal with selected role - webhook will be called on submit
                      setVerificationModal({
                        open: true,
                        documentApprovalId: roleSelectionModal.documentApprovalId,
                        documentType: 'marriage_certificate',
                        initialOcrData: roleSelectionModal.ocrData,
                        detectedRole: 'bride', // Pass selected role to verification modal
                        viewMode: 'edit'
                      });
                    }}
                    className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-full hover:bg-pink-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2 transition-all duration-200 font-medium text-sm"
                    aria-label="I am the Bride"
                  >
                    <span className="mr-2">👰</span>
                    I am the Bride
                  </button>
                </div>
        </Modal>
      )}

      <SimpleDocumentViewerModal
        isOpen={otherDocumentViewer.open}
        onClose={() => setOtherDocumentViewer({ open: false, document: null })}
        filePath={otherDocumentViewer.document?.filePath}
        fileName={otherDocumentViewer.document?.name}
        title={otherDocumentViewer.document?.name || 'Other Document'}
        statusBadge={<StatusBadge status="on_file" />}
        onDownload={handleDownloadOtherDocument}
        downloading={otherDocumentDownloading}
        theme="employee"
      />

      {/* Document Verification Modal */}
      <DocumentVerificationModal
        key={`${verificationModal.documentApprovalId}-${verificationModal.documentType}`} // Force re-render when document changes
        isOpen={verificationModal.open}
        onClose={() => setVerificationModal({ open: false, documentApprovalId: null, documentType: null, initialOcrData: {}, detectedRole: null, viewMode: 'edit' })}
        documentApprovalId={verificationModal.documentApprovalId}
        documentType={verificationModal.documentType}
        ocrData={verificationModal.initialOcrData || {}}
        mode="employee"
        viewMode={verificationModal.viewMode || 'edit'}
        employeeProfile={hrProfile || profile}
        detectedRole={verificationModal.detectedRole}
        onApprovalComplete={(result) => {
          if (result?.deleted && verificationModal.documentApprovalId) {
            setDocuments(prev => prev.filter(doc => doc.id !== verificationModal.documentApprovalId));
            refetchDocuments();
          } else if (result?.status === 'submitted' && verificationModal.documentApprovalId) {
            setDocuments(prev => prev.map(doc => 
              doc.id === verificationModal.documentApprovalId 
                ? { ...doc, status: 'submitted' }
                : doc
            ));
            refetchDocuments();
          }
          setVerificationModal({ open: false, documentApprovalId: null, documentType: null, initialOcrData: {}, detectedRole: null, viewMode: 'edit' });
        }}
      />
      <ConfirmModal
        isOpen={!!confirmDialog}
        title={confirmDialog?.title || ''}
        message={confirmDialog?.message || ''}
        confirmLabel={confirmDialog?.confirmLabel || 'Confirm'}
        cancelLabel={confirmDialog?.cancelLabel || 'Cancel'}
        variant={confirmDialog?.variant || 'danger'}
        onConfirm={() => {
          confirmCallbackRef.current?.(true);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        onCancel={() => {
          confirmCallbackRef.current?.(false);
          confirmCallbackRef.current = null;
          setConfirmDialog(null);
        }}
        theme="employee"
      />
    </div>
  );
};

export default Profile;